import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  Box,
  Flex,
  Grid,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Popover,
  Radio,
  Select,
  Text,
  TextInput,
  Textarea,
  Tooltip,
  rem,
  Drawer,
  ActionIcon,
  Checkbox,
  Button,
  Space,
} from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { TbPlus, TbTrash } from "react-icons/tb";
import {
  LuBookOpen,
  LuMapPin,
  LuUsers,
  LuFileText,
  LuPower,
  LuBell,
  LuInfo,
  LuShieldCheck,
  LuStethoscope,
  LuTarget,
  LuPenLine,
} from "react-icons/lu";
import { useMediaQuery } from "@mantine/hooks";
import { IconInfoCircle, IconPencil, IconTrash, IconUsers, IconFileText, IconClipboardCheck, IconCopy } from "@tabler/icons-react";
import { useDispatch, useSelector } from "react-redux";
import { createNote, editNote, generateAiNote, generateBothAiNotes, saveNoteDraft, getNoteDraft, deleteNoteDraft, getAllDrafts, updateAiSuggestionEvent, logAiSuggestionEvent, captureExemplarNotes } from "../../services/note";
import { generateQuickNoteAi } from "../../services/quickNoteAi";
import { showSkaiTokenError } from "../../utils/skaiErrors";
import { normalizeAction, trackAiSuggestionShown, trackAiSuggestionResolved, trackAiNoteFinalized } from "../../utils/pendo";
import { getAllPatientsModal } from "../../services/patient";
import {
  decrypt,
  formatDate,
  getNameFromAbbreviation,
  getOptions,
  noteType_Client,
  noteType_Client_Options,
  showError,
} from "../../utils";
import useStyles from "./styles";
import { DropDownArrowSvg } from "../Svgs/DropDownArrowSvg";
import { IconChevronDown } from "@tabler/icons-react";
import { getAllFacilities, getAllFacilitiesModal } from "../../services";
import { planTypeLabels } from "../MAR/ClientSupportPlans";
import { carePlanPartsOf } from "../../pages/Home/NoteType/studio/schema";
import EssentialViewInfo from "./EssentialViewInfo";
import {
  Billing_Services_Note,
  Essential_Support_Note,
  Intervention_Progress_Note,
  Group_Note,
  PROGRESS_Note,
} from "../../http";
import ButtonComp from "../ButtonComponent/ButtonComp";
import { RiDeleteBin7Line } from "react-icons/ri";
import GroupedButtons from "../GroupedButtons/GroupedButtons";
import CustomModal from "../CustomModal/CustomModal";
import pendingModalIcon from "../../images/pendingModalIcon.png";
import NoteTypeComp from "../NoteTypeComp/NoteTypeComp";
import DateSvg from "../Svgs/DateSvg";
import { AiOutlineDelete } from "react-icons/ai";
import NotePendingSvg from "../Svgs/NotePendingSvg";
import ViewInfo from "./ViewInfo";
import { useSpeechToText } from "./useSpeechToText";
import RecordingControls from "./RecordingControls";
import swal from "sweetalert";
import { getSkaiTokenUsage } from "../../services/skaiTokens";
import moment from "moment";
import AiActionButtons from "./AiActionButtons";
import QuickNotesPicker from "../QuickNotesPicker";
import ComparePanel from "./ComparePanel";
import StudioSectionsForm from "./StudioSectionsForm";
import { getActivityLibrary } from "../../services/activityLibrary";
import { getDefaultPrompting } from "../../services/defaultPrompting";
import { TiPlusOutline } from "react-icons/ti";
import { FiAlertTriangle } from "react-icons/fi";

// Blank Incident Report state. Defined once and spread wherever the form is
// initialised or cleared, so a new report never inherits the previous one's
// answers.
const INITIAL_INCIDENT_NOTE = {
  type: null,
  otherType: "",
  severity: null,
  clientsInvolved: [],
  staffInvolved: [],
  injurySustained: false,
  emergencyServicesCalled: false,
  restraintUsed: false,
  notifiedContacts: {},
  notificationNotes: "",
  followUpAction: "",
  // The Supervisor signs and approves from the note view — these are carried
  // through here only so re-saving an edit doesn't wipe them.
  reporterName: "",
  reporterSignature: "",
  reporterSignatureImage: "",
  reporterSignedAt: null,
  // Approval itself lives on the note-level `approvalStatus` field — these
  // two are just the audit trail of who approved and when.
  reviewedBy: "",
  reviewedAt: null,
};

const NoteDrawer = ({
  opened,
  close,
  title,
  view,
  setView,
  userId,
  noteType: NOTETYPE,
  autoRestoreDraft,
  isClientView = false,
  setOpenModal,
  setBillingServices,
  setProgressNoteState,
  progressNoteState,
  resetPatientTrigger,
  clientDetails,
  initialFacilityId,
  initialShift,
  autoGenerateActivities,
  // "Use a Previous Note": data still carries the source note (so every
  // edit-mode prefill effect seeds the form exactly like it would for
  // editing), but Save must create a new note instead of updating the
  // source one — this only overrides that one decision.
  saveAsNew = false,
  // True when the separate read-only view drawer (NoteModalNew) is open. The
  // post-save "Note Successfully Saved" / "Note Edited" confirmation modals live
  // in this always-mounted component and persist until dismissed — so when the
  // user opens a note to view without clicking OK first, they'd otherwise show
  // on top of the view drawer. This lets us close them when the view opens.
  viewOpened = false,
  ...data
}) => {
  // Get the current time in HH:mm:ss format
  const { sortField, sortOrder, currentPage, dateRange, activeFilters } = data;
  const { classes } = useStyles();
  const ref = useRef();
  const edit = data.data ? true : false;
  const noteT = data?.data?.noteId;
  let getEssentialSupport = data?.data?.essentialSupport;
  let getGoalsData = data?.data?.goals;
  const dispatch = useDispatch();
  const { allNotesTypes, noteSettings } = useSelector((state) => state?.note);
  // Defaults to enabled (matches the settings page's own default) until the
  // org's note-settings have loaded, so behavior doesn't regress in the gap.
  const autoSaveDraftsEnabled = noteSettings?.general?.autoSaveDrafts !== false;
  const { allPlans } = useSelector((state) => state?.supportPlan);
  const { profile } = useSelector((state) => state.profile);
  const { allPatients } = useSelector((state) => state?.patient);
  const { facilities } = useSelector((state) => state.facility);
  const [messages, setMessages] = useState([]);

  const [errors, setErrors] = useState({});
  const [outcomeNotes, setOutcomeNotes] = useState([]);
  const [essentialNotes, setEssentialNotes] = useState([]);
  const [essentialSupportCount, setEssentialSupportCount] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [facilityDetials, setFacilityDetials] = useState(null);
  const [medicationOptions, setMedicationOptions] = useState([]);
  const [supportOptions, setSupportOptions] = useState([]);
  const [outComeTitle, setOutComeTitle] = useState([]);
  const [outcomeNumbers, setOutComeNumbers] = useState([]);
  const [essentialSupportType, setEssentialSupportType] = useState([]);
  const [essentialSupportNumber, setEssentialSupportNumber] = useState([]);
  const [noteType, setNoteType] = useState(
    noteT
      ? allNotesTypes?.ntypes?.find((value) => value?.id === noteT?.id)
      : allNotesTypes?.ntypes?.find((value) => value?.id === NOTETYPE)
  );
  const [aiActionsOpen, setAiActionsOpen] = useState(false);
  // { key, getValue, setValue } — generalized so Quick Notes can insert into a
  // plain form field (main Description) or an array-item field (per-outcome/
  // per-goal descriptions), whichever textarea it was opened from.
  const [quickNotesTarget, setQuickNotesTarget] = useState(null);
  const COMPARE_PANEL_RESET = {
    open: false, action: null, context: null,
    option1: null, option2: null, loading1: false, loading2: false,
    option1Model: null, option2Model: null,
    option1WordCount: null, option2WordCount: null,
    option1LatencyMs: null, option2LatencyMs: null,
    regenerationCount: 0,
    // Actual display-slot order set by ComparePanel's onArrange callback after flip is settled
    slot1Model: null, slot2Model: null,
    ollamaAvailable: true,
  };
  const [comparePanel, setComparePanel] = useState(COMPARE_PANEL_RESET);
  // Tracks AI suggestions accepted into the note, keyed by field context, for final-save tracking
  const aiTouchedFieldsRef = useRef({});
  const [pendingModal, setPendingModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [currentNote, setCurrentNote] = useState("");
  const isMobileScreen = useMediaQuery("(max-width: 600px)");
  const [supportPlan, setSupportPlan] = useState("no");
  const [openedAccordion, setOpenedAccordion] = useState("0");
  const [essentialAccordion, setEssentialAccordion] = useState("0");
  const [essentialSupportToggle, setEssentialSupportToggle] = useState("no");
  const [interventionPlanToggle, setInterventionPlanToggle] = useState("no");
  const [incidentNoteData, setIncidentNoteData] = useState({
    ...INITIAL_INCIDENT_NOTE,
  });
  // Serious and Critical incidents are reportable — the Agency / State
  // Reporting Line has to be notified for them.
  const isReportableSeverity =
    incidentNoteData.severity === "Serious" ||
    incidentNoteData.severity === "Critical";
  const severityColorMap = {
    Minor: {
      border: "rgb(166, 244, 197)",
      background: "rgb(236, 253, 243)",
      text: "rgb(2, 122, 72)",
      dot: "rgb(18, 183, 106)",
    },
    Moderate: {
      border: "rgb(254, 223, 137)",
      background: "rgb(255, 250, 235)",
      text: "rgb(181, 71, 8)",
      dot: "rgb(247, 144, 9)",
    },
    Serious: {
      border: "rgb(254, 205, 202)",
      background: "rgb(254, 243, 242)",
      text: "rgb(180, 35, 24)",
      dot: "rgb(240, 68, 56)",
    },
    Critical: {
      border: "rgb(253, 162, 155)",
      background: "rgb(254, 228, 226)",
      text: "rgb(145, 32, 24)",
      dot: "rgb(217, 45, 32)",
    },
  };

  const [serviceNumber, setServiceNumber] = useState(null);
  const icon = (
    <DropDownArrowSvg color={true} style={{ width: rem(1), height: rem(1) }} />
  );
  const [isListening, setIsListening] = useState(false);
  const [micLabel, setMicLabel] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingType, setRecordingType] = useState(null);
  const [essentialSupportErrors, setEssentialSupportErrors] = useState([]);

  const timerRef = useRef(null);

  // ── Auto-save draft ──
  const [draftStatus, setDraftStatus] = useState("idle");
  const [pendingDraft, setPendingDraft] = useState(null);
  const [draftFetching, setDraftFetching] = useState(false);
  const draftRestoredRef = useRef(false);
  // Set true the moment a draft is restored (button click OR auto-restore)
  // so the draft-fetch effect below — which re-runs because restoring
  // changes its own dependencies (clientsInvolved/selectedFacility) —
  // doesn't immediately re-fetch and re-show the same draft it just cleared.
  const justRestoredDraftRef = useRef(false);
  const lastSavedSnapshotRef = useRef("");

  const [aiAssistantCardOpened, setAiAssistantCardOpened] = useState(false);
  const isEssentialSupport = noteType?.id === Essential_Support_Note;
  const isIncidentNote = noteType?.id === Essential_Support_Note;
  const isBilling_Services = noteType?.id === Billing_Services_Note;
  const isIntervention_Progress_Note = noteType?.id === Intervention_Progress_Note;
  const isGroupNote = noteType?.id === Group_Note;
  // Edit mode mein group note single client ki tarah behave karta hai
  const isGroupNoteCreating = isGroupNote && !edit;
  // The group multi-client picker must render when CREATING *and* EDITING a
  // group note (it's disabled in view). Gating only on isGroupNoteCreating made
  // edit fall back to the generic single-client Select, so the saved roster
  // never showed and a subsequent save overwrote clientIds with an empty array.
  const showGroupClientPicker = isGroupNote && !view;
  // A user-authored Note-Type Studio template: it carries the rich `sections[]`
  // model and is NOT one of the special hand-built built-in types below. These
  // render from the template exactly as designed (see StudioSectionsForm).
  const isSpecialNoteType =
    [Essential_Support_Note, Billing_Services_Note, Intervention_Progress_Note, Group_Note, PROGRESS_Note].includes(noteType?.id) ||
    noteType?.name === "Incident Report" ||
    noteType?.name === "Progress Note";
  const isStudioTemplate =
    !isSpecialNoteType &&
    Array.isArray(noteType?.sections) &&
    noteType.sections.length > 0;
  // A studio template can include a "Use care plan?" (careplan) field. When it's
  // set to Yes we surface the same care-plan authoring blocks the built-in note
  // types use — Support Plan Outcomes, Essential Support, or both, whichever the
  // template author picked, and each only if the selected client has that plan
  // type active.
  const careplanField = isStudioTemplate
    ? noteType.sections
        .flatMap((s) => s?.fields || [])
        .find((f) => f?.type === "careplan")
    : null;
  const careplanLabel = careplanField?.label;
  const isCareplanTemplate = !!careplanField;
  // [] when there's no careplan field, otherwise one or both parts — legacy
  // fields (authored before the picker existed) normalise to ["outcomes"].
  const careplanParts = carePlanPartsOf(careplanField);
  const careplanHasOutcomes = careplanParts.includes("outcomes");
  const careplanHasEssential = careplanParts.includes("essential");

  // ── Multi-client / multi-staff support for standard note types ────────────
  // Every standard note type now lets the user pick MULTIPLE clients & staff on
  // note creation — there is no more single-client/single-staff Select path.
  // These render "Client Name" / "Staff Name" from noteType.custom, so the
  // shared multiClients / multiStaff MultiSelect (see ReturnFormItem below)
  // replaces the single Select for all of them at once.
  //
  // Three note types are excluded because they don't fit this shared path:
  //  • Group Note — ships its own multi-picker and splits one submission into a
  //    Note per client (selectedGroupClients + fields.clientIds).
  //  • Incident Report — its own multi-picker (incidentNoteData.clientsInvolved
  //    / staffInvolved).
  //  • Studio templates — render fields from `sections` (no "Client Name" custom
  //    field / visible picker); their client comes from the userId/selectedPatient
  //    context, and the save flow already mirrors that into "Client Name".
  //
  // A legacy note saved with a single scalar id gets that value wrapped into a
  // 1-item array on load (see the load effect below) and saves back as an array.
  const isMultiSelectNoteType =
    !!noteType &&
    noteType?.name !== "Incident Report" &&
    !isGroupNote &&
    !isStudioTemplate;
  const [multiClients, setMultiClients] = useState([]);
  const [multiStaff, setMultiStaff] = useState([]);
  // Holds the Client(s) / Staff / Facility selected on a note (captured at save
  // time) so the follow-up note opened from the "Do you want to add a new
  // essential support note?" / billing modal opens pre-filled with the same
  // selection. Set only when a follow-up will open; cleared right after it seeds.
  const carryOverRef = useRef(null);
  // Always multi-select for these note types — new notes AND old notes being
  // edited/viewed alike. A legacy single-id note gets its one value wrapped
  // into a 1-item array on load (see the load effect below) and, from then on,
  // saves back out as an array too — there's no more "old shape" to fall back to.
  const useMultiClient = isMultiSelectNoteType;
  const useMultiStaff = isMultiSelectNoteType;
  // When more than one client is on the note, the per-client care-plan sections
  // (Support Plan / Essential Support / Intervention) don't apply and stay hidden —
  // that behavior is unchanged, only single-client notes carry a care plan.
  //
  // Studio templates are excluded from isMultiSelectNoteType (they have no
  // "Client Name" custom field), but their own client field IS a multi-select
  // and feeds this same `multiClients` state through onClientsChange — so they
  // get the identical rule: more than one client, no care plan. That's what
  // hides their "Use care plan?" field (see hideCarePlan below).
  const isMultiClientSelected =
    (useMultiClient || isStudioTemplate) && multiClients?.length > 1;

  const [selectedGroupClients, setSelectedGroupClients] = useState([]);
  const initialDescription = {
    outcomeTitle: "",
    outcomeNumber: "",
    outComeCategory: "",
    description: "",
    details: null,
    error: "",
    planId: "1",
  };

  const initialEssentialSupport = {
    id: Date.now(),
    essentialSupportType: "",
    essentialActivityNumber: "",
    description: "",
  };

  const initialGoals = {
    id: Date.now(),
    goalNumber: "",
    goalNote: "",
    startTimeGoal: "",
    endTimeGoal: "",
    goalActivities: [
      { id: Date.now(), objectives: "", Intervention_Instruction: "" },
    ],
    behaviorDescription: "",
    responseDescription: "",
    planDescription: "",
  };
  const [selectedFacility, setSelectedFacility] = useState();
  const today = new Date();

  const [descriptions, setDescriptions] = useState([
    { id: 1, ...initialDescription },
  ]);

  const [essentialSupport, setEssentialSupport] = useState([
    { id: 1, ...initialEssentialSupport },
  ]);

  // Care Plan Activities should surface whenever the matching radio ("Use
  // Support Plan" / "Use Essential Support" / "Use Intervention plan") is Yes
  // AND that plan type is actually attached to the patient — not just because
  // the radio was toggled on with no real plan behind it.
  const hasActiveSupportPlan = useMemo(
    () =>
      !!allPlans?.some(
        (v) =>
          v?.client?._id === selectedPatient?.id &&
          v?.facility?._id === selectedFacility &&
          v?.isActive &&
          v?.planType?.name?.includes("Support Plan")
      ),
    [allPlans, selectedPatient, selectedFacility]
  );

  const hasActiveEssentialSupportPlan = useMemo(
    () =>
      !!allPlans?.some(
        (v) =>
          v?.client?._id === selectedPatient?.id &&
          v?.facility?._id === selectedFacility &&
          v?.isActive &&
          v?.planType?.name?.includes("Essential Supports")
      ),
    [allPlans, selectedPatient, selectedFacility]
  );

  const hasActiveInterventionPlan = useMemo(
    () =>
      !!allPlans?.some(
        (v) =>
          v?.client?._id === selectedPatient?.id &&
          v?.facility?._id === selectedFacility &&
          v?.isActive &&
          v?.planType?.name?.includes("Intervention")
      ),
    [allPlans, selectedPatient, selectedFacility]
  );

  const hasCarePlan =
    (supportPlan === "yes" && hasActiveSupportPlan) ||
    (essentialSupportToggle === "yes" && hasActiveEssentialSupportPlan) ||
    (interventionPlanToggle === "yes" && hasActiveInterventionPlan);

  const [goals, setGoals] = useState([{ id: 1, ...initialGoals }]);

  const { staff, allStaff } = useSelector((state) => state.staff);
  const { allSignature } = useSelector((state) => state?.signature);
  const [staffDetials, setStaffDetials] = useState();
  const draftRestoringRef = useRef(false);
  React.useEffect(() => {
    // Skip reset if we're restoring a draft (otherwise this wipes the restored goals/descriptions)
    if (draftRestoringRef.current) { draftRestoringRef.current = false; return; }
    setDescriptions([{ id: 1, ...initialDescription }]);
    setEssentialSupport([{ id: 1, ...initialEssentialSupport }]);
    setGoals([{ id: 1, ...initialGoals }]);
  }, [selectedFacility]);

  useEffect(() => {
    setRecordingType(null);
  }, []);

  // Selecting more than one client hides the per-client care-plan sections, so
  // force their toggles off — the note must not carry a support/essential/
  // intervention plan when it spans multiple clients.
  useEffect(() => {
    if (isMultiClientSelected) {
      setSupportPlan("no");
      setEssentialSupportToggle("no");
      setInterventionPlanToggle("no");
      // A studio template hides its "Use care plan?" field outright while more
      // than one client is selected, so clear any answer already given — the
      // note must not save "Yes" to a question the user can no longer see.
      if (isStudioTemplate && careplanLabel && !view) {
        form.setFieldValue(careplanLabel, "No");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiClientSelected]);

  // Whenever the drawer closes, clear the multi-selects so the next time it
  // opens for a NEW note it never shows the previous note's clients/staff
  // (the drawer instance can stay mounted between opens).
  useEffect(() => {
    if (!opened) {
      setMultiClients([]);
      setMultiStaff([]);
    }
  }, [opened]);

  // The post-save confirmation modals ("Note Successfully Saved" / "Note
  // Edited") persist after the drawer closes until the user clicks OK. If they
  // instead open a note to view, those modals would show on top of the view
  // drawer — dismiss them the moment the view opens.
  useEffect(() => {
    if (viewOpened) {
      setPendingModal(false);
      setEditModal(false);
    }
  }, [viewOpened]);

  // Auto-select the logged-in user as staff on NEW multi-select notes (they can
  // still add others / remove themselves).
  useEffect(() => {
    if (
      opened &&
      isMultiSelectNoteType &&
      !view &&
      !edit &&
      profile?.user?.id &&
      !multiStaff?.length
    ) {
      setMultiStaff([profile.user.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isMultiSelectNoteType, view, edit, profile?.user?.id]);

  // Prefill the multi-selects when viewing/editing a note. Notes saved with the
  // new array structure carry their value straight through; a legacy note saved
  // with a single scalar id gets that one value wrapped into a 1-item array —
  // it now renders (read-only, per the disabled rule below) as a one-chip
  // multi-select instead of falling back to a plain single Select.
  // Seeds ONCE per opened note. `data` is the rest-props object, so it has a
  // new identity on every single render — keying this effect off it re-ran the
  // seeding after every state change and instantly restored the source note's
  // clients/staff, making both multi-selects impossible to change. That was
  // invisible while editing (they're disabled there) but broke duplicating,
  // where they're meant to be editable. Depend on the note's id instead.
  const multiSelectSeedRef = useRef(null);
  useEffect(() => {
    if (!opened) {
      multiSelectSeedRef.current = null;
      return;
    }
    // Studio templates render their own client picker (it writes straight into
    // the form), so they don't need the seeding below for display — but they DO
    // need multiClients populated, since that's what marks a note as spanning
    // several clients and hides the "Use care plan?" field while it does.
    if (!(edit || view) || !(isMultiSelectNoteType || isStudioTemplate)) return;
    const noteKey = data?.data?.id || data?.data?._id || null;
    if (noteKey && multiSelectSeedRef.current === noteKey) return;
    const c = data?.data?.fields?.["Client Name"];
    const s = data?.data?.fields?.["Staff Name"];
    if (c) {
      const clientIds = Array.isArray(c) ? c : [c];
      setMultiClients(clientIds);
      // Point selectedPatient at the first client so facility/provider derived
      // display stays populated (care-plan sections are hidden for >1 client).
      if (!isStudioTemplate) {
        const firstPatient = allPatients?.find((p) => p?.id === clientIds?.[0]);
        if (firstPatient) setSelectedPatient(firstPatient);
      }
    }
    if (s && !isStudioTemplate) setMultiStaff(Array.isArray(s) ? s : [s]);
    // Only consider the note seeded once the patient list is in — otherwise a
    // seed that ran before allPatients loaded would block the re-run that
    // finally resolves selectedPatient.
    if (allPatients?.length) multiSelectSeedRef.current = noteKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    opened,
    edit,
    view,
    isMultiSelectNoteType,
    isStudioTemplate,
    data?.data?.id,
    allPatients,
  ]);

  // Opened from a specific client's profile — preselect that client.
  useEffect(() => {
    if (
      userId &&
      isMultiSelectNoteType &&
      !edit &&
      !view &&
      !multiClients?.length
    ) {
      setMultiClients([userId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isMultiSelectNoteType, edit, view]);


  // Reset patient when user clicks "Add Later" in the AddEssentialNote/Billing modal
  useEffect(() => {
    if (resetPatientTrigger) {
      setSelectedPatient(null);
      setBillingServices("");
    }
  }, [resetPatientTrigger]);

  // Seed the follow-up note (opened from the "Do you want to add a new
  // essential support note?" / billing modal) with the Client(s), Staff and
  // Facility carried over from the note that preceded it. Runs after the
  // auto-select-self / preselect-client effects above so it wins over their
  // defaults, and only when carryOverRef was set at save time (that flow only).
  // The user is free to change any of them afterwards.
  useEffect(() => {
    if (opened && !edit && !view && progressNoteState && carryOverRef.current) {
      const { clients, staff, facility } = carryOverRef.current;
      carryOverRef.current = null;
      if (clients?.length) {
        setMultiClients(clients);
        const firstPatient = allPatients?.find((p) => p?.id === clients[0]);
        if (firstPatient) setSelectedPatient(firstPatient);
        form.setFieldValue("Client Name", useMultiClient ? clients : clients[0]);
      }
      if (staff?.length) {
        setMultiStaff(staff);
        form.setFieldValue("Staff Name", useMultiStaff ? staff : staff[0]);
      }
      if (facility) {
        setSelectedFacility(facility);
        form.setFieldValue("Facility Name", facility);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, edit, view, progressNoteState]);

  const usageData = useSelector((state) => state.skai.usageHistory);
  const [showWarning, setShowWarning] = useState(false);
  const [messageValue, setMessageValue] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [selectedSupportType, setSelectedSupportType] = useState(null);
  const [activities, setActivities] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [essentialNumber, setEssentialNumber] = useState(null);
  const [behaviorCardOpened, setBehaviorCardOpened] = useState(false);
  const [responseCardOpened, setResponseCardOpened] = useState(false);
  const [planCardOpened, setPlanCardOpened] = useState(false);
  const [behaviorLoading, setBehaviorLoading] = useState(false);
  const [responseLoading, setResponseLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const selectedStateAbbr = getNameFromAbbreviation(
    selectedPatient?.form1?.state
  );

  const usageArray = Array.isArray(usageData) ? usageData : [];
  const existingRecord = usageArray.find(
    (item) =>
      item?.admin === profile?.user?.admin || item?.admin === profile?.user?.id
  );

  const checkTokenUsageWarning = (existingRecord, values) => {
    if (existingRecord?.active === false && existingRecord?.paid === false) {
      swal({
        title: "AI Note Assistant Credits Not Found",
        text: "You have not purchased any AI credits yet. Please visit the Settings page and navigate to the AI credits section to buy credits.",
        icon: "info",
        dangerMode: false,
      });

      return;
    }
    // OD-1230 — warn off the two-bucket balance. This previously read
    // `existingRecord.adminTotalTokens.adminTotalTokens` — a property of a string —
    // so the percentage was always NaN→0 and the warning never fired at all.
    // Free tokens are spent before purchased ones, so the number that matters is the
    // combined remaining balance.
    if (!existingRecord?.planIsUnlimited) {
      const freeBalance = Math.max(0, Number(existingRecord?.freeBalance) || 0);
      const purchasedBalance = Math.max(0, Number(existingRecord?.purchasedBalance) || 0);
      const remaining = freeBalance + purchasedBalance;

      if (remaining <= 0) {
        setShowWarning(true);
        swal({
          title: "AI Note Assistant Quota Reached",
          text: "You've used all of your AI Note Assistant tokens — this month's free tokens and any purchased balance. Free tokens refill at the start of next month, or your admin can buy more from the Settings page.",
          icon: "error",
          dangerMode: true,
        });
        return; // Do NOT allow AI usage
      }

      // Low-balance nudge. An absolute threshold rather than a percentage: purchased
      // tokens have no ceiling to take a percentage of.
      if (remaining <= 10) {
        setShowWarning(true);
        swal({
          title: "AI Note Assistant Tokens Running Low",
          text: `You have ${remaining} AI Note Assistant token${
            remaining === 1 ? "" : "s"
          } left (${freeBalance} free, ${purchasedBalance} purchased). Free tokens refill next month; your admin can buy more anytime.`,
          icon: "warning",
          dangerMode: true,
        });
      }
    }
    sendMessageToOpenAI(values);
    if (values === "behavior") {
      setBehaviorCardOpened(true);
    } else if (values === "response") {
      setResponseCardOpened(true);
    } else if (values === "plan") {
      setPlanCardOpened(true);
    } else {
      setAiAssistantCardOpened(true);
    }

    setShowWarning(false);
  };

  useEffect(() => {
    getSkaiTokenUsage(profile?.user?.admin || profile?.user?.id);
  }, [profile]);

  useEffect(() => {
    getAllFacilities(1);
    // getAllSignatures();
  }, []);

  const [essentialSupportTypeState, setEssentialSupportTypeState] = useState(
    []
  );
  const [goalsAccordion, setGoalsAccordion] = useState("0");
  const [goalOptions, setGoalOptions] = useState([]);
  const [goalNumber, setGoalNumbers] = useState([]);
  const [objectivesByGoal, setObjectivesByGoal] = useState([]);
  const [interventionsByGoal, setInterventionsByGoal] = useState([]);
  const [goalItems, setGoalItems] = useState([
    [{ id: Date.now(), objectives: "", Intervention_Instruction: "" }],
  ]);

  const isAddDisabled =
    goalItems[goalItems.length - 1].length >=
    Math.min(objectivesByGoal.length, interventionsByGoal.length);

  // Add row
  const handleAddGoalItem = (index) => {
    let tempGoalItems = [...goalItems];

    tempGoalItems[index].push({
      id: Date.now(),
      objectives: "",
      Intervention_Instruction: "",
    });

    setGoalItems(tempGoalItems);
    // setGoalItems((prev) => [
    //   ...prev,
    //   { id: Date.now(), objectives: "", Intervention_Instruction: "" },
    // ]);
  };

  // Remove row
  const handleRemoveGoalItem = (id, goalIndex) => {
    const updatedGoalItems = goalItems[goalIndex].filter(
      (item) => item.id !== id
    );
    let finalGoalItems = [...goalItems];
    finalGoalItems[goalIndex] = updatedGoalItems;
    setGoalItems(finalGoalItems);
    // setGoalItems(updatedGoalItems);
    setGoals((prevGoals) =>
      prevGoals.map((goal, index) =>
        index === goalIndex
          ? { ...goal, goalActivities: updatedGoalItems }
          : goal
      )
    );
  };

  // Outcome Title / Outcome # are optional when a care plan is authored, and
  // Mantine's Select renders an option's raw `value` whenever its label is
  // empty — which put the outcome's internal id (a Date.now() timestamp) in the
  // dropdown instead of a name. Always hand the option something readable.
  const outcomeOptionLabel = (outcome, plan) => {
    const word =
      planTypeLabels[plan?.planType?.planId]?.addNewButtonLabel || "Outcome";
    return (
      outcome?.outcomeTitle ||
      outcome?.customOutcome ||
      outcome?.outcomeCategory ||
      `${word} ${outcome?.outcomeNumber ?? ""}`.trim()
    );
  };

  const outcomeNumberOptionLabel = (outcome) => {
    const number = outcome?.outcomeNumber;
    return number || number === 0 ? String(number) : "—";
  };

  useEffect(() => {
    let outcomeTitle = [];
    let outcomeNumberArray = [];
    let essentialSupportArray = [];
    let essentialSupportNumber = [];
    let planIdToSave = null;
    let goalTitleOptions = [];
    let goalNumberOptions = [];

    if (noteType?.name === "Intervention Progress Note") {
      const filteredGoals = selectedPatient
        ? allPlans
            ?.filter(
              (v) =>
                v?.client?._id === selectedPatient?.id &&
                v?.facility?._id === selectedFacility
            )
            ?.map((v) => {
              v?.goals?.map((goal) => {
                if (edit) {
                  if (
                    v?.status === "active" &&
                    v?.isActive === true &&
                    v?.deleted !== true
                  ) {
                    goalTitleOptions.push({
                      value: goal?.Identified_Relevant_Attainable_Goals,
                      label: goal?.Identified_Relevant_Attainable_Goals,
                    });

                    goalNumberOptions.push({
                      value: goal?.goalNumber,
                      label: goal?.goalNumber,
                    });
                  }
                } else {
                  if (
                    v?.status === "active" &&
                    v?.isActive === true &&
                    v?.deleted !== true
                  ) {
                    goalTitleOptions.push({
                      value: goal?.Identified_Relevant_Attainable_Goals,
                      label: goal?.Identified_Relevant_Attainable_Goals,
                      disabled: goal.hasOwnProperty("isActive")
                        ? !goal?.isActive
                        : false,
                    });

                    goalNumberOptions.push({
                      value: goal?.goalNumber,
                      label: goal?.goalNumber,
                      disabled: goal.hasOwnProperty("isActive")
                        ? !goal?.isActive
                        : false,
                    });
                  }
                }
              });
              return v;
            })
        : [];
    } else {
      const filtered = selectedPatient
        ? allPlans
            ?.filter(
              (v) =>
                v?.client?._id === selectedPatient?.id &&
                v?.facility?._id === selectedFacility
            )
            ?.map((v) => {
              v?.outcomes?.map((vv) => {
                // If block is for edit and else block is for add
                if (edit) {
                  // Check if care plan is acive or discontinued
                  if (
                    v?.status === "active" &&
                    v?.isActive === true &&
                    v?.deleted !== true
                  ) {
                    // if care plan is active then push all the outcomes
                    outcomeTitle.push({
                      value: vv?.id,
                      label: outcomeOptionLabel(vv, v),
                      disabled: vv.hasOwnProperty("isActive")
                        ? !vv?.isActive
                        : false,
                    });
                    outcomeNumberArray.push({
                      value: vv?.id,
                      label: outcomeNumberOptionLabel(vv),
                      disabled: vv.hasOwnProperty("isActive")
                        ? !vv?.isActive
                        : false,
                    });

                    if (v?.planType?.planId) {
                      planIdToSave = v?.planType?.planId;
                    }
                    // essential support details
                    if (v?.essentialSupport) {
                      if (v?.essentialSupport?.length > 0) {
                        setEssentialSupportCount(
                          v?.essentialSupport?.flatMap((_es) => _es?.activities)
                        );
                        // setEssentialSupportCount(v?.essentialSupport);
                      }
                    }
                  } else if (
                    v?.status === "discontinued" &&
                    v?.isActive === false
                  ) {
                    // if care plan is discontinued then check if one of its outcome is used while creating progress note
                    let filteredDesc = data?.data?.descriptions?.filter(
                      (_d) => _d?.outcomeNumber === vv?.id
                    );

                    if (filteredDesc?.length > 0) {
                      // if outcome is used then push the outcome
                      outcomeTitle.push({
                        value: vv?.id,
                        label: `${outcomeOptionLabel(vv, v)} (D)`,
                        disabled: true,
                      });
                      outcomeNumberArray.push({
                        value: vv?.id,
                        label: `${outcomeNumberOptionLabel(vv)} (D)`,
                        disabled: true,
                      });
                    }
                  }
                } else {
                  if (
                    v?.status === "active" &&
                    v?.isActive === true &&
                    v?.deleted !== true
                  ) {
                    outcomeTitle.push({
                      value: vv?.id,
                      label: outcomeOptionLabel(vv, v),
                      disabled: vv.hasOwnProperty("isActive")
                        ? !vv?.isActive
                        : false,
                    });
                    outcomeNumberArray.push({
                      value: vv?.id,
                      label: outcomeNumberOptionLabel(vv),
                      disabled: vv.hasOwnProperty("isActive")
                        ? !vv?.isActive
                        : false,
                    });
                    // If planType is present then save the planId to state for labels
                    if (v?.planType?.planId) {
                      planIdToSave = v?.planType?.planId;
                    }
                    // essential support details
                    if (v?.essentialSupport) {
                      if (v?.essentialSupport?.length > 0) {
                        setEssentialSupportCount(
                          v?.essentialSupport?.flatMap((_es) => _es?.activities)
                        );
                        // setEssentialSupportCount(v?.essentialSupport);
                      }
                    }

                    return v;
                  }
                }
              });

              if (
                v?.status === "active" &&
                v?.isActive === true &&
                v?.deleted !== true
              ) {
                v?.essentialSupport?.map((vv) => {
                  essentialSupportArray.push({
                    value: vv?.id,
                    label: vv?.essentialSupportType,
                  });

                  vv?.activities?.map((_a) => {
                    essentialSupportNumber.push({
                      value: _a?.id,
                      label: _a?.supportStatement,
                    });
                  });
                });
                // Get essential support types
                const getEssentialSupportTypes = Array.from(
                  new Set(
                    v?.essentialSupport?.map(
                      (item) => item.essentialSupportType
                    )
                  )
                ).filter(Boolean);

                const formatted = getEssentialSupportTypes.map((type) => ({
                  label: type,
                  value: type,
                }));
                setEssentialSupportTypeState(formatted);

                // Filter activities based on the selected support type
                const filteredData = v?.essentialSupport?.filter(
                  (item) => item.essentialSupportType === selectedSupportType
                );
                const allActivities = filteredData?.map(
                  (item) => item.activities
                );
                // setActivities(allActivities);
              }
            })
        : [];
    }
    // setObjectivesByGoal(objectivesMap);
    // setInterventionsByGoal(interventionsMap);

    setGoalOptions(goalTitleOptions);
    setGoalNumbers(goalNumberOptions);
    // setGoalActivities(goalActivitiesOptions);

    // Set the planId to the state
    if (planIdToSave) {
      // setSavePlanId(planIdToSave);
    }
    setEssentialSupportNumber(essentialSupportNumber);
    setEssentialSupportType(essentialSupportArray);
    setOutComeTitle(outcomeTitle || []);
    setOutComeNumbers(outcomeNumberArray || []);
    if (selectedPatient) {
      let serviceNumber = selectedPatient?.form1?.services?.filter(
        (i) => i?.servicenumber == data?.data?.fields["Service Type"]
      )?.[0];
      setServiceNumber(serviceNumber);
    }
  }, [selectedPatient, allPlans, selectedFacility, selectedSupportType, noteType]);

  // useEffect(() => {
  //   // essential support details
  //   allPlans
  //     ?.filter(
  //       (v) =>
  //         v?.client?._id === selectedPatient?.id &&
  //         v?.facility?._id === selectedFacility
  //     )
  //     ?.map((v) => {
  //       setOutcomesLength(v?.outcomes?.length);
  //     });
  // }, []);

  // `value` is the clicked radio ("yes"/"no"); fall back to flipping the current
  // state for any caller that doesn't pass one.
  const handleSupportPlanChange = (value) => {
    setSupportPlan(
      value === "yes" || value === "no"
        ? value
        : supportPlan === "yes"
        ? "no"
        : "yes"
    );
    setAiAssistantCardOpened(false);
    setMessages([]);
    setAiActionsOpen(false);
    setComparePanel(COMPARE_PANEL_RESET);
    form.setFieldValue("Description", "");
    // Clearing the last outcome's text used to mutate `descriptions` in place —
    // which threw (and so aborted the toggle entirely) whenever descriptions was
    // empty or undefined, e.g. on a note saved without a care plan.
    setDescriptions((prev) => {
      const list = prev?.length ? prev : [{ id: 1, ...initialDescription }];
      return list.map((d, i) =>
        i === list.length - 1 ? { ...d, description: "" } : d
      );
    });
  };

  // Add New Outcome Handler
  const addDescription = (e) => {
    setAiAssistantCardOpened(false);
    handleRecordingConfirm();
    // setMessages([]);
    if (descriptions) {
      setDescriptions([
        ...descriptions,
        { id: Date.now(), ...initialDescription },
      ]);
      setOpenedAccordion(`${descriptions?.length}`);
    }
  };

  // // Add New Essential Support Handler
  const addEssentialSupport = () => {
    setAiAssistantCardOpened(false);

    handleRecordingConfirm();
    if (essentialSupport?.length < essentialSupportCount?.length) {
      setEssentialSupport([
        ...essentialSupport,
        {
          id: Date.now(),
          ...initialEssentialSupport,
        },
      ]);
      setEssentialAccordion(`${essentialSupport?.length}`);
    }
  };

  // // Add New Goal handler
  const addNewGoal = () => {
    // setAiAssistantCardOpened(false);

    // handleRecordingConfirm();
    if (goals?.length < goalOptions?.length) {
      setGoals([
        ...goals,
        {
          id: Date.now(),
          ...initialGoals,
          goalActivities: [
            { id: Date.now(), objectives: "", Intervention_Instruction: "" },
          ],
        },
      ]);
      setGoalsAccordion(`${goals?.length}`);
      setGoalItems([
        ...goalItems,
        [{ id: Date.now(), objectives: "", Intervention_Instruction: "" }],
      ]);
      setBehaviorCardOpened(false);
      setResponseCardOpened(false);
      setPlanCardOpened(false);
    }
  };

  // Remove Outcome Handler
  const removeMedication = (medicationId) => {
    setDescriptions(descriptions?.filter((med) => med.id !== medicationId));
  };

  // Remove Essential Support Handler
  const removeEssentialSupport = (medicationId) => {
    const filtered = essentialSupport?.filter((med) => {
      return med.id !== medicationId;
    });
    setEssentialSupport(filtered);
  };

  // Remove goal Handler
  const removeGoal = (Id) => {
    const filtered = goals?.filter((item) => {
      return item.id !== Id;
    });
    setGoals(filtered);
  };

  // Edit Outcome Inputs Handler
  const handleMedicationChange = (medicationId, field, value, indexx) => {
    let filteredObj = null;
    if (field === "outcomeTitle") {
      filteredObj = outComeTitle?.filter((item) => item?.value === value)?.[0];
    } else if (field === "outcomeNumber") {
      filteredObj = outcomeNumbers?.filter(
        (item) => item?.value === value
      )?.[0];
    }

    let previous = descriptions?.find(
      (v) => v?.outcomeTitle === value || v?.outcomeNumber === value
    );
    let rest = {};
    let choosenPlan = null;
    if (field === "outcomeTitle" || field === "outcomeNumber") {
      let { id, ...others } =
        allPlans
          ?.filter(
            (v) =>
              v?.client?._id === selectedPatient?.id &&
              v?.facility?._id === selectedFacility
          )
          ?.filter((_fp) => _fp?.outcomes?.find((_oc) => _oc?.id === value))?.[0]
          ?.outcomes?.find((v) => v?.id === value) || {};
      rest = others;

      allPlans
        ?.filter(
          (v) =>
            v?.client?._id === selectedPatient?.id &&
            v?.facility?._id === selectedFacility
        )
        ?.map((_fp) => {
          if (_fp?.outcomes?.find((_oc) => _oc?.id === value)) {
            choosenPlan = _fp;
          }
        });
    }

    setDescriptions((prevMedications) =>
      prevMedications.map((med, index) => {
        if (index === indexx) {
          if (field !== "metGoal" && field !== "description") {
            return med?.id === medicationId
              ? {
                  ...med,
                  ...(outcomeNotes[index]?.length > 0 && {
                    description: outcomeNotes[index],
                  }),
                  [field]: value,
                  [field == "outcomeTitle" ? "outcomeNumber" : "outcomeTitle"]:
                    filteredObj?.value,
                  outComeCategory:
                    field === "outcomeTitle" || field === "outcomeNumber"
                      ? allPlans
                          ?.filter(
                            (v) =>
                              v?.client?._id === selectedPatient?.id &&
                              v?.facility?._id === selectedFacility
                          )
                          ?.filter((_fp) =>
                            _fp?.outcomes?.find((_oc) => _oc?.id === value)
                          )?.[0]
                          ?.outcomes?.find((v) => v?.id === value)
                          ?.outcomeCategory
                      : med?.outComeCategory,
                  details:
                    field === "outcomeTitle" || field === "outcomeNumber"
                      ? filterNonNullFields(rest)
                      : med?.details,
                  error: previous ? "Already Selected" : null,
                  planId: choosenPlan ? choosenPlan?.planType?.planId : "1",
                }
              : med;
          } else {
          }
          let obj = {
            ...med,
            [field]: value,
            outComeCategory:
              field === "outcomeTitle" || field === "outcomeNumber"
                ? allPlans
                    ?.filter(
                      (v) =>
                        v?.client?._id === selectedPatient?.id &&
                        v?.facility?._id === selectedFacility
                    )
                    ?.filter((_fp) =>
                      _fp?.outcomes?.find((_oc) => _oc?.id === value)
                    )?.[0]
                    ?.outcomes?.find((v) => v?.id === value)?.outcomeCategory
                : med?.outComeCategory,
            details:
              field === "outcomeTitle" || field === "outcomeNumber"
                ? filterNonNullFields(rest)
                : med?.details,
            error: previous ? "Already Selected" : null,
          };
          if (field === "outcomeTitle" || field === "outcomeNumber") {
            obj.planId = choosenPlan ? choosenPlan?.planType?.planId : "1";
          }
          return med.id === medicationId ? obj : med;
        } else {
          return med;
        }
      })
    );
    setErrors("");
  };

  const handleEssentialTypeChange = (supportId, field, value, indexx) => {
    if (field === "identifiedGoal") {
      // setGoalItems([
      //   { id: Date.now(), objectives: "", Intervention_Instruction: "" },
      // ]);
      setGoals((prev) =>
        prev?.map((item, index) => {
          if (index === indexx) {
            return item.id === supportId
              ? {
                  ...item,
                  [field]: value,
                  goalNumber: "",
                }
              : item;
          } else {
            return item;
          }
        })
      );
      const matchingGoal = allPlans
        ?.filter(
          (v) =>
            v?.client?._id === selectedPatient?.id &&
            v?.facility?._id === selectedFacility &&
            v?.status === "active" &&
            v?.isActive === true &&
            v?.deleted !== true
        )
        ?.flatMap((v) => v?.goals || [])
        ?.filter((_g) => _g?.Identified_Relevant_Attainable_Goals === value);

      const formattedObjectives = matchingGoal?.flatMap(
        (goal) =>
          goal?.goalActivities?.map((_ga) => ({
            label: _ga.objectives,
            value: _ga.objectives,
          })) || []
      );
      setObjectivesByGoal(
        formattedObjectives.length > 0 ? formattedObjectives : []
      );

      const formattedInterventions = matchingGoal?.flatMap(
        (goal) =>
          goal?.goalActivities?.map((_ga) => ({
            label: _ga.Intervention_Instruction,
            value: _ga.Intervention_Instruction,
          })) || []
      );
      setInterventionsByGoal(
        formattedInterventions.length > 0 ? formattedInterventions : []
      );

      let newGoalItems = [...goalItems];
      newGoalItems[indexx] = [
        {
          id: Date.now(),
          objectives: "",
          Intervention_Instruction: "",
        },
      ];
      setGoalItems(newGoalItems);
      const formattedGoalNumber = Array.from(
        new Set(matchingGoal.map((item) => item?.goalNumber).filter(Boolean))
      ).map((freq) => ({
        label: freq,
        value: freq,
      }));
      setGoals((prev) =>
        prev?.map((item, index) => {
          if (index === indexx && item.id === supportId) {
            return {
              ...item,
              goalNumber: formattedGoalNumber?.map((goal) => goal.value)[0],
            };
          }
          return item;
        })
      );
    } else {
      setEssentialSupport((prev) =>
        prev?.map((item, index) => {
          if (index === indexx) {
            return item.id === supportId
              ? {
                  ...item,
                  [field]: value,
                  essentialSupportType: value,
                  howOftenEssentialSupport: "", // reset on type change
                  essentialNumber: "", // reset on type change
                  essentialActivityNumber: "", // reset on type change
                }
              : item;
          } else {
            return item;
          }
        })
      );
      setSelectedSupportType(value);

      const matchingSupport = allPlans
        ?.filter(
          (v) =>
            v?.client?._id === selectedPatient?.id &&
            v?.facility?._id === selectedFacility &&
            v?.status === "active" &&
            v?.isActive === true &&
            v?.deleted !== true
        )
        ?.flatMap((v) => v?.essentialSupport || [])
        ?.filter((_es) => _es?.essentialSupportType === value);

      // ✅ Handle both old and new data structures
      // const allFrequencies =
      //   matchingSupport?.flatMap((support) => {
      //     // case 1: old structure (top-level howOftenEssentialSupport)
      //     if (support?.howOftenEssentialSupport) {
      //       return support?.howOftenEssentialSupport;
      //     }

      //     // case 2: new structure (inside activities)
      //     if (Array.isArray(support?.activities)) {
      //       return support?.activities
      //         ?.map((activity) => activity?.howOftenEssentialSupport)
      //         ?.filter(Boolean);
      //     }
      //     return [];
      //   }) || [];

      const allFrequencies =
        matchingSupport?.flatMap((support) => {
          // 1️⃣ First check inside activities
          const activityFrequencies =
            support?.activities
              ?.map((activity) => activity?.howOftenEssentialSupport)
              ?.filter(Boolean) || [];

          // 2️⃣ If found inside activities, use them
          if (activityFrequencies.length > 0) {
            return activityFrequencies;
          }

          // 3️⃣ Otherwise, fall back to top-level
          if (support?.howOftenEssentialSupport) {
            return [support?.howOftenEssentialSupport];
          }

          // 4️⃣ Default: return empty array
          return [];
        }) || [];

      const uniqueFrequencies = [...new Set(allFrequencies)];

      const formattedFrequency = uniqueFrequencies.map((freq) => ({
        label: freq,
        value: freq,
      }));

      setFrequencyOptions(
        formattedFrequency.length > 0 ? formattedFrequency : []
      );

      // ✅ Frequency is optional on the care plan — for supports of this type
      // that have no frequency set at all, surface their outcome number right
      // away instead of waiting on a frequency selection that will never come.
      const supportsWithoutFrequency = matchingSupport?.filter((support) => {
        const hasActivityFrequency = support?.activities?.some(
          (activity) => activity?.howOftenEssentialSupport
        );
        return !hasActivityFrequency && !support?.howOftenEssentialSupport;
      });

      const formattedEssentialNumbers = Array.from(
        new Set(
          supportsWithoutFrequency
            ?.map((item) => item?.essentialNumber)
            ?.filter(Boolean)
        )
      ).map((num) => ({ label: num, value: num }));
      setEssentialNumber(formattedEssentialNumbers);

      // ✅ Extract all activities from matching supports
      const allActivities =
        matchingSupport?.flatMap((support) => support?.activities || []) || [];

      // ✅ Format activities for the dropdown
      const formattedActivities = allActivities.map((activity) => ({
        supportStatement: activity?.supportStatement,
        id: activity?.id,
      }));
      setActivities(formattedActivities);
    }
  };

  const handleEssentialTypeChangeForNoteView = (value, frequency) => {
    const matchingSupport = allPlans
      ?.filter(
        (v) =>
          v?.client?._id === selectedPatient?.id &&
          v?.facility?._id === selectedFacility &&
          v?.status === "active" &&
          v?.isActive === true &&
          v?.deleted !== true
      )
      ?.flatMap((v) => v?.essentialSupport || [])
      ?.filter((_es) => _es?.essentialSupportType === value);

    if (!matchingSupport?.length) return;

    let allFrequencies = [];
    matchingSupport?.forEach((item) => {
      const foundActs = item?.activities?.filter(
        (act) =>
          act?.howOftenEssentialSupport && item?.essentialSupportType === value
      );
      if (foundActs?.length) {
        foundActs.forEach((act) =>
          allFrequencies.push(act?.howOftenEssentialSupport)
        );
      } else if (item?.howOftenEssentialSupport) {
        allFrequencies.push(item?.howOftenEssentialSupport);
      }
    });

    const formattedFrequency = Array.from(new Set(allFrequencies)).map(
      (freq) => ({ label: freq, value: freq })
    );
    setFrequencyOptions(formattedFrequency);

    const matchingFrequencySupports = matchingSupport?.filter((_es) => {
      const hasActivityMatch = _es?.activities?.some(
        (act) =>
          act?.howOftenEssentialSupport === frequency &&
          _es?.essentialSupportType === value
      );
      // Frequency is optional — supports of the same type with no frequency
      // at all should still contribute their outcome number.
      const hasNoFrequency =
        !_es?.howOftenEssentialSupport &&
        !_es?.activities?.some((act) => act?.howOftenEssentialSupport);
      return (
        hasActivityMatch ||
        _es?.howOftenEssentialSupport === frequency ||
        hasNoFrequency
      );
    });

    const formattedEssentialNumber = Array.from(
      new Set(
        matchingFrequencySupports
          ?.map((item) => item?.essentialNumber)
          ?.filter(Boolean)
      )
    ).map((num) => ({ label: num, value: num }));
    setEssentialNumber(formattedEssentialNumber);

    let relatedActivities = [];
    matchingSupport?.forEach((item) => {
      const hasNoFrequency =
        !item?.howOftenEssentialSupport &&
        !item?.activities?.some((act) => act?.howOftenEssentialSupport);

      if (hasNoFrequency && item?.essentialSupportType === value) {
        if (item?.activities?.length) {
          relatedActivities.push(...item.activities);
        } else {
          relatedActivities.push({
            ...item,
            id: item.activities?.[0]?.id || item.id,
            supportStatement:
              item.activities?.[0]?.supportStatement || item.supportStatement,
          });
        }
        return;
      }

      const foundActs = item?.activities?.filter(
        (act) =>
          act?.howOftenEssentialSupport === frequency &&
          item?.essentialSupportType === value
      );
      if (foundActs?.length) {
        relatedActivities.push(...foundActs);
      } else if (
        item?.howOftenEssentialSupport === frequency &&
        item?.essentialSupportType === value
      ) {
        relatedActivities.push({
          ...item,
          id: item.activities?.[0]?.id || item.id, // prefer activity id if available
          supportStatement:
            item.activities?.[0]?.supportStatement || item.supportStatement,
        });
      }
    });

    relatedActivities = relatedActivities.filter(
      (act, idx, self) => idx === self.findIndex((a) => a?.id === act?.id)
    );

    setActivities(relatedActivities);
  };

  const handleEssentialFrequencyChange = (supportId, field, value, indexx) => {
    setEssentialSupport((prev) =>
      prev?.map((item, index) => {
        if (index === indexx) {
          return item.id === supportId
            ? {
                ...item,
                [field]: value,
                howOftenEssentialSupport: value,
                //essentialNumber: "",
                essentialNumber: item?.essentialNumber,
                essentialActivityNumber: "",
              }
            : item;
        } else return item;
      })
    );

    const allEssentialSupports = allPlans
      ?.filter(
        (v) =>
          v?.client?._id === selectedPatient?.id &&
          v?.facility?._id === selectedFacility &&
          v?.status === "active" &&
          v?.isActive === true &&
          v?.deleted !== true
      )
      ?.flatMap((v) => v?.essentialSupport || []);

    const filteredSupports = allEssentialSupports?.filter((_es) => {
      const hasActivityMatch = _es?.activities?.some(
        (act) =>
          act?.howOftenEssentialSupport === value &&
          _es?.essentialSupportType === selectedSupportType
      );
      // Frequency is optional — supports of the same type with no frequency
      // at all should still contribute their outcome number.
      const hasNoFrequency =
        _es?.essentialSupportType === selectedSupportType &&
        !_es?.howOftenEssentialSupport &&
        !_es?.activities?.some((act) => act?.howOftenEssentialSupport);
      return (
        hasActivityMatch ||
        _es?.howOftenEssentialSupport === value ||
        hasNoFrequency
      );
    });

    const formattedEssentialNumbers = Array.from(
      new Set(
        filteredSupports?.map((item) => item?.essentialNumber).filter(Boolean)
      )
    ).map((num) => ({ label: num, value: num }));
    setEssentialNumber(formattedEssentialNumbers);

    let relatedActivities = [];
    filteredSupports?.forEach((item) => {
      const hasNoFrequency =
        !item?.howOftenEssentialSupport &&
        !item?.activities?.some((act) => act?.howOftenEssentialSupport);

      if (hasNoFrequency && item?.essentialSupportType === selectedSupportType) {
        if (item?.activities?.length) {
          relatedActivities.push(...item.activities);
        } else {
          relatedActivities.push({
            ...item,
            id: item.activities?.[0]?.id || item.id,
            supportStatement:
              item.activities?.[0]?.supportStatement || item.supportStatement,
          });
        }
        return;
      }

      const foundActs = item?.activities?.filter(
        (act) =>
          act?.howOftenEssentialSupport === value &&
          item?.essentialSupportType === selectedSupportType
      );
      if (foundActs?.length) {
        relatedActivities.push(...foundActs);
      } else if (
        item?.howOftenEssentialSupport === value &&
        item?.essentialSupportType === selectedSupportType
      ) {
        relatedActivities.push({
          ...item,
          id: item.activities?.[0]?.id || item.id, // prefer activity id if available
          supportStatement:
            item.activities?.[0]?.supportStatement || item.supportStatement,
        });
      }
    });

    relatedActivities = relatedActivities.filter(
      (act, idx, self) => idx === self.findIndex((a) => a?.id === act?.id)
    );

    setActivities(relatedActivities);
  };

  const handleEssentialNumberChange = (supportId, field, value, indexx) => {
    if (field === "goalNumber") {
      setGoals((prev) =>
        prev?.map((item, index) => {
          if (index === indexx) {
            return item.id === supportId
              ? {
                  ...item,
                  [field]: value,
                  goalNumber: value,
                  // essentialActivityNumber: "", // reset on type change
                }
              : item;
          } else {
            return item;
          }
        })
      );
    } else {
      setEssentialSupport((prev) =>
        prev?.map((item, index) => {
          if (index === indexx) {
            return item.id === supportId
              ? {
                  ...item,
                  [field]: value,
                  essentialNumber: value,
                  essentialActivityNumber: "", // reset on type change
                }
              : item;
          } else {
            return item;
          }
        })
      );
    }
  };
  const handleEssentialChange = (supportId, field, value, indexx) => {
    let filtered_plan_1 = allPlans?.filter(
      (v) =>
        v?.client?._id === selectedPatient?.id &&
        v?.facility?._id === selectedFacility &&
        v?.status === "active" &&
        v?.isActive === true &&
        v?.deleted !== true
    );

    // Find the essential support that includes this activity
    const selectedSupport = filtered_plan_1
      ?.flatMap((plan) => plan.essentialSupport || [])
      ?.find((es) => es.activities?.some((a) => a.id === value));

    const selectedActivity = selectedSupport?.activities?.find(
      (a) => a.id === value
    );

    const allFrequencies = (() => {
      if (!selectedSupport) return [];

      // Case 1: Activities have their own howOftenEssentialSupport
      if (
        Array.isArray(selectedSupport?.activities) &&
        selectedSupport.activities.length > 0
      ) {
        const activityFrequencies = selectedSupport.activities
          .map((activity) => activity?.howOftenEssentialSupport)
          .filter(Boolean);

        if (activityFrequencies.length > 0) {
          return activityFrequencies;
        }
      }

      // Case 2: Top-level howOftenEssentialSupport (old structure)
      if (selectedSupport?.howOftenEssentialSupport) {
        return [selectedSupport.howOftenEssentialSupport];
      }

      return [];
    })();

    const uniqueFrequencies = [...new Set(allFrequencies)];

    const formattedFrequency = uniqueFrequencies.map((freq) => ({
      label: freq,
      value: freq,
    }));

    setFrequencyOptions(
      formattedFrequency.length > 0 ? formattedFrequency : []
    );

    // // Find the essential support that includes this activity
    // const selectedSupport = filtered_plan_1
    //   ?.flatMap((plan) => plan.essentialSupport || [])
    //   ?.find((es) => es.activities?.some((a) => a.id === value));

    // const matchingSupport = filtered_plan_1?.flatMap((v) => v?.essentialSupport || [])
    //   ?.filter((_es) => _es?.essentialSupportType === selectedSupport?.essentialSupportType);

    // const selectedActivity = selectedSupport.activities?.find(
    //   (a) => a.id === value
    // );

    // let allFrequencies = [];
    // matchingSupport?.forEach((item) => {
    //   const foundActs = item?.activities?.filter(
    //     (act) =>
    //       act?.howOftenEssentialSupport && item?.essentialSupportType === selectedSupport?.essentialSupportType
    //   );
    //   if (foundActs?.length) {
    //     foundActs.forEach((act) =>
    //       allFrequencies.push(act?.howOftenEssentialSupport)
    //     );
    //   } else if (item?.howOftenEssentialSupport) {
    //     allFrequencies.push(item?.howOftenEssentialSupport);
    //   }
    // });

    // const formattedFrequency = Array.from(new Set(allFrequencies)).map(
    //   (freq) => ({ label: freq, value: freq })
    // );
    // setFrequencyOptions(formattedFrequency);

    const finalHowOften =
      selectedActivity?.howOftenEssentialSupport ||
      selectedSupport?.howOftenEssentialSupport ||
      "";

    const formattedEssentialNumber = Array.from(
      new Set(
        filtered_plan_1
          ?.flatMap((v) => v?.essentialSupport || [])
          ?.map((item) => item?.essentialNumber)
          ?.filter(Boolean)
      )
    ).map((num) => ({ label: num, value: num }));
    setEssentialNumber(formattedEssentialNumber);

    let selectedEsType = [];

    filtered_plan_1?.forEach((_fp) => {
      _fp?.essentialSupport?.forEach((_es) => {
        if (_es?.activities?.find((_a) => _a?.id == value)) {
          selectedEsType.push(_es);
        }
      });
    });

    setEssentialSupport((prev) =>
      prev?.map((item, index) => {
        if (index === indexx && item.id === supportId) {
          const selectedSupport = selectedEsType?.[0];

          // const selectedActivity = selectedSupport?.activities?.find(
          //   (act) => act?.id === value
          // );

          return {
            ...item,
            ...selectedSupport,
            essentialActivityNumber: value,
            howOftenEssentialSupport: finalHowOften,
            essentialNumber: selectedSupport?.essentialNumber,
          };
        }
        return item;
      })
    );
  };

  // add note of respective essential plan
  const handleEssentialSupportDesc = (supportId, field, value, indexx) => {
    if (isIntervention_Progress_Note) {
      setGoals((prev) =>
        prev?.map((item, index) => {
          if (index === indexx) {
            return item.id === supportId ? { ...item, [field]: value } : item;
          } else {
            return item;
          }
        })
      );
    } else {
      setEssentialSupport((prev) =>
        prev?.map((item, index) => {
          if (index === indexx) {
            return item.id === supportId ? { ...item, [field]: value } : item;
          } else {
            return item;
          }
        })
      );
    }
  };

  const handleGoalActivityChange = (id, field, value, index, goalIndex) => {
    const matchingActivities = allPlans
      ?.filter(
        (v) =>
          v?.client?._id === selectedPatient?.id &&
          v?.facility?._id === selectedFacility &&
          v?.status === "active" &&
          v?.isActive === true &&
          v?.deleted !== true
      )
      ?.flatMap((v) => v?.goals || [])
      ?.flatMap((g) => g?.goalActivities || []);

    const updatedGoalItems = goalItems[goalIndex].map((item) => {
      if (item.id !== id) return item;

      if (field === "objectives") {
        const selectedActivity = matchingActivities?.find(
          (ga) => ga?.objectives === value
        );
        return {
          ...item,
          objectives: value,
          Intervention_Instruction:
            selectedActivity?.Intervention_Instruction || "",
        };
      }

      if (field === "Intervention_Instruction") {
        const selectedActivity = matchingActivities?.find(
          (ga) => ga?.Intervention_Instruction === value
        );
        return {
          ...item,
          Intervention_Instruction: value,
          objectives: selectedActivity?.objectives || "",
        };
      }

      return { ...item, [field]: value };
    });

    let finalGoalItems = [...goalItems];

    finalGoalItems[goalIndex] = updatedGoalItems;

    setGoalItems(finalGoalItems);
    // setGoalItems(updatedGoalItems);
    setGoals((prevGoals) =>
      prevGoals.map((goal, i) =>
        i === goalIndex ? { ...goal, goalActivities: updatedGoalItems } : goal
      )
    );
  };

  useEffect(() => {
    if (getGoalsData && getGoalsData.length > 0) {
      // Extract goal activities from all goals
      const allActivities = getGoalsData.flatMap(
        (goal) => goal.goalActivities || []
      );

      let finalGoalItems = [];

      getGoalsData?.map((_g, index) => {
        finalGoalItems.push(_g.goalActivities);
      });

      setGoalItems(finalGoalItems);

      // Set goal items for display
      // setGoalItems(allActivities);

      const formattedObjectives = allActivities?.map((_ga) => ({
        label: _ga.objectives,
        value: _ga.objectives,
      }));

      setObjectivesByGoal(
        formattedObjectives.length > 0 ? formattedObjectives : []
      );

      const formattedInterventions = allActivities?.map((_ga) => ({
        label: _ga.Intervention_Instruction,
        value: _ga.Intervention_Instruction,
      }));

      setInterventionsByGoal(
        formattedInterventions.length > 0 ? formattedInterventions : []
      );
    }
  }, [getGoalsData]);

  // Reset Form
  const resetForm = (preservePatient = false) => {
    setErrors("");
    setFormErrors({});
    form.reset();
    setEssentialSupportErrors([]);
    aiTouchedFieldsRef.current = {};

    setOutcomeNotes([]);
    setAiAssistantCardOpened(false);
    setBehaviorCardOpened(false);
    setResponseCardOpened(false);
    setPlanCardOpened(false);

    setEssentialNotes([]);
    setSupportPlan("no");
    setMessages([]);
    setEssentialSupportToggle("no");
    setInterventionPlanToggle("no");
    setEssentialSupport([{ id: 1, ...initialEssentialSupport }]);
    setGoals([{ id: 1, ...initialGoals }]);
    setGoalItems([
      [{ id: Date.now(), objectives: "", Intervention_Instruction: "" }],
    ]);
    setAiActionsOpen(false);
    setComparePanel(COMPARE_PANEL_RESET);
    setServiceNumber(null);
    handleRecordingCancel();
    setEssentialAccordion("0");
    setGoalsAccordion("0");

    setEssentialSupportTypeState([]);
    setEssentialSupportNumber([]);
    setSelectedSupportType(null);
    setEssentialNumber(null);
    setFrequencyOptions([]);
    setActivities([]);

    // Incident Report answers live outside `form`, so form.reset() above does
    // not touch them — clear them here or the next new report opens pre-filled
    // with the previous one's incident type, severity, clients and follow-up.
    setIncidentNoteData({ ...INITIAL_INCIDENT_NOTE });

    if (!preservePatient) {
      setBillingServices("");
      setSelectedPatient(null);
    }
    if (NOTETYPE === Billing_Services_Note) {
      setBillingServices("");
      setSelectedPatient(null);
    }
    setSelectedGroupClients([]);
    setMultiClients([]);
    setMultiStaff([]);
    close();
    // setPatientsId(null);
  };

  const cancelForm = () => {
    setErrors("");
    setFormErrors({});
    form.reset();
    setNoteType(null);
    setOutcomeNotes([]);
    setAiAssistantCardOpened(false);
    setBehaviorCardOpened(false);
    setResponseCardOpened(false);
    setPlanCardOpened(false);

    setEssentialNotes([]);
    setSupportPlan("no");
    setMessages([]);
    setEssentialSupportToggle("no");
    setInterventionPlanToggle("no");
    setEssentialSupport([{ id: 1, ...initialEssentialSupport }]);
    setGoals([{ id: 1, ...initialGoals }]);
    setGoalItems([
      [{ id: Date.now(), objectives: "", Intervention_Instruction: "" }],
    ]);
    setAiActionsOpen(false);
    setComparePanel(COMPARE_PANEL_RESET);
    setServiceNumber(null);
    handleRecordingCancel();
    setEssentialAccordion("0");
    setGoalsAccordion("0");

    setEssentialSupportTypeState([]);
    setEssentialSupportNumber([]);
    setSelectedSupportType(null);
    setEssentialNumber(null);
    setFrequencyOptions([]);
    setActivities([]);

    // Same as resetForm — these live outside `form`, so clear them explicitly.
    setIncidentNoteData({ ...INITIAL_INCIDENT_NOTE });

    setBillingServices("");
    setSelectedPatient(null);
    if (NOTETYPE === Billing_Services_Note) {
      setBillingServices("");
      setSelectedPatient(null);
    }
    setMultiClients([]);
    setMultiStaff([]);
    close();
    // setPatientsId(null);
  };

  const handleNoteTypeChange = (newNoteType) => {
    form.reset();
    setErrors({});
    setEssentialSupportErrors([]);
    setInterventionPlanToggle("no");
    setSupportPlan("no");
    setEssentialSupportToggle("no");
    setDescriptions([{ id: 1, ...initialDescription }]);
    setEssentialSupport([{ id: 1, ...initialEssentialSupport }]);
    setGoals([{ id: 1, ...initialGoals }]);
    setGoalItems([[{ id: Date.now(), objectives: "", Intervention_Instruction: "" }]]);
    setOpenedAccordion("0");
    setEssentialAccordion("0");
    setGoalsAccordion("0");
    setMessages([]);
    setOutcomeNotes([]);
    setEssentialNotes([]);
    setAiAssistantCardOpened(false);
    setBehaviorCardOpened(false);
    setResponseCardOpened(false);
    setPlanCardOpened(false);
    setAiActionsOpen(false);
    setComparePanel(COMPARE_PANEL_RESET);
    setServiceNumber(null);
    setEssentialSupportTypeState([]);
    setEssentialSupportNumber([]);
    setSelectedSupportType(null);
    setEssentialNumber(null);
    setFrequencyOptions([]);
    setActivities([]);
    setSelectedGroupClients([]);
    setMultiClients([]);
    setMultiStaff([]);
    handleRecordingCancel();
    setNoteType(newNoteType);
    if (selectedPatient?.id) form.setFieldValue("Client Name", selectedPatient.id);
    // Facility and Shift start over with the new note type — every care plan,
    // essential support and goal lookup above was just cleared and they are all
    // keyed off the facility, so carrying the old one over leaves the form
    // showing a facility whose data is no longer loaded. Written as an explicit
    // null rather than left to form.reset(): reset() drops the key entirely,
    // which hands Mantine's Select `value === undefined` and it falls back to
    // uncontrolled mode, still displaying the previous selection. Only written
    // for fields the new note type actually declares, so a note type without a
    // Facility/Shift field doesn't gain a stray null key in its saved fields.
    setSelectedFacility(null);
    setFacilityDetials(null);
    const nextFields = new Set(
      (newNoteType?.custom || []).map((c) => c?.field)
    );
    if (nextFields.has("Facility Name")) form.setFieldValue("Facility Name", null);
    if (nextFields.has("Shift")) form.setFieldValue("Shift", null);
  };

  // OD-1164: reads the CURRENT value of the field an AI suggestion was accepted into,
  // so the exemplar capture reflects what the user actually edited and saved — not the
  // suggestion as it looked at accept time.
  const readAiFieldLiveValue = (context, entry) => {
    if (context === "main") return form.values?.["Description"] ?? "";
    if (context?.startsWith("description_")) {
      const idx = Number(context.split("_")[1]);
      return descriptions?.[idx]?.description ?? "";
    }
    if (context?.startsWith("essential_")) {
      const idx = Number(context.split("_")[1]);
      return essentialSupport?.[idx]?.description ?? "";
    }
    if (context === "behavior" || context === "response" || context === "plan") {
      const idx = entry?.goal_index ?? 0;
      return goals?.[idx]?.[`${context}Description`] ?? "";
    }
    return entry?.final_text ?? "";
  };

  // Builds the aiMetadata block for the note payload from fields the user accepted an AI
  // suggestion into, and returns a snapshot of those fields. Captured up front because the
  // create/edit callbacks (which call resetForm, clearing aiTouchedFieldsRef) can run
  // before the save promise resolves — reading the live ref afterward would see it empty.
  const attachAiMetadata = (obj) => {
    const touched = aiTouchedFieldsRef.current;
    const contexts = Object.keys(touched);
    if (!contexts.length) return { payload: obj, aiFieldsSnapshot: null };

    const aiFields = contexts.map((context) => {
      const entry = touched[context];
      const aiText = entry.ai_text ?? entry.final_text ?? "";
      const liveText = readAiFieldLiveValue(context, entry);
      const finalText = typeof liveText === "string" && liveText.trim() ? liveText : entry.final_text ?? "";
      return {
        context,
        action: entry.action,
        chosen_model: entry.chosen_model,
        ai_text: aiText,
        final_text: finalText,
        was_edited: aiText.trim() !== finalText.trim(),
      };
    });
    return { payload: { ...obj, aiMetadata: { fields: aiFields } }, aiFieldsSnapshot: aiFields };
  };

  // Fires the "final note saved" Pendo/analytics events for a previously captured snapshot,
  // tagged with the saved note's real id so the data can be associated back to that note.
  const trackAiNoteFinalization = (aiFieldsSnapshot, noteId) => {
    if (!aiFieldsSnapshot?.length) return;

    aiFieldsSnapshot.forEach(({ context, action, chosen_model, final_text }) => {
      trackAiNoteFinalized({
        note_id: noteId ?? null,
        note_type: noteType?.name || "",
        action,
        chosen_model,
        field_context: context,
      });
      logAiSuggestionEvent({
        note_id: noteId ?? null,
        note_type: noteType?.name || "",
        action,
        chosen_model,
        field_context: context,
        final_note_content: final_text,
        event: "note_finalized",
      });
    });

    // OD-1164: send the AI original + user-edited saved text pairs to the exemplar
    // store as candidates. Client ids are included so the backend can scrub the
    // client's name along with generic PHI before anything is persisted.
    const isValidId = (id) => typeof id === "string" && /^[a-f\d]{24}$/i.test(id);
    const clientIds = (isGroupNote ? selectedGroupClients || [] : [selectedPatient?.id || userId]).filter(isValidId);
    const examples = aiFieldsSnapshot
      .filter((f) => typeof f.final_text === "string" && f.final_text.trim())
      .map(({ context, action, chosen_model, ai_text, final_text, was_edited }) => ({
        field_context: context,
        action,
        chosen_model,
        ai_text: ai_text ?? "",
        final_text,
        was_edited: !!was_edited,
      }));
    if (examples.length) {
      captureExemplarNotes({
        note_id: isValidId(noteId) ? noteId : null,
        note_type: noteType?.name || "",
        client_ids: clientIds,
        examples,
      });
    }
  };

  // Save Note
  const onSave = async (values) => {
    const NOTETYPE = noteType?.id;

    if (noteType?.name === "Incident Report") {
      if (!values?.["Description"]?.trim()) {
        swal({
          title: "Description of Incident is required",
          text: "Please describe what happened.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (!values?.["Immediate Actions Taken"]?.trim()) {
        swal({
          title: "Immediate Actions Taken is required",
          text: "Please describe the immediate actions taken.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (!incidentNoteData.type) {
        swal({
          title: "Incident Type is required",
          text: "Please select the incident type.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (incidentNoteData.type === "Other" && !incidentNoteData.otherType?.trim()) {
        swal({
          title: "Specify Incident Type is required",
          text: "Please describe the incident type.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (!incidentNoteData.severity) {
        swal({
          title: "Severity is required",
          text: "Please select the severity of this incident.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (!incidentNoteData.clientsInvolved?.length) {
        swal({
          title: "Client(s) Involved is required",
          text: "Please select at least one client involved in this incident.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (!incidentNoteData.staffInvolved?.length) {
        swal({
          title: "Staff & Witnesses Present is required",
          text: "Please select at least one staff member for this incident.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (
        incidentNoteData.injurySustained &&
        typeof incidentNoteData.injurySustained === "object" &&
        !incidentNoteData.injurySustained.medicalAttention
      ) {
        swal({
          title: "Medical Attention is required",
          text: "Please select the level of medical attention provided.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (
        incidentNoteData.emergencyServicesCalled &&
        typeof incidentNoteData.emergencyServicesCalled === "object" &&
        incidentNoteData.emergencyServicesCalled.clientTransported &&
        !incidentNoteData.emergencyServicesCalled.receivingHospital?.trim()
      ) {
        swal({
          title: "Receiving Hospital / Facility is required",
          text: "Please enter the receiving hospital or facility.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (
        isReportableSeverity &&
        !incidentNoteData.notifiedContacts?.["agency-state-reporting"]
      ) {
        swal({
          title: "Agency / State Reporting Line is required",
          text: `A ${incidentNoteData.severity} incident is reportable — confirm the Agency / State Reporting Line was notified under Notifications Made.`,
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
    }

    // Multi-select note types must have at least one client and one staff.
    if (isMultiSelectNoteType) {
      if (useMultiClient && !multiClients?.length) {
        swal({
          title: `${noteType_Client || "Client Name"} is required`,
          text: "Please select at least one client.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
      if (useMultiStaff && !multiStaff?.length) {
        swal({
          title: "Staff Name is required",
          text: "Please select at least one staff member.",
          icon: "warning",
          dangerMode: true,
        });
        return;
      }
    }

    let obj = {
      noteId: NOTETYPE,
      fields: { ...values, noteId: NOTETYPE },
      supportPlan,
      userId,
    };
    // Studio templates store the client/facility under the author's own field
    // labels (e.g. "Client(s)"), but the notes list associates & filters every
    // note by fields["Client Name"] / fields["Facility Name"]. Mirror the chosen
    // client/facility into those canonical keys so the note shows up in the list.
    if (isStudioTemplate) {
      const clientIds = multiClients?.length
        ? multiClients
        : selectedPatient?.id || userId
        ? [selectedPatient?.id || userId]
        : [];
      if (clientIds.length && !obj.fields["Client Name"]) obj.fields["Client Name"] = clientIds;
      if (selectedFacility && !obj.fields["Facility Name"]) obj.fields["Facility Name"] = selectedFacility;
    }
    if (NOTETYPE === Billing_Services_Note) {
      obj.fields["Facility Name"] = selectedPatient?.facilityId?.[0]?.id;
    }
    if (isGroupNote) {
      obj.fields.clientIds = selectedGroupClients;
    }
    if (noteType?.name === "Incident Report") {
      // Incident Report stores multiple clients/staff under the SAME field
      // names every other note type uses ("Client Name" / "Staff Name") —
      // the value is an array here instead of a single id. These two ARE
      // driven by noteType.custom / ReturnFormItem, so they stay in `fields`
      // like every other note type.
      obj.fields["Client Name"] = incidentNoteData.clientsInvolved;
      obj.fields["Staff Name"] = incidentNoteData.staffInvolved;

      // Everything else below is hand-built UI (not from noteType.custom /
      // ReturnFormItem) — same pattern as essentialSupport/goals/descriptions,
      // stored as a separate top-level object instead of inside `fields`.
      obj.incidentDetails = {
        incidentType: incidentNoteData.type,
        otherType: incidentNoteData.type === "Other" ? incidentNoteData.otherType : "",
        severity: incidentNoteData.severity,
        injurySustained: incidentNoteData.injurySustained,
        emergencyServicesCalled: incidentNoteData.emergencyServicesCalled,
        restraintUsed: incidentNoteData.restraintUsed,
        notifiedContacts: incidentNoteData.notifiedContacts,
        notificationNotes: incidentNoteData.notificationNotes,
        followUpAction: incidentNoteData.followUpAction,
        reporterName: incidentNoteData.reporterName,
        reporterSignature: incidentNoteData.reporterSignature,
        reporterSignatureImage: incidentNoteData.reporterSignatureImage,
        reporterSignedAt: incidentNoteData.reporterSignedAt,
        reviewedBy: incidentNoteData.reviewedBy,
        reviewedAt: incidentNoteData.reviewedAt,
      };
    }
    // Multi-select note types store client/staff under the same canonical keys,
    // always as an array — even a single selection saves as a 1-item array now,
    // so a legacy scalar note moves onto this same structure the moment it's
    // touched again (see the load effect that wraps a saved scalar into [value]).
    if (isMultiSelectNoteType) {
      if (useMultiClient) obj.fields["Client Name"] = multiClients;
      if (useMultiStaff) obj.fields["Staff Name"] = multiStaff;
    }

    // The built-in Essential Support Note, plus any studio template whose care
    // plan field includes Essential Supports — both document the same
    // `essentialSupport` rows, so both validate and persist them.
    const documentsEssential = noteType?.id === Essential_Support_Note || careplanHasEssential;

    const isValid = validateEssentialSupport();
    if (!isValid && documentsEssential && essentialSupportToggle === "yes") return;

    // With more than one client on the note the per-client care-plan sections
    // don't apply, so no support/essential/intervention plan is attached.
    if (documentsEssential && !isMultiClientSelected) {
      if (essentialSupport !== undefined) {
        obj.essentialSupport = essentialSupport;
      }
      if (essentialSupportToggle !== undefined) {
        obj.essentialSupportToggle = essentialSupportToggle;
      }
    } else if (isIntervention_Progress_Note && !isMultiClientSelected) {
      if (goals !== undefined) {
        obj.goals = goals;
      }
      if (interventionPlanToggle !== undefined) {
        obj.interventionPlanToggle = interventionPlanToggle;
      }
    }
    const isProgressNote = noteType?.name === "Progress Note";
    if (
      !isMultiClientSelected &&
      (isProgressNote ||
        noteType?.custom?.find((_c) => _c?.field === "Support Plan") ||
        (isCareplanTemplate && supportPlan === "yes"))
    )
      obj["descriptions"] = descriptions;

    // if (isProgressNote) obj["descriptions"] = descriptions;

    // if (isProgressNote && supportPlan === "yes")
    //   obj.fields["Description"] = descriptions
    //     ?.map((rest) => rest?.details?.outcomeTitle)
    //     ?.join(", ");

    if (edit && !saveAsNew) {
      let fields = { ...values };
      delete fields["noteId"];
      delete fields["noteType"];
      const obj = {
        ...values,
        id: data?.data?.id,
        fields,
        supportPlan,
        userId,
        essentialSupport,
        essentialSupportToggle,
        goals,
        interventionPlanToggle,
        noteDate: values.Date,
      };
      if (isStudioTemplate) {
        const clientIds = multiClients?.length
          ? multiClients
          : selectedPatient?.id || userId
          ? [selectedPatient?.id || userId]
          : [];
        if (clientIds.length && !obj.fields["Client Name"]) obj.fields["Client Name"] = clientIds;
        if (selectedFacility && !obj.fields["Facility Name"]) obj.fields["Facility Name"] = selectedFacility;
      }
      if (isGroupNote) obj.fields.clientIds = selectedGroupClients;
      if (noteType?.name === "Incident Report") {
        // Same field names as every other note type ("Client Name" / "Staff
        // Name"), but the value is an array for Incident Report.
        obj.fields["Client Name"] = incidentNoteData.clientsInvolved;
        obj.fields["Staff Name"] = incidentNoteData.staffInvolved;

        // Hand-built UI fields (not from noteType.custom / ReturnFormItem) —
        // stored as a separate top-level object, same pattern as
        // essentialSupport/goals/descriptions.
        obj.incidentDetails = {
          incidentType: incidentNoteData.type,
          otherType: incidentNoteData.type === "Other" ? incidentNoteData.otherType : "",
          severity: incidentNoteData.severity,
          injurySustained: incidentNoteData.injurySustained,
          emergencyServicesCalled: incidentNoteData.emergencyServicesCalled,
          restraintUsed: incidentNoteData.restraintUsed,
          notifiedContacts: incidentNoteData.notifiedContacts,
          notificationNotes: incidentNoteData.notificationNotes,
          followUpAction: incidentNoteData.followUpAction,
          reporterName: incidentNoteData.reporterName,
          reporterSignature: incidentNoteData.reporterSignature,
          reporterSignatureImage: incidentNoteData.reporterSignatureImage,
          reporterSignedAt: incidentNoteData.reporterSignedAt,
            reviewedBy: incidentNoteData.reviewedBy,
          reviewedAt: incidentNoteData.reviewedAt,
        };
      }
      // Multi-select note types store client/staff under the same canonical
      // keys, always as an array — even a single selection saves as a 1-item
      // array, so an edited legacy scalar note moves onto this structure too.
      if (isMultiSelectNoteType) {
        if (useMultiClient) obj.fields["Client Name"] = multiClients;
        if (useMultiStaff) obj.fields["Staff Name"] = multiStaff;
      }
      // With more than one client on the note, no care plan applies — strip any
      // plan data so a multi-client note never carries one.
      if (isMultiClientSelected) {
        obj.supportPlan = "no";
        obj.essentialSupportToggle = "no";
        obj.interventionPlanToggle = "no";
        delete obj.essentialSupport;
        delete obj.goals;
        delete obj.descriptions;
      }
      if (
        !isMultiClientSelected &&
        (isProgressNote ||
          noteType?.custom?.find((_c) => _c?.field === "Support Plan") ||
          (isCareplanTemplate && supportPlan === "yes"))
      )
        obj["descriptions"] = descriptions;
      // if (isProgressNote) obj["descriptions"] = descriptions;

      if (isProgressNote && supportPlan === "yes" && !isMultiClientSelected)
        obj.fields["Description"] = descriptions
          ?.map((rest) => rest?.details?.outcomeTitle)
          ?.join(", ");
      const validations = validateFields();
      if (validations !== true && supportPlan === "yes") {
        validateFields();
      } else {
        const editNoteId = obj.id;
        const { payload: editPayload, aiFieldsSnapshot: editAiFields } = attachAiMetadata(obj);
        editNote(editPayload, false, resetForm, sortField, sortOrder, setEditModal, currentPage, dateRange, activeFilters);
        trackAiNoteFinalization(editAiFields, editNoteId);
        resetForm();
        setErrors("");
      }
    } else {
      const validations = validateFields();
      let isBillingService = profile?.admin?.billingAgree;
      if (validations !== true && supportPlan === "yes") {
        validateFields();
      } else {
        // Determine before saving whether selectedPatient should be preserved
        // (when another note in the flow — Essential or Billing Services — will follow)
        let ifEssential = false;
        if (NOTETYPE === PROGRESS_Note) {
          ifEssential = allPlans?.some((item) => {
            return (
              item?.isActive &&
              item?.planType?.name?.includes("Essential Supports") &&
              item?.essentialSupport?.length
            );
          });
        }
        const willOpenBilling =
          (NOTETYPE === PROGRESS_Note && !ifEssential && isBillingService) ||
          (NOTETYPE === Essential_Support_Note && progressNoteState && isBillingService);
        const preservePatient = ifEssential || willOpenBilling;

        // Carry this note's Client(s) / Staff / Facility into the follow-up note
        // (Essential Support / Billing Services) that the modal is about to open,
        // so it appears pre-filled with the same selection. Captured BEFORE
        // resetForm() below clears these. Only set when a follow-up will open —
        // a plain new note never inherits a previous note's selection.
        carryOverRef.current = preservePatient
          ? {
              clients: multiClients?.length
                ? [...multiClients]
                : selectedPatient?.id
                ? [selectedPatient.id]
                : [],
              staff: multiStaff?.length ? [...multiStaff] : [],
              facility: form.values?.["Facility Name"] || selectedFacility || null,
            }
          : null;

        const refreshUserId = (isClientView && !isGroupNote) ? (selectedPatient?.id || userId) : null;
        const { payload: createPayload, aiFieldsSnapshot: createAiFields } = attachAiMetadata(obj);
        const createResult = await createNote(createPayload, () => resetForm(preservePatient), refreshUserId, currentPage, dateRange, sortField, sortOrder, activeFilters);
        if (createResult !== false) {
          trackAiNoteFinalization(createAiFields, createResult?.id ?? createResult?._id);
        }
        resetForm(preservePatient);
        if (createResult !== false) {
          const cId = isGroupNote ? null : (selectedPatient?.id || userId);
          if (NOTETYPE && cId) {
            deleteNoteDraft(NOTETYPE, cId);
            getAllDrafts(dispatch, isClientView ? cId : null);
          }
        }
        // A follow-up prompt ("Do you want to add a new essential support /
        // billing note?") takes the place of the plain "Note Successfully
        // Saved" confirmation — the two must never stack, so only show the
        // confirmation when no follow-up prompt opens.
        let willOpenFollowUp = false;
        if (NOTETYPE === PROGRESS_Note) {
          if (ifEssential) {
            setOpenModal(true);
            setProgressNoteState("progress");
            willOpenFollowUp = true;
          } else if (isBillingService) {
            setOpenModal(true);
            setBillingServices("billing");
            willOpenFollowUp = true;
          } else {
            setOpenModal(false);
          }
        } else if (NOTETYPE === Essential_Support_Note) {
          if (progressNoteState && isBillingService) {
            // setOpenModal(true);
            setBillingServices("billing");
            // A billing follow-up is opening — flag it so the plain "Note
            // Successfully Saved" confirmation below is suppressed and the two
            // don't stack (same guard the Progress Note branch above applies).
            willOpenFollowUp = true;
          } else {
            setOpenModal(false);
          }
        } else {
          setOpenModal(false);
        }
        setPendingModal(!willOpenFollowUp);
        setErrors("");
      }
    }
  };

  function objectToString(obj) {
    let result = Object.entries(obj)
      .map(
        ([key, value]) =>
          `${key.charAt(0).toUpperCase() + key.slice(1)}: ${`"${
            value || "-"
          }"`}`
      )
      .join("\n");

    return result;
  }

  const arrayToString = (array) => {
    if (array?.length === 0 || array === undefined) return "";
    return array
      ?.filter((v) => v)
      ?.map((v, i) => `${i + 1}) ${v}`)
      .join("\n \n");
  };

  function filterNonNullFields(obj) {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && value !== undefined && value !== "") {
        result[key] = value;
      }
    }
    return result;
  }

  useEffect(() => {
    if (selectedPatient && isBilling_Services) {
      form.setFieldValue("Provider", selectedPatient?.providerId[0]?.id);
    }
  }, [selectedPatient]);

  useEffect(() => {
    if (essentialSupport) {
      let data = getEssentialSupport?.[0];

      if (view === true || edit === true) {
        handleEssentialTypeChangeForNoteView(
          data?.essentialSupportType,
          data?.howOftenEssentialSupport
        );
      }
    }
  }, [view, edit, essentialSupportToggle]);

  // useEffect(() => {
  // if(essentialSupport?.length > 0 && view === true) {
  //   let data = getEssentialSupport?.[0]
  //   handleEssentialTypeChangeForNoteView(data?.essentialSupportType, data?.howOftenEssentialSupport)
  // }
  // }, [view]);

  // useEffect(() => {
  // if(essentialSupport?.length > 0 && edit === true){
  //   let data = getEssentialSupport?.[0]
  //   handleEssentialTypeChangeForNoteView(data?.essentialSupportType, data?.howOftenEssentialSupport)
  // }
  // }, [edit]);

  // Handle Client Change
  const handlePatientChange = (patientId, name) => {
    if (name === "Service Type") {
      let selectedService = selectedPatient?.form1?.services?.filter(
        (val) => val?.servicenumber === patientId
      )?.[0];
      setServiceNumber(selectedService);
    }
    !edit && !view && !draftRestoredRef.current && setDescriptions([{ id: 1, ...initialDescription }]);
    setAiActionsOpen(false);
    setComparePanel(COMPARE_PANEL_RESET);

    form.setFieldValue(name, patientId);
    let facilityExists = noteType?.custom?.find(
      (value) => value?.fieldType?.name?.toLowerCase() === "facilities"
    );
    let patient = allPatients?.find((value) => value?.id === patientId);

    let pcpInfo = {
      "PCP Name": patient?.form2?.["pcpName"],
      "PCP Email": patient?.form2?.["pcpEmail"],
      "PCP Phone Number": patient?.form2?.["pcpPhoneNumber"],
      "PCP Fax Number": patient?.form2?.["pcpFaxNumber"],
    };

    let insuranceDetails = {
      "Insurance Company Name": patient?.form2?.["insuranceCompanyName"],
      "Member Name": patient?.form2?.["memberName"],
      "Member Number": patient?.form2?.["memberNumber"],
      "Prescription Group": patient?.form2?.["prescriptionGroup"],
      "Group Number": patient?.form2?.["groupNumber"],
      "Effective Date": formatDate(patient?.form2?.["effectiveDate"]),
    };
    (edit || view) &&
      form.setFieldValue(
        facilityExists?.field,
        data?.data?.fields?.["Facility Name"]
      );

    form.setFieldValue("All PCP Info", objectToString(pcpInfo));
    form.setFieldValue(
      "Known Allergies",
      arrayToString(patient?.form1?.knownAllergies)
    );
    form.setFieldValue(
      "Dietary Restrictions",
      arrayToString(patient?.form1?.dietaries)
    );

    form.setFieldValue(
      "Known Illnes",
      arrayToString(
        patient?.form1?.knownIllness
          ? patient?.form1?.knownIllness?.map(
              (v) =>
                `${v?.illness || ""} - Contagious: "${v?.contagious || ""}"`
            )
          : []
      ) || "-"
    );
    form.setFieldValue("Insurance Details", objectToString(insuranceDetails));
    form.setFieldValue("Known Wounds", patient?.form1?.woundsInformation);

    form.setFieldValue("Medication Records", "");
    form.setFieldValue("Support Plan", "");
    setMedicationOptions(
      Array.isArray(patient?.form3)
        ? patient?.form3?.map((value) => {
            let { isPrescribed, id, ...rest } = value;
            return {
              label: value?.medicationName,
              value: objectToString({
                ...filterNonNullFields(rest),
                Prescribed: isPrescribed ? "Yes" : "No",
              }),
            };
          })
        : []
    );

    setSupportOptions(
      allPlans
        ?.find((v) => v?.client?.id === patient?.id)
        ?.outcomes?.map(({ id, ...rest }) => ({
          value: objectToString({
            ...filterNonNullFields(rest),
          }),
          label: rest.outcomeTitle,
        })) || []
    );
    if (patient) setSelectedPatient(patient);
    if (patient) setFormErrors((prev) => ({ ...prev, name: undefined }));

    // Clear any draft banner from the previous client immediately.
    // The combined useEffect below will fetch the draft for the new client.
    if (!edit && !view) setPendingDraft(null);
  };

  // Shared multi-client handler — used by both the built-in MultiSelect below
  // and the Note-Type Studio's client field (passed in as `onClientChange`).
  // Keeps `selectedPatient` / all the per-patient derived fields (facility,
  // provider, medications, care plans) in sync with the FIRST selected client
  // via `handlePatientChange`, same as before a client could be multi-valued.
  const onClientsChange = (ids, name) => {
    const allIds =
      allPatients
        ?.filter((_p) => _p?.active === true && _p?.deleted === false)
        ?.map((p) => p?.id) || [];
    const list = Array.isArray(ids) ? ids : ids ? [ids] : [];
    const resolved = list.includes("ALL") ? allIds : list;
    setMultiClients(resolved);
    const first = resolved?.[0];
    if (first) {
      handlePatientChange(first, name);
    } else {
      setSelectedPatient(null);
    }
    // handlePatientChange (above) writes the single first id into the form
    // field for its own bookkeeping — re-set it to the full selection so
    // Studio templates, which read this field's raw value (not `multiClients`
    // state) when building their save payload, persist every selected client.
    form.setFieldValue(name, resolved.length ? resolved : null);
  };

  // Handle Facility Change
  const handleFacilityChange = (value, name) => {
    form.setFieldValue(name, value);
    setSelectedFacility(value);
    setFormErrors((prev) => ({ ...prev, facility: undefined }));
    if (isGroupNoteCreating) {
      const facilityClients = allPatients
        ?.filter(
          (_p) =>
            _p?.active === true &&
            _p?.deleted === false &&
            _p?.facilityId?.some((f) => f?.id === value)
        )
        ?.map((p) => p?.id) || [];
      setSelectedGroupClients(facilityClients);
    }
    if (noteType?.name === "Incident Report" && !userId) {
      setIncidentNoteData((prev) => ({ ...prev, clientsInvolved: [] }));
    }
    setAiActionsOpen(false);
    setComparePanel(COMPARE_PANEL_RESET);
    let facilityDetails = facilities?.facilities?.find(
      (fac) => String(fac?.id) === value
    );
    setFacilityDetials(facilityDetails);
  };

  useEffect(() => {
    if (!opened) {
      setMedicationOptions([]);
      setSupportOptions([]);
      setDescriptions([{ id: 1, ...initialDescription }]);
      setSupportPlan("no");
      setEssentialSupportToggle("no");
      setInterventionPlanToggle("no");
      setAiActionsOpen(false);
      setComparePanel(COMPARE_PANEL_RESET);
      setSelectedGroupClients([]);
      setSelectedFacility(null);
      setIncidentNoteData({ ...INITIAL_INCIDENT_NOTE });
      // The drawer instance stays mounted between opens, so per-note state must
      // be cleared on close or the next NEW note opens pre-filled with the
      // previous note's client, note type and care-plan content. (The
      // multi-selects are cleared in a separate close effect above; these are
      // the single-client / note-type / care-plan pieces it doesn't cover.)
      setSelectedPatient(null);
      setNoteType(null);
      setStaffDetials();
      setFacilityDetials(null);
      setEssentialSupport([{ id: 1, ...initialEssentialSupport }]);
      setGoals([{ id: 1, ...initialGoals }]);
      setGoalItems([
        [{ id: Date.now(), objectives: "", Intervention_Instruction: "" }],
      ]);
      setOutcomeNotes([]);
      setEssentialNotes([]);
      setSelectedSupportType(null);
      setActivities([]);
      setEssentialSupportTypeState([]);
      setEssentialSupportNumber([]);
      setEssentialNumber(null);
      setFrequencyOptions([]);
      setServiceNumber(null);
      getAllFacilitiesModal("all");

      form.reset();
    }
    if (noteT && edit) {
      getAllFacilitiesModal("all");
      form?.setFieldValue(
        "noteType",
        allNotesTypes?.ntypes?.find((value) => value?.id === noteT?.id)?.name
      );

      let _noteType = {
        ...allNotesTypes?.ntypes?.find((value) => value?.id === noteT?.id),
      };

      let customs = Array.isArray(_noteType?.custom)
        ? [..._noteType.custom]
        : [];

      let indexOfPatient = customs.findIndex(
        (v) => v?.field === noteType_Client
      );
      let patientClone = customs[indexOfPatient];
      if (indexOfPatient !== -1) {
        customs.splice(indexOfPatient, 1);
        customs.unshift(patientClone);
        _noteType.custom = customs;
      }
      setNoteType(_noteType);
      setSelectedFacility(data?.data?.fields?.["Facility Name"]);
      // This block only runs in edit mode (see the `noteT && edit` guard above).
      // The backend splits a multi-client Group Note submission into one
      // separate Note per client at create time, so an existing saved Group
      // Note document is always single-client ("Client Name") — `clientIds`
      // is a create-time-only field and must not be read back here, or
      // editing one client's note would repopulate the picker with the
      // original multi-client roster and re-split into other clients' notes
      // on save.
      if (noteT?.id === Group_Note && data?.data?.fields?.["Client Name"]) {
        const cn = data.data.fields["Client Name"];
        setSelectedGroupClients(Array.isArray(cn) ? cn : [cn]);
      }
      if (_noteType?.name === "Incident Report") {
        const incidentDetails = data?.data?.incidentDetails || {};
        setIncidentNoteData((prev) => ({
          ...prev,
          type: incidentDetails.incidentType ?? prev.type,
          otherType: incidentDetails.otherType ?? prev.otherType,
          severity: incidentDetails.severity ?? prev.severity,
          // Older Incident Reports (saved before multi-client/staff support)
          // stored a single id here instead of an array — wrap it so it
          // still shows up correctly in the multi-select UI.
          clientsInvolved: Array.isArray(data?.data?.fields?.["Client Name"])
            ? data.data.fields["Client Name"]
            : data?.data?.fields?.["Client Name"] || data?.data?.fields?.["Patient Name"]
            ? [data.data.fields["Client Name"] || data.data.fields["Patient Name"]]
            : [],
          staffInvolved: Array.isArray(data?.data?.fields?.["Staff Name"])
            ? data.data.fields["Staff Name"]
            : data?.data?.fields?.["Staff Name"]
            ? [data.data.fields["Staff Name"]]
            : [],
          injurySustained: incidentDetails.injurySustained ?? prev.injurySustained,
          emergencyServicesCalled: incidentDetails.emergencyServicesCalled ?? prev.emergencyServicesCalled,
          restraintUsed: incidentDetails.restraintUsed ?? prev.restraintUsed,
          notifiedContacts: incidentDetails.notifiedContacts || {},
          notificationNotes: incidentDetails.notificationNotes || "",
          followUpAction: incidentDetails.followUpAction || "",
          reporterName: incidentDetails.reporterName || "",
          reporterSignature: incidentDetails.reporterSignature || "",
          reporterSignatureImage: incidentDetails.reporterSignatureImage || "",
          reporterSignedAt: incidentDetails.reporterSignedAt || null,
          reviewedBy: incidentDetails.reviewedBy || "",
          reviewedAt: incidentDetails.reviewedAt || null,
        }));
      }
    }
    if (opened) {
      getAllPatientsModal("all");
      getAllFacilitiesModal("active");
    }
  }, [noteT, opened]);

  // Quick Notes data — fetched once per drawer open here (not inside
  // QuickNotesPicker itself, which is mounted up to 7x in this form; fetching
  // there would fire the same request once per mounted instance).
  useEffect(() => {
    if (opened) {
      getActivityLibrary();
      getDefaultPrompting();
    }
  }, [opened]);

  useEffect(() => {
    if (edit) {
      setAiAssistantCardOpened(false);
      console.log('data?.data?.fields',data?.data?.fields);
      
      setFacilityDetials(data?.data?.fields?.["Facility Name"]);
      if (data?.data?.fields)
        Object.entries(data?.data?.fields)?.map((value) => {
          if (
            `${value[1]}`.split(":")?.length === 3 &&
            `${value[1]}`.split("-")?.length === 3
          ) {
            let date = new Date(value[1]);
            form?.setFieldValue(value[0], date);
          } else {
            form?.setFieldValue(value[0], value[1]);
          }
        });

      // "Use a Previous Note" copies everything above, but Date/Time should
      // land on today/now, not the source note's original date/time.
      if (saveAsNew) {
        form?.setFieldValue("Date", new Date());
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent
        );
        if (isSafari) {
          const hours = new Date().getHours(),
            minutes = new Date().getMinutes();
          form?.setFieldValue("Time", `${hours}:${minutes}`);
        } else {
          form?.setFieldValue(
            "Time",
            new Date().toLocaleTimeString("en-US", { hour12: false })
          );
        }
      }
    }

    if (NOTETYPE && opened) {
      form?.setFieldValue(
        "noteType",
        allNotesTypes?.ntypes?.find((value) => value?.id === NOTETYPE)?.name
      );

      let _noteType = {
        ...allNotesTypes?.ntypes?.find((value) => value?.id === NOTETYPE),
      };
      // let customs = _noteType ? [..._noteType?.custom] : [];
      let customs = Array.isArray(_noteType?.custom)
        ? [..._noteType.custom]
        : [];

      let indexOfPatient = customs?.findIndex(
        (v) => v?.field === noteType_Client
      );
      let patientClone = customs[indexOfPatient];
      if (indexOfPatient !== -1) {
        customs.splice(indexOfPatient, 1);
        customs.unshift(patientClone);
        _noteType.custom = customs;
      }
      setNoteType(_noteType);
    }
  }, [NOTETYPE, edit, opened]);

  useEffect(() => {
    if (
      opened &&
      noteType?.name === "Incident Report" &&
      !view &&
      !edit &&
      profile?.user?.id &&
      !incidentNoteData.staffInvolved?.length
    ) {
      setIncidentNoteData((prev) => ({
        ...prev,
        staffInvolved: [profile.user.id],
      }));
    }
  }, [opened, noteType?.name, view, edit, profile?.user?.id]);

  useEffect(() => {
    if (opened && noteType?.name !== "Incident Report") {
      if (profile?.user?.type === "staff" && !view && !edit) {
        form.setFieldValue("Staff Name", profile?.user?.id);
      } else if (profile?.user?.type !== "staff" && !view && !edit) {
        form.setFieldValue("Staff Name", profile?.user?.id);
      } else {
        form.setFieldValue("Staff Name", data?.data?.fields["Staff Name"]);
      }
      if (selectedPatient) {
        form.setFieldValue("Client Name", selectedPatient?.id);
      } else if (data?.data?.fields["Client Name"]) {
        form.setFieldValue("Client Name", data?.data?.fields["Client Name"]);
      } else if (data?.data?.fields["Patient Name"]) {
        form.setFieldValue("Client Name", data?.data?.fields["Patient Name"]);
      }
      if (selectedPatient && isBilling_Services) {
        form.setFieldValue("Provider", selectedPatient?.providerId[0]?.id);
      }
      if (selectedPatient && isBilling_Services) {
        form.setFieldValue(
          "Facility Name",
          selectedPatient?.facilityId?.map((item) => item?.id)
        );
      }
      let patientType = noteType?.custom?.find(
        (v) => v?.fieldType?.name === noteType_Client_Options
      );
      // Multi-select note types (e.g. Essential Support Note) store "Client
      // Name" as an ARRAY. The single-id comparisons below never match an
      // array, so `patient` stayed undefined and the patient-dependent load
      // block (medications, support options, AND essentialSupport/goals with
      // their toggles) was skipped — which is why saved essential supports and
      // their fields didn't show when editing. Normalize an array value to its
      // first client id before matching.
      const firstId = (v) => (Array.isArray(v) ? v?.[0] : v);
      const clientNameId = firstId(data?.data?.fields?.["Client Name"]);
      const patientFieldId = firstId(data?.data?.fields?.[patientType?.field]);
      // Same precedence the nested ternary here used to express (clientNameId >
      // "Patient Name" > the note type's own client field), minus the bug it
      // carried: the clientNameId branch was only reachable when the note ALSO
      // had a legacy "Patient Name". A studio template has neither "Patient
      // Name" nor a `fieldType`-bearing custom entry for `patientType` to match,
      // so `patientFieldId` was `fields[undefined]` and `patient` came back
      // undefined — skipping this whole block, which is what loads the saved
      // supportPlan / essentialSupportToggle and their rows. That's why a
      // template note's care-plan documentation didn't render in the view drawer.
      const notePatientId =
        clientNameId || firstId(data?.data?.fields?.["Patient Name"]) || patientFieldId;
      let patient = allPatients?.find((value) => value?.id === notePatientId);
      if (patient) {
        form.setFieldValue(noteType_Client, patient.id);
        setMedicationOptions(
          Array.isArray(patient?.form3)
            ? patient?.form3?.map((value) => {
                let { isPrescribed, id, ...rest } = value;
                return {
                  label: value?.medicationName,
                  value: objectToString({
                    ...filterNonNullFields(rest),
                    Prescribed: isPrescribed ? "Yes" : "No",
                  }),
                };
              })
            : []
        );

        setSupportOptions(
          allPlans
            ?.find((v) => v?.client?.id === patient?.id)
            ?.outcomes?.map(({ id, ...rest }) => ({
              value: objectToString({
                ...filterNonNullFields(rest),
              }),
              label: rest.outcomeTitle,
            })) || []
        );
        setSelectedPatient(patient);
        // Guarded like essentialSupport/goals below: a note saved without a care
        // plan has no descriptions, and assigning that straight through left
        // `descriptions` undefined, which broke the Use Support Plan toggle.
        if (data?.data?.descriptions?.length) {
          setDescriptions(data.data.descriptions);
          setOutcomeNotes(data.data.descriptions.map((_d) => _d?.description));
        }
        setEssentialNotes(
          data?.data?.essentialSupport?.map((_e) => _e?.description)
        );
        setSupportPlan(data?.data?.supportPlan || "no");
        setEssentialSupportToggle(data?.data?.essentialSupportToggle || "no");
        setInterventionPlanToggle(data?.data?.interventionPlanToggle || "no");
        if (data?.data?.essentialSupport?.length) {
          setEssentialSupport(data?.data?.essentialSupport);
        }
        if (data?.data?.goals?.length) {
          setGoals(data?.data?.goals);
        }
      }
    }
    if (!view)
      noteType?.custom?.forEach((field) => {
        if (field?.fieldType?.name === "date" && !edit) {
          form?.setFieldValue(field.field, new Date());
        }
        if (field?.fieldType?.name === "time" && !edit) {
          const currentTime = new Date().toLocaleTimeString("en-US", {
            hour12: false,
          });
          const isSafari = /^((?!chrome|android).)*safari/i.test(
            navigator.userAgent
          );
          if (isSafari) {
            let hours = new Date().getHours(),
              minutes = new Date().getMinutes();
            form?.setFieldValue(field.field, `${hours}:${minutes}`);
          } else {
            form?.setFieldValue(field.field, currentTime);
          }
        }
      });
  }, [noteType?.custom, opened]);

  useEffect(() => {
    if (userId) {
      handlePatientChange(userId, "Client Name");
    }
  }, [userId, allPatients]);

  // When a note is opened from a client's profile, Incident Report needs its
  // own array-based fields (clientsInvolved) preselected too — the generic
  // handlePatientChange above only sets the single-value form field, which
  // Incident Report doesn't use for its client selection.
  useEffect(() => {
    if (
      userId &&
      noteType?.name === "Incident Report" &&
      !view &&
      !edit &&
      !incidentNoteData.clientsInvolved?.length
    ) {
      const patient = allPatients?.find((p) => p?.id === userId);
      if (patient) {
        setIncidentNoteData((prev) => ({ ...prev, clientsInvolved: [userId] }));
        // Facility is intentionally left blank — user must choose it
        // themselves. The Facility dropdown's options are already scoped to
        // this client's facilities (see the Facilities fieldType's data
        // filter, which checks noteType?.name === "Incident Report" && userId).
      }
    }
  }, [userId, allPatients, noteType?.name, view, edit]);

  // When a note is opened from a client's profile, Group Note also locks to
  // that one client (same treatment as the Incident Report effect above).
  useEffect(() => {
    if (userId && isGroupNoteCreating && !selectedGroupClients?.length) {
      const patient = allPatients?.find((p) => p?.id === userId);
      if (patient) {
        setSelectedGroupClients([userId]);
      }
    }
  }, [userId, allPatients, isGroupNoteCreating]);

  // ── Generate Note (Task module → ADL activities) prefill/auto-generate ──
  // Resolve the requested noteType once note types load, regardless of whether
  // they were already in Redux by the time this component mounted.
  const autoNoteTypeSetRef = useRef(false);
  useEffect(() => {
    if (!opened) {
      autoNoteTypeSetRef.current = false;
      return;
    }
    if (!autoGenerateActivities?.length || autoNoteTypeSetRef.current || noteType) return;
    const nt = allNotesTypes?.ntypes?.find((v) => v?.id === NOTETYPE);
    if (nt) {
      setNoteType(nt);
      autoNoteTypeSetRef.current = true;
    }
  }, [opened, autoGenerateActivities, NOTETYPE, allNotesTypes, noteType]);

  // Preselect the facility passed in from the caller once facilities have loaded.
  useEffect(() => {
    if (opened && initialFacilityId && !selectedFacility && facilities?.facilities?.length) {
      const fac = facilities.facilities.find(
        (f) => String(f?.id) === String(initialFacilityId)
      );
      if (fac) handleFacilityChange(String(fac.id), "Facility Name");
    }
  }, [opened, initialFacilityId, facilities, selectedFacility]);

  useEffect(() => {
    setCurrentNote(noteType?.id);
    setAiActionsOpen(false);
    setComparePanel(COMPARE_PANEL_RESET);
  }, [noteType]);

  // Fetch draft whenever the client OR the note type changes.
  // Clears the previous draft first so a stale banner never lingers.
  useEffect(() => {
    if (edit || view || !noteType?.id) return;
    // Restoring a draft changes this effect's own dependencies
    // (clientsInvolved/selectedFacility), which would otherwise immediately
    // re-fetch and re-show the very draft that was just restored/cleared.
    if (justRestoredDraftRef.current) { justRestoredDraftRef.current = false; return; }
    const isIncidentReportFetch = noteType?.name === "Incident Report";
    // Backend matches `client` by array membership even when it's stored
    // as an array, so any one involved client id is enough to find the
    // Incident Report / Group Note draft.
    const patientId = isIncidentReportFetch
      ? incidentNoteData.clientsInvolved?.[0]
      : isGroupNote
      ? selectedGroupClients?.[0]
      : selectedPatient?.id || userId;
    if (!patientId) return;
    const facId = (isIncidentReportFetch || isGroupNote) ? selectedFacility : selectedPatient?.facilityId?.id;
    // Incident Report / Group Note's facility isn't implied by the client
    // (one client can belong to several facilities) — without it, the
    // backend query has no facility filter at all and can match a draft
    // from a totally different facility. Wait until both are chosen before
    // even attempting the fetch.
    if ((isIncidentReportFetch || isGroupNote) && !facId) return;
    setPendingDraft(null);
    if (autoRestoreDraft) setDraftFetching(true);
    getNoteDraft(noteType.id, patientId, facId).then((draft) => {
      if (draft?.formState?.fields && Object.keys(draft.formState.fields).length > 2) {
        setPendingDraft(draft.formState);
      } else if (isIncidentReportFetch && draft?.formState?.incidentDetails) {
        setPendingDraft(draft.formState);
      } else if (isGroupNote && draft?.formState?.groupClientIds?.length) {
        setPendingDraft(draft.formState);
      } else {
        if (autoRestoreDraft) setDraftFetching(false);
      }
    }).catch(() => { if (autoRestoreDraft) setDraftFetching(false); });
  }, [noteType?.id, noteType?.name, selectedPatient?.id, incidentNoteData.clientsInvolved, selectedFacility, isGroupNote, selectedGroupClients]);

  // When opened from Drafts tab, auto-restore without requiring user to click "Continue editing"
  useEffect(() => {
    if (autoRestoreDraft && pendingDraft) {
      restoreDraft();
      draftRestoredRef.current = true;
      setDraftFetching(false);
    }
  }, [pendingDraft, autoRestoreDraft]);

  const initialValues = {};

  allNotesTypes?.ntypes?.custom?.map((value) => {
    initialValues[value?.field] = "";
  });

  const form = useForm({
    initialValues: (() => {
      const initialValues = { name: edit ? data?.data?.name : "" };
      noteType?.custom?.forEach((field) => {
        initialValues[field.field] = "";
      });

      return initialValues;
    })(),
  });

  // Recomputed on every render straight from current form.values / component
  // state — no submit-time validation step to trust, no popup, just disables
  // Save whenever something required is still empty. custom[].priority (the
  // Note Type Studio's "Required" toggle) drives it generically for every
  // note type; Incident Report's own hand-required fields (see onSave) are
  // mirrored here too since they aren't part of custom[].
  const isSaveDisabled = (() => {
    const excluded = new Set([
      "Client Name", "Patient Name", "Staff Name", "Facility Name", "Date", "Time", "Description",
    ]);
    const isEmpty = (v) => {
      if (v === null || v === undefined) return true;
      if (typeof v === "string") return v.trim() === "";
      if (Array.isArray(v)) return v.length === 0;
      return false;
    };
    const hasEmptyCustomRequired = (noteType?.custom || []).some(
      (c) => c?.priority && c?.field && !excluded.has(c.field) && isEmpty(form.values?.[c.field]),
    );
    if (hasEmptyCustomRequired) return true;

    if (noteType?.name === "Incident Report") {
      if (isEmpty(form.values?.["Description"])) return true;
      if (isEmpty(form.values?.["Immediate Actions Taken"])) return true;
      if (!incidentNoteData.type) return true;
      if (incidentNoteData.type === "Other" && isEmpty(incidentNoteData.otherType)) return true;
      if (!incidentNoteData.severity) return true;
      if (!incidentNoteData.clientsInvolved?.length) return true;
      if (!incidentNoteData.staffInvolved?.length) return true;
      if (
        incidentNoteData.injurySustained &&
        typeof incidentNoteData.injurySustained === "object" &&
        !incidentNoteData.injurySustained.medicalAttention
      ) {
        return true;
      }
      if (
        incidentNoteData.emergencyServicesCalled &&
        typeof incidentNoteData.emergencyServicesCalled === "object" &&
        incidentNoteData.emergencyServicesCalled.clientTransported &&
        isEmpty(incidentNoteData.emergencyServicesCalled.receivingHospital)
      ) {
        return true;
      }
      if (isReportableSeverity && !incidentNoteData.notifiedContacts?.["agency-state-reporting"]) {
        return true;
      }
    }

    return false;
  })();

  // Drive the shared supportPlan / essentialSupportToggle state from the studio
  // "Use care plan?" toggle. Only the part the template documents can flip to
  // "yes", and only when the client actually has that plan type active — so the
  // other block (and its validation) never fires, and neither fires without a
  // plan behind it.
  useEffect(() => {
    if (!isCareplanTemplate || !careplanLabel) return;
    // Viewing a saved note: its own stored toggles win. Recomputing from the
    // client's CURRENT plans would hide documentation that was captured back
    // when the plan was still active.
    if (view) return;
    // Multiple clients → no per-client care plan at all, same as the built-in
    // Progress / Essential Support / Intervention notes.
    const wantsCarePlan =
      form.values[careplanLabel] === "Yes" && !isMultiClientSelected;
    setSupportPlan(
      wantsCarePlan && careplanHasOutcomes && hasActiveSupportPlan ? "yes" : "no"
    );
    setEssentialSupportToggle(
      wantsCarePlan && careplanHasEssential && hasActiveEssentialSupportPlan
        ? "yes"
        : "no"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCareplanTemplate,
    careplanLabel,
    form.values[careplanLabel],
    careplanHasOutcomes,
    careplanHasEssential,
    hasActiveSupportPlan,
    hasActiveEssentialSupportPlan,
    isMultiClientSelected,
    view,
  ]);

  // Seed the Shift field with the caller-provided value once the note type is ready.
  useEffect(() => {
    if (opened && noteType && initialShift && !form.values.Shift) {
      form.setFieldValue("Shift", initialShift);
    }
  }, [opened, initialShift, noteType]);

  // Once client and facility are in place, call the same quick-note AI endpoint
  // the Quick Notes picker uses (POST /note/ai-response/quick-note) with the
  // completed ADL activities, and drop the reply straight into Description.
  const autoGenerateFiredRef = useRef(false);
  useEffect(() => {
    if (!opened) {
      autoGenerateFiredRef.current = false;
      return;
    }
    if (!autoGenerateActivities?.length || autoGenerateFiredRef.current) return;
    if (!selectedPatient || !facilityDetials?.name) return;
    autoGenerateFiredRef.current = true;
    generateQuickNoteAi({
      activities: autoGenerateActivities,
      clientName: selectedPatient?.name || "",
      shift: initialShift || "",
    }).then((result) => {
      const text =
        result?.aiReply ||
        result?.data?.aiReply ||
        result?.data?.note ||
        result?.note ||
        (typeof result?.data === "string" ? result.data : "");
      if (text) form.setFieldValue("Description", text);
    });
  }, [opened, autoGenerateActivities, selectedPatient, facilityDetials, initialShift]);

  // ── Auto-save interval using refs to avoid stale closures ──
  const formRef = useRef(form);
  const stateRef = useRef({});
  formRef.current = form;
  const isIncidentReport = noteType?.name === "Incident Report";
  stateRef.current = {
    edit,
    view,
    opened,
    autoSaveDraftsEnabled,
    NOTETYPE,
    noteTypeId: noteType?.id,
    clientId: isGroupNote
      ? selectedGroupClients
      : isIncidentReport
      ? incidentNoteData.clientsInvolved
      : isMultiSelectNoteType
      ? multiClients
      : selectedPatient?.id || userId,
    facilityId: isGroupNote || isIncidentReport ? selectedFacility : selectedPatient?.facilityId?.id,
    incidentDetails: isIncidentReport ? incidentNoteData : null,
    // Group Note's client picker (selectedGroupClients) is separate React
    // state, not part of the Mantine form's own values — snapshot it
    // alongside incidentDetails (same reason: Incident Report's multi-client
    // selection isn't a form field either) so restoreDraft has it to restore.
    groupClientIds: isGroupNote ? selectedGroupClients : null,
    // Same reasoning for the other multi-select note types (Essential
    // Support, Billing Services, Intervention Progress, Progress Note) —
    // multiClients/multiStaff are separate state too, never mirrored into
    // form.values while editing (only folded into fields at actual Save).
    multiClientIds: isMultiSelectNoteType ? multiClients : null,
    multiStaffIds: isMultiSelectNoteType ? multiStaff : null,
    descriptions, essentialSupport, essentialSupportCount, essentialSupportType, essentialSupportNumber, essentialNumber, activities, frequencyOptions, outComeTitle, outcomeNumbers, goals, goalItems, goalOptions, goalNumber, objectivesByGoal, interventionsByGoal, supportPlan, essentialSupportToggle, interventionPlanToggle,
  };

  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const s = stateRef.current; const f = formRef.current;
        if (s.edit || s.view || !s.opened || !s.autoSaveDraftsEnabled || !f?.values) return;
        const ntId = s.NOTETYPE || s.noteTypeId; const clientId = s.clientId; const facilityId = s.facilityId || f.values?.["Facility Name"];
        const hasClient = Array.isArray(clientId) ? clientId.length > 0 : !!clientId;
        if (!ntId || !hasClient || !facilityId) return;
        const snap = JSON.stringify({ fields: f.values, incidentDetails: s.incidentDetails, groupClientIds: s.groupClientIds, multiClientIds: s.multiClientIds, multiStaffIds: s.multiStaffIds, descriptions: s.descriptions, essentialSupport: s.essentialSupport, essentialSupportCount: s.essentialSupportCount, essentialSupportType: s.essentialSupportType, essentialSupportNumber: s.essentialSupportNumber, essentialNumber: s.essentialNumber, activities: s.activities, frequencyOptions: s.frequencyOptions, outComeTitle: s.outComeTitle, outcomeNumbers: s.outcomeNumbers, goals: s.goals, goalItems: s.goalItems, goalOptions: s.goalOptions, goalNumber: s.goalNumber, objectivesByGoal: s.objectivesByGoal, interventionsByGoal: s.interventionsByGoal, supportPlan: s.supportPlan, essentialSupportToggle: s.essentialSupportToggle, interventionPlanToggle: s.interventionPlanToggle });
        if (snap === lastSavedSnapshotRef.current) return;
        setDraftStatus("saving");
        const ok = await saveNoteDraft({ noteTypeId: ntId, clientId, facilityId, formState: JSON.parse(snap) });
        if (ok) { lastSavedSnapshotRef.current = snap; setDraftStatus("saved"); setTimeout(() => setDraftStatus("idle"), 2500); }
        else setDraftStatus("idle");
      } catch { setDraftStatus("idle"); }
    }, 3000);
    return () => clearInterval(id);
  }, []);

  // Restore draft
  const restoreDraft = () => {
    if (!pendingDraft) return;
    const fs = { ...pendingDraft };
    setPendingDraft(null);
    justRestoredDraftRef.current = true;

    const skip = new Set(["noteId", "noteid", "Staff Name", "name", "Client Name", "Patient Name"]);
    const dateFields = new Set();
    (noteType?.custom || []).forEach(f => { if (f?.fieldType?.name?.toLowerCase() === "date") dateFields.add(f.field); });
    const keys = new Set(Object.keys(form.values || {}));

    // 1. Set toggles + arrays synchronously
    if (fs.supportPlan) setSupportPlan(fs.supportPlan);
    if (fs.essentialSupportToggle) setEssentialSupportToggle(fs.essentialSupportToggle);
    if (fs.interventionPlanToggle) setInterventionPlanToggle(fs.interventionPlanToggle);
    if (fs.descriptions?.length > 0) setDescriptions(fs.descriptions);
    if (fs.essentialSupport?.length > 0) setEssentialSupport(fs.essentialSupport);
    if (fs.essentialSupportCount?.length > 0) setEssentialSupportCount(fs.essentialSupportCount);
    if (fs.essentialSupportType?.length > 0) setEssentialSupportType(fs.essentialSupportType);
    if (fs.essentialSupportNumber?.length > 0) setEssentialSupportNumber(fs.essentialSupportNumber);
    if (fs.activities?.length > 0) setActivities(fs.activities);
    if (fs.essentialNumber) setEssentialNumber(fs.essentialNumber);
    if (fs.frequencyOptions?.length > 0) setFrequencyOptions(fs.frequencyOptions);
    if (fs.outComeTitle?.length > 0) setOutComeTitle(fs.outComeTitle);
    if (fs.outcomeNumbers?.length > 0) setOutComeNumbers(fs.outcomeNumbers);
    if (fs.goals?.length > 0) setGoals(fs.goals);
    if (fs.goalItems?.length > 0) setGoalItems(fs.goalItems);
    if (fs.goalOptions?.length > 0) setGoalOptions(fs.goalOptions);
    if (fs.goalNumber?.length > 0) setGoalNumbers(fs.goalNumber);
    if (fs.objectivesByGoal?.length > 0) setObjectivesByGoal(fs.objectivesByGoal);
    if (fs.interventionsByGoal?.length > 0) setInterventionsByGoal(fs.interventionsByGoal);

    // Re-set after delay in case selectedFacility useEffect resets them
    const saved = { goals: fs.goals, goalItems: fs.goalItems, goalOptions: fs.goalOptions, goalNumber: fs.goalNumber, objectivesByGoal: fs.objectivesByGoal, interventionsByGoal: fs.interventionsByGoal, descriptions: fs.descriptions, essentialSupport: fs.essentialSupport, essentialSupportCount: fs.essentialSupportCount, essentialSupportType: fs.essentialSupportType, essentialSupportNumber: fs.essentialSupportNumber, essentialNumber: fs.essentialNumber, activities: fs.activities, frequencyOptions: fs.frequencyOptions, outComeTitle: fs.outComeTitle, outcomeNumbers: fs.outcomeNumbers };
    setTimeout(() => {
      draftRestoringRef.current = true;
      if (saved.goals?.length > 0) setGoals(saved.goals);
      if (saved.goalItems?.length > 0) setGoalItems(saved.goalItems);
      if (saved.goalOptions?.length > 0) setGoalOptions(saved.goalOptions);
      if (saved.goalNumber?.length > 0) setGoalNumbers(saved.goalNumber);
      if (saved.objectivesByGoal?.length > 0) setObjectivesByGoal(saved.objectivesByGoal);
      if (saved.interventionsByGoal?.length > 0) setInterventionsByGoal(saved.interventionsByGoal);
      if (saved.descriptions?.length > 0) setDescriptions(saved.descriptions);
      if (saved.essentialSupport?.length > 0) setEssentialSupport(saved.essentialSupport);
      if (saved.essentialSupportCount?.length > 0) setEssentialSupportCount(saved.essentialSupportCount);
      if (saved.essentialSupportType?.length > 0) setEssentialSupportType(saved.essentialSupportType);
      if (saved.essentialSupportNumber?.length > 0) setEssentialSupportNumber(saved.essentialSupportNumber);
      if (saved.activities?.length > 0) setActivities(saved.activities);
      if (saved.essentialNumber) setEssentialNumber(saved.essentialNumber);
      if (saved.frequencyOptions?.length > 0) setFrequencyOptions(saved.frequencyOptions);
      if (saved.outComeTitle?.length > 0) setOutComeTitle(saved.outComeTitle);
      if (saved.outcomeNumbers?.length > 0) setOutComeNumbers(saved.outcomeNumbers);
    }, 800);

    // 3. Set facility — guard prevents selectedFacility useEffect from resetting goals
    draftRestoringRef.current = true;
    if (fs.fields?.["Facility Name"]) {
      handleFacilityChange(fs.fields["Facility Name"], "Facility Name");
    }

    // 4. Set all form fields — don't check if key exists (form.setFieldValue can add new keys)
    if (fs.fields) {
      Object.entries(fs.fields).forEach(([k, v]) => {
        if (skip.has(k) || v == null) return;
        try {
          if (dateFields.has(k) && typeof v === "string") {
            const d = new Date(v); if (!isNaN(d.getTime())) form.setFieldValue(k, d);
          } else {
            form.setFieldValue(k, v);
          }
        } catch {}
      });
    }

    // 5. Incident Report's own data (multi-client/staff arrays + hand-built
    // fields) isn't part of the generic form.values — restore it separately.
    // Runs AFTER handleFacilityChange above (which clears clientsInvolved
    // for Incident Report on facility change) so the restored selection
    // isn't wiped out by it.
    if (fs.incidentDetails) {
      setIncidentNoteData((prev) => ({
        ...prev,
        ...fs.incidentDetails,
        // Opened from a specific client's profile — that client is locked
        // (Client Involved select is disabled), so even if the draft was
        // saved with more clients selected at the time, only the one the
        // user is currently on should end up selected.
        clientsInvolved: userId ? [userId] : fs.incidentDetails.clientsInvolved,
      }));
    }

    // 6. Group Note's client picker (selectedGroupClients) is likewise
    // separate React state, not part of form.values — restore it the same
    // way incidentDetails is restored above.
    if (isGroupNote && fs.groupClientIds?.length) {
      setSelectedGroupClients(fs.groupClientIds);
    }

    // 7. Multi-select note types (Essential Support, Billing Services,
    // Intervention Progress, Progress Note) hold their Client(s)/Staff
    // selections in separate multiClients/multiStaff state, not scalar form
    // fields — same reason Group Note's picker needs restoring above, and
    // saved under the same dedicated snapshot keys (fs.fields never actually
    // carries them — see the auto-save snapshot). Also seeds selectedPatient
    // from the first client, same as the (edit || view) prefill effect does,
    // since Facility auto-derives from selectedPatient for these note types
    // — without it Facility never fills in either.
    if (isMultiSelectNoteType) {
      const c = fs.multiClientIds;
      const s = fs.multiStaffIds;
      if (c?.length) {
        setMultiClients(c);
        const firstPatient = allPatients?.find((p) => p?.id === c?.[0]);
        if (firstPatient) setSelectedPatient(firstPatient);
      }
      if (s?.length) setMultiStaff(s);
    }

    // A studio template keeps its clients in the form itself (the locked
    // "Client Name" key), so the restore above skips it — but multiClients is
    // what tells the drawer a note spans several clients, which is what hides
    // the "Use care plan?" field. Mirror the restored selection into it.
    if (isStudioTemplate) {
      const c = fs.fields?.["Client Name"];
      const ids = Array.isArray(c) ? c : c ? [c] : [];
      if (ids.length) setMultiClients(ids);
    }

    setDraftStatus("restored"); setTimeout(() => setDraftStatus("idle"), 3000);
  };

  // Save on close, reset refs; fire "abandoned" if compare panel was open when modal closed
  useEffect(() => {
    if (!opened) {
      setComparePanel((prev) => {
        if (prev.open) {
          trackAiSuggestionResolved({
            action: normalizeAction(prev.action),
            outcome: "abandoned",
            option_chosen: null,
            chosen_model: null,
            regeneration_count: prev.regenerationCount,
            note_type: noteType?.name || "",
          });
          updateAiSuggestionEvent(prev.suggestionEventId, {
            selected_option: null,
            regeneration_count: prev.regenerationCount,
          });
        }
        return prev;
      });
      draftRestoredRef.current = false;
      setDraftStatus("idle");
      lastSavedSnapshotRef.current = "CLOSED";
      setPendingDraft(null);
      setDraftFetching(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  useEffect(() => {
    const staffIdForLookup =
      noteType?.name === "Incident Report"
        ? incidentNoteData.staffInvolved?.[0]
        : useMultiStaff
        ? multiStaff?.[0]
        : form.values?.["Staff Name"];
    const staffDetails = staff?.staffs?.find(
      (value) => String(value?.id) === String(staffIdForLookup)
    );
    setStaffDetials(staffDetails);
  }, [staff, form.values?.["Staff Name"], noteType?.name, incidentNoteData.staffInvolved, useMultiStaff, multiStaff]);

  // `facilityDetials` (the full facility object) is derived state, same as
  // `staffDetials` above — keep it in sync with whichever facility id is
  // selected, however it got selected. handleFacilityChange isn't the only
  // path: the Progress Note → Essential Support / Billing carry-over sets
  // `selectedFacility` + the form field directly, which used to leave this
  // null even though the Select visibly showed a facility — so SKAI reported
  // "Facility Name is required." and `isAiAccess` hid the AI buttons outright
  // for staff. Edit mode is left alone; it seeds this from the saved note.
  useEffect(() => {
    if (edit) return;
    // Billing Services notes hold "Facility Name" as an array of the client's
    // facilities — take the first, same as the client-id normalization above.
    const rawFacility = selectedFacility || form.values?.["Facility Name"];
    const facilityId = Array.isArray(rawFacility) ? rawFacility?.[0] : rawFacility;
    if (!facilityId || !facilities?.facilities?.length) return;
    const fac = facilities.facilities.find(
      (f) => String(f?.id) === String(facilityId)
    );
    if (fac) setFacilityDetials(fac);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, selectedFacility, form.values?.["Facility Name"], facilities]);

  const validateFields = () => {
    const newErrors = {};
    descriptions?.forEach((medication, index) => {
      if (!medication?.outcomeNumber) {
        newErrors[index] = "Field is required";
      }
      if (!medication?.outcomeTitle) {
        newErrors[index] = "Field is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEssentialSupport = () => {
    const errors = essentialSupport.map((item) => {
      const activityEmpty = !item.essentialActivityNumber;
      const typeEmpty = !item.essentialSupportType;
      return activityEmpty || typeEmpty;
    });
    setEssentialSupportErrors(errors);

    // Optionally return whether the form is valid
    return !errors.includes(true);
  };

  const noteDetails = form.values;

  const sendMessageToOpenAI = async (value) => {
    setMessages({});
    if (value === "behavior") {
      setBehaviorLoading(true);
    } else if (value === "response") {
      setResponseLoading(true);
    } else if (value === "plan") {
      setPlanLoading(true);
    } else {
      setLoading(true); // only when no value comes / doesn't match any case
    }

    try {
      const aiRequest = buildAiContext(value);

      const aiReply = await generateAiNote(
        aiRequest,
        profile?.user?.admin || profile?.user?.id,
        "ollama"
      );

      if (aiReply) {
        if (supportPlan === "yes") {
          setDescriptions((prev) => {
            const updated = [...prev];
            updated[openedAccordion] = {
              ...updated[openedAccordion],
              message: aiReply?.aiReply,
            };
            return updated;
          });
        } else if (essentialSupportToggle === "yes") {
          setEssentialSupport((prev) => {
            const updated = [...prev];
            updated[essentialAccordion] = {
              ...updated[essentialAccordion],
              message: aiReply?.aiReply,
            };
            return updated;
          });
        } else if (interventionPlanToggle === "yes") {
          setGoals((prev) => {
            const updated = [...prev];
            updated[goalsAccordion] = {
              ...updated[goalsAccordion],

              // message: aiReply,
              ...(value === "behavior"
                ? { behaviorAiReply: aiReply?.aiReply || "" }
                : value === "response"
                ? { responseAiReply: aiReply?.aiReply || "" }
                : value === "plan"
                ? { planAiReply: aiReply?.aiReply || "" }
                : { message: aiReply?.aiReply || "" }),
            };
            return updated;
          });
        } else {
          setMessages((prev) => {
            const updated = { ...prev, message: aiReply?.aiReply };
            return updated;
          });
        }
      }
    } catch (error) {
      if (value === "behavior") {
        setBehaviorLoading(false);
      } else if (value === "response") {
        setResponseLoading(false);
      } else if (value === "plan") {
        setPlanLoading(false);
      } else {
        setLoading(false); // only when no value comes / doesn't match any case
      }
      // This used to fail silently — the spinner stopped and nothing appeared,
      // with no hint that the org had simply run out of SkAi tokens.
      if (!showSkaiTokenError(error)) {
        showError(
          error?.response?.data?.message || "Unable to generate response from SkAi.",
          error?.response?.status
        );
      }
    } finally {
      if (value === "behavior") {
        setBehaviorLoading(false);
      } else if (value === "response") {
        setResponseLoading(false);
      } else if (value === "plan") {
        setPlanLoading(false);
      } else {
        setLoading(false); // only when no value comes / doesn't match any case
      }
    }
  };

  // ── buildAiContext: collects structured context for the backend prompt builder ──
  // Returns { action, context } — no prompt text lives in the frontend.
  const buildAiContext = (value, fieldContext) => {
    const noteDetails = form.values;

    // Resolve the text the user actually typed from the field the AI button
    // was clicked in. Previously this was re-derived from note-type NAME
    // strings ("Progress Note" / "Essential Support Note") plus the open
    // accordion index — but the rest of this file identifies those types by
    // ID constant, so a type whose name didn't match exactly fell through to
    // form.values.Description, which is empty for the accordion textareas.
    // The prompt then went out with an empty Staff Note and the model
    // returned the same generic note no matter what was typed.
    const resolveFieldText = (ctx) => {
      if (!ctx) return null;
      if (ctx === "main") return form.values?.Description?.trim() || "";
      if (ctx.startsWith("description_"))
        return descriptions?.[Number(ctx.split("_")[1])]?.description?.trim() || "";
      if (ctx.startsWith("essential_"))
        return essentialSupport?.[Number(ctx.split("_")[1])]?.description?.trim() || "";
      if (ctx === "behavior") return goals?.[goalsAccordion]?.behaviorDescription?.trim() || "";
      if (ctx === "response") return goals?.[goalsAccordion]?.responseDescription?.trim() || "";
      if (ctx === "plan") return goals?.[goalsAccordion]?.planDescription?.trim() || "";
      return null;
    };
    const fieldText = resolveFieldText(fieldContext);

    // Essential support context
    let tempEssentialSupport = "";
    const selectedSupport = essentialSupport?.[essentialAccordion];
    if (essentialSupport) {
      const selectedActivity = selectedSupport?.activities?.find(
        (activity) => String(activity?.id) === String(selectedSupport?.essentialActivityNumber)
      );
      let howToSupportText = "";
      const howToSupport = selectedActivity?.howToSupport;
      if (Array.isArray(howToSupport)) {
        howToSupportText = howToSupport.map((item) => (typeof item === "string" ? item : item?.text)).filter(Boolean).join(", ");
      } else if (typeof howToSupport === "string") {
        howToSupportText = howToSupport;
      }
      const supportStatement = selectedActivity?.supportStatement || "";
      tempEssentialSupport = `${supportStatement}\n${howToSupportText}`.trim();
    }

    // Support activities context
    let filteredDescriptions = descriptions?.filter((item) => item?.details !== null);
    let supportActivitiesSection = "";
    const selectedItem = descriptions?.[openedAccordion];
    if (filteredDescriptions?.length > 0 && selectedItem?.details) {
      const { outcomeCategory, activities } = selectedItem.details;
      const activityNames = activities?.map((activity) => activity?.supportActivity).filter(Boolean).join(", ") || "";
      const supportInstructionsText = activities?.map((activity) => {
        const instr = activity?.supportInstructions;
        if (Array.isArray(instr)) return instr.map((i) => (typeof i === "string" ? i : i?.text)).filter(Boolean).join(", ");
        else if (typeof instr === "string") return instr;
        return "";
      }).filter(Boolean).join(", ") || "";
      supportActivitiesSection = `${outcomeCategory || ""}\n${activityNames}\n${supportInstructionsText}`.trim();
    }

    // Intervention plan context
    let tempInterventionPlan = "";
    const selectedIntervention = goals?.[goalsAccordion];
    if (selectedIntervention?.goalActivities?.length) {
      tempInterventionPlan = selectedIntervention.goalActivities.map(
        (activity) => ` - Objectives : ${activity?.objectives || ""}\n - Instructions : ${activity?.Intervention_Instruction || ""}`
      ).join("\n\n");
    }

    // fieldText wins whenever the caller told us which field it came from;
    // the derivation below is only the fallback for callers that don't
    // (sendMessageToOpenAI).
    const staffNoteValue =
      fieldText !== null
        ? fieldText
        : supportPlan === "yes" || essentialSupportToggle === "yes"
        ? selectedItem?.description?.trim() || selectedSupport?.description?.trim() || ""
        : noteDetails?.Description?.trim() || "";

    // Determine the action name the backend uses to select the prompt template
    const action =
      interventionPlanToggle === "yes" && value === "behavior" ? "behavior"
      : value === "response" ? "response"
      : value === "plan" ? "plan"
      : value === "polishNotePrompt" ? "polishNotePrompt"
      : value === "rewriteNotePrompt" ? "rewriteNotePrompt"
      : value === "elaborateNotePrompt" ? "elaborateNotePrompt"
      : "custom";

    // Frontend resolves which input field to use for intervention notes
    // Identify by ID, like the rest of this component — the display name is
    // admin-editable and was silently failing this check.
    const isInterventionNote =
      isIntervention_Progress_Note && interventionPlanToggle === "yes";

    // Multi-select note types can carry several clients AND several staff
    // (held in multiClients / multiStaff). The AI prompt previously received
    // only the first client (selectedPatient) and first staff (staffDetials),
    // so the generated note read as a single-client/single-staff session.
    // Resolve the FULL list of names so the note documents everyone selected.
    const resolveClientName = (id) => allPatients?.find((p) => p?.id === id)?.name || "";
    const resolveStaffName = (id) =>
      staff?.staffs?.find((s) => String(s?.id) === String(id))?.name || "";

    // Incident Report holds its clients/staff in its own picker
    // (incidentNoteData.clientsInvolved / staffInvolved) and is excluded from
    // the selectedPatient / multiClients paths, so resolve its names directly —
    // otherwise individualName/staffName arrive empty and the AI prompt leaves
    // its "{Individual Name}" placeholder in the generated text.
    const isIncidentReport = noteType?.name === "Incident Report";

    const aiClientNames = isGroupNote
      ? selectedGroupClients.map(resolveClientName).filter(Boolean)
      : isIncidentReport
      ? (incidentNoteData.clientsInvolved || []).map(resolveClientName).filter(Boolean)
      : useMultiClient && multiClients?.length
      ? multiClients.map(resolveClientName).filter(Boolean)
      : [selectedPatient?.name].filter(Boolean);

    const aiStaffNames = isIncidentReport
      ? (incidentNoteData.staffInvolved || []).map(resolveStaffName).filter(Boolean)
      : useMultiStaff && multiStaff?.length
      ? multiStaff.map(resolveStaffName).filter(Boolean)
      : [staffDetials?.name].filter(Boolean);

    const aiClientsLabel = aiClientNames.join(", ");
    const aiStaffLabel = aiStaffNames.join(", ");
    const isMultiParticipant = aiClientNames.length > 1 || aiStaffNames.length > 1;

    const context = {
      state: selectedStateAbbr || "",
      dateOfService: moment(noteDetails?.Date).format("MM/DD/YYYY") || "",
      sessionType: isGroupNote
        ? "Group Session (multiple participants — the note must reflect observations and documentation for ALL participants listed below)"
        : isMultiParticipant
        ? `Session involving multiple people — the note MUST document ALL ${aiClientNames.length} client(s) (${aiClientsLabel}) and ALL ${aiStaffNames.length} staff member(s) (${aiStaffLabel}) by name, not just one individual.`
        : "Individual Session",
      individualName: aiClientsLabel || selectedPatient?.name || "",
      supportActivities: `${supportActivitiesSection || ""} ${tempEssentialSupport || ""}`.trim(),
      staffNote: staffNoteValue,
      staffName: aiStaffLabel || staffDetials?.name || "",
      shift: noteDetails?.Shift || "",
      noteType: noteDetails?.noteid?.name || "",
      facilityType: facilityDetials?.name || "",
      behaviorInput:
        fieldContext === "behavior" && fieldText !== null
          ? fieldText
          : isInterventionNote
          ? selectedIntervention?.behaviorDescription?.trim() || ""
          : noteDetails?.Description?.trim() || "",
      responseInput:
        fieldContext === "response" && fieldText !== null
          ? fieldText
          : isInterventionNote
          ? selectedIntervention?.responseDescription?.trim() || ""
          : noteDetails?.Description?.trim() || "",
      planInput:
        fieldContext === "plan" && fieldText !== null
          ? fieldText
          : isInterventionNote
          ? selectedIntervention?.planDescription?.trim() || ""
          : noteDetails?.Description?.trim() || "",
      identifiedGoals: selectedIntervention?.identifiedGoal || "",
      interventionPlan: tempInterventionPlan,
    };

    return { action, context };
  };

  // ── handleDualCompare: fires both providers simultaneously and shows ComparePanel ──
  const handleDualCompare = async (promptKey, context) => {
    if (!["behavior", "response", "plan", "custom-description"].includes(promptKey)) {
      const errors = validatePromptFields();
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
    }
    setFormErrors({});

    if (existingRecord?.active === false && existingRecord?.paid === false) {
      swal({
        title: "AI Note Assistant Credits Not Found",
        text: "You have not purchased any AI credits yet. Please visit the Settings page and navigate to the AI credits section to buy credits.",
        icon: "info",
        dangerMode: false,
      });
      return;
    }
    if (existingRecord?.planType === "onetime-purchase") {
      const tokensUsage = existingRecord?.adminTotalTokens;
      const totalTokens = Number(tokensUsage?.adminTotalTokens);
      const usedTokens = Number(tokensUsage?.adminUsedTokens);
      const originalTotal = totalTokens + usedTokens;
      const percentageUsed = originalTotal > 0 ? Math.round((usedTokens / originalTotal) * 100) : 0;
      if (percentageUsed >= 100) {
        setShowWarning(true);
        swal({ title: "AI Note Assistant Quota Reached", text: "You've reached your monthly AI Note Assistant quota. Please contact your admin if you need additional access.", icon: "error", dangerMode: true });
        return;
      }
      if (percentageUsed >= 90) {
        setShowWarning(true);
        swal({ title: "AI Note Assistant Quota Exceeded", text: "You've used 90% of your AI Note Assistant quota for this month. Continued use may require admin approval.", icon: "warning", dangerMode: true });
      }
    }

    setAiActionsOpen(false);
    setComparePanel((prev) => ({
      ...COMPARE_PANEL_RESET,
      open: true, action: promptKey, context,
      loading1: true, loading2: true,
      ollamaAvailable: false,
      regenerationCount: prev.open && prev.action === promptKey ? prev.regenerationCount + 1 : 0,
    }));

    const aiRequest = buildAiContext(promptKey, context);
    const adminId = profile?.user?.admin || profile?.user?.id;

    generateBothAiNotes(aiRequest, adminId).then((data) => {
      const m1 = data?.option1_model ?? "openai-gpt-4o-mini";
      const m2 = data?.option2_model ?? "ollama-phi3:3.8b";
      setComparePanel((prev) => ({
        ...prev,
        option1: data?.openaiReply || "Unable to generate response from SkAi.",
        option2: data?.ollamaReply || "Unable to generate response from SkAi.",
        option1Model: m1,
        option2Model: m2,
        option1WordCount: data?.option1_word_count ?? null,
        option2WordCount: data?.option2_word_count ?? null,
        option1LatencyMs: data?.option1_latency_ms ?? null,
        option2LatencyMs: data?.option2_latency_ms ?? null,
        suggestionEventId: data?.suggestionEventId ?? null,
        ollamaAvailable: data?.ollamaAvailable ?? true,
        loading1: false,
        loading2: false,
      }));
      trackAiSuggestionShown({
        action: normalizeAction(promptKey),
        option_1_model: m1,
        option_2_model: m2,
        note_type: noteType?.name || "",
      });
      setShowWarning(false);
    }).catch((error) => {
      // Out of tokens isn't a generation failure — saying "Unable to generate
      // response from SkAi." there reads as an outage. Close the panel (there's
      // nothing to insert) and tell the user their balance is spent.
      if (showSkaiTokenError(error)) {
        setComparePanel({ ...COMPARE_PANEL_RESET });
        return;
      }
      setComparePanel((prev) => ({
        ...prev,
        option1: "Unable to generate response from SkAi.",
        option2: "Unable to generate response from SkAi.",
        loading1: false,
        loading2: false,
      }));
    });
  };

  // ── handleQuickNotesInsert: appends selected library activities as text into whichever textarea Quick Notes was opened from ──
  // `items` ({name, type}[]) carries each activity's hidden Activity Type — not persisted yet
  // (no note field for it), but available here for a future AI/reporting/analytics integration.
  const handleQuickNotesInsert = (text, items) => {
    if (!quickNotesTarget || !text) return;
    const existing = (quickNotesTarget.getValue() || "").trim();
    quickNotesTarget.setValue(existing ? `${existing}\n${text}` : text);
    setQuickNotesTarget(null);
  };

  // ── handleUseCompareOption: applies the chosen option to the correct field ──
  const handleUseCompareOption = (text, context, meta) => {
    if (!text) return;
    if (context === "main") {
      form.setFieldValue("Description", text);
    } else if (context?.startsWith("description_")) {
      const idx = Number(context.split("_")[1]);
      setDescriptions((prev) => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], description: text };
        return updated;
      });
    } else if (context?.startsWith("essential_")) {
      const idx = Number(context.split("_")[1]);
      setEssentialSupport((prev) => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], description: text };
        return updated;
      });
    } else if (context === "behavior") {
      setGoals((prev) => {
        const updated = [...prev];
        updated[goalsAccordion] = { ...updated[goalsAccordion], behaviorDescription: text };
        return updated;
      });
    } else if (context === "response") {
      setGoals((prev) => {
        const updated = [...prev];
        updated[goalsAccordion] = { ...updated[goalsAccordion], responseDescription: text };
        return updated;
      });
    } else if (context === "plan") {
      setGoals((prev) => {
        const updated = [...prev];
        updated[goalsAccordion] = { ...updated[goalsAccordion], planDescription: text };
        return updated;
      });
    }

    console.log("[AI-DEBUG] handleUseCompareOption reached, context:", context);
    setComparePanel((prev) => {
      console.log("[AI-DEBUG] setComparePanel updater running, prev.action:", prev.action);
      const { displaySlot, slot1Model, slot2Model } = meta || {};
      const chosenModel = displaySlot === 1 ? slot1Model : slot2Model;
      aiTouchedFieldsRef.current[context] = {
        action: normalizeAction(prev.action),
        chosen_model: chosenModel ?? null,
        final_text: text,
        // OD-1164: the untouched AI suggestion, kept so the save-time capture can
        // pair it with whatever the user edited it into before saving.
        ai_text: text,
        // behavior/response/plan write into the goal open at accept time — remember
        // which one, since a different accordion may be open by the time of save.
        goal_index: ["behavior", "response", "plan"].includes(context) ? Number(goalsAccordion) || 0 : null,
      };
      console.log("[AI-DEBUG] aiTouchedFieldsRef after set:", aiTouchedFieldsRef.current);
      trackAiSuggestionResolved({
        action: normalizeAction(prev.action),
        outcome: "selected",
        option_chosen: displaySlot ?? null,
        chosen_model: chosenModel ?? null,
        regeneration_count: prev.regenerationCount,
        note_type: noteType?.name || "",
      });
      updateAiSuggestionEvent(prev.suggestionEventId, {
        selected_option: displaySlot ? String(displaySlot) : null,
        regeneration_count: prev.regenerationCount,
      });
      return { ...prev, open: false };
    });
  };

  // ── handleArrangeCompare: stores the actual display-slot model order in state ──
  const handleArrangeCompare = ({ slot1Model, slot2Model }) => {
    setComparePanel((prev) => ({ ...prev, slot1Model, slot2Model }));
  };

  // ── handleCancelCompare: fires dismissed telemetry and closes the panel ──
  const handleCancelCompare = () => {
    setComparePanel((prev) => {
      trackAiSuggestionResolved({
        action: normalizeAction(prev.action),
        outcome: "dismissed",
        option_chosen: null,
        chosen_model: null,
        regeneration_count: prev.regenerationCount,
        note_type: noteType?.name || "",
      });
      updateAiSuggestionEvent(prev.suggestionEventId, {
        selected_option: null,
        regeneration_count: prev.regenerationCount,
      });
      return { ...prev, open: false };
    });
  };

  const recordingTypeRef = useRef(recordingType);
  useEffect(() => {
    recordingTypeRef.current = recordingType;
  }, [recordingType]);

  const originalStaffNoteRef = useRef("");

  const { startListening, stopListening } = useSpeechToText((transcript) => {
    form.setFieldValue("Description", transcript);

    if (recordingTypeRef.current === "description") {
      const updatedDescriptions = [...descriptions];
      updatedDescriptions[openedAccordion] = {
        ...updatedDescriptions[openedAccordion],
        description: transcript,
      };
      setDescriptions(updatedDescriptions);
    }

    if (
      recordingTypeRef.current === "essential" &&
      essentialSupport?.length > 0
    ) {
      const updatedEssentialDes = [...essentialSupport];
      updatedEssentialDes[essentialAccordion] = {
        ...updatedEssentialDes[essentialAccordion],
        description: transcript,
      };
      setEssentialSupport(updatedEssentialDes);
    } else if (recordingTypeRef.current === "behavior" && goals?.length > 0) {
      const updatedGoals = [...goals];
      updatedGoals[goalsAccordion] = {
        ...updatedGoals[goalsAccordion],
        behaviorDescription: transcript,
      };
      setGoals(updatedGoals);
    } else if (recordingTypeRef.current === "response" && goals?.length > 0) {
      const updatedGoals = [...goals];
      updatedGoals[goalsAccordion] = {
        ...updatedGoals[goalsAccordion],
        responseDescription: transcript,
      };
      setGoals(updatedGoals);
    } else if (recordingTypeRef.current === "plan" && goals?.length > 0) {
      const updatedGoals = [...goals];
      updatedGoals[goalsAccordion] = {
        ...updatedGoals[goalsAccordion],
        planDescription: transcript,
      };
      setGoals(updatedGoals);
    }
  });

  const startTimer = () => {
    setRecordingTime(0);
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const handleMicStart = (type) => {
    if (type === "behavior") {
      setMicLabel(type);
      setRecordingType(type);
    } else if (type === "response") {
      setMicLabel(type);
      setRecordingType(type);
    } else if (type === "plan") {
      setMicLabel(type);
      setRecordingType(type);
    } else {
      setIsListening(true);
      setRecordingType(type);
    }
    startTimer();
    startListening();
  };

  const handleRecordingCancel = (type) => {
    form.setFieldValue("Description", "");
    if (recordingTypeRef.current === "description") {
      const updatedDescriptions = [...descriptions];
      updatedDescriptions[openedAccordion] = {
        ...updatedDescriptions[openedAccordion],
        description: "",
      };
      setDescriptions(updatedDescriptions);
    }
    if (
      recordingTypeRef.current === "essential" &&
      essentialSupport?.length > 0
    ) {
      const updatedEssentialDes = [...essentialSupport];
      updatedEssentialDes[essentialAccordion] = {
        ...updatedEssentialDes[essentialAccordion],
        description: "",
      };
      setEssentialSupport(updatedEssentialDes);
    }
    if (type === "behavior") {
      const updatedGoalDes = [...goals];
      updatedGoalDes[goalsAccordion] = {
        ...updatedGoalDes[goalsAccordion],
        behaviorDescription: "",
      };
      setGoals(updatedGoalDes);
      setMicLabel(false);
    } else if (type === "response") {
      const updatedGoalDes = [...goals];
      updatedGoalDes[goalsAccordion] = {
        ...updatedGoalDes[goalsAccordion],
        responseDescription: "",
      };
      setGoals(updatedGoalDes);
      setMicLabel(false);
    } else if (type === "plan") {
      const updatedGoalDes = [...goals];
      updatedGoalDes[goalsAccordion] = {
        ...updatedGoalDes[goalsAccordion],
        planDescription: "",
      };
      setGoals(updatedGoalDes);
      setMicLabel(false);
    }
    stopListening();
    stopTimer();
    setRecordingTime(0);
    setIsListening(false);
  };

  const handleRecordingConfirm = (type) => {
    stopListening();
    stopTimer();
    if (type === "behavior") {
      setMicLabel(false);
    } else if (type === "response") {
      setMicLabel(false);
    } else if (type === "plan") {
      setMicLabel(false);
    } else {
      setIsListening(false);
    }
  };

  let isAiAccess =
    !profile?.user?.role && !profile?.user?.type
      ? true
      : facilityDetials &&
        (facilityDetials?.skaiAccess === true ||
          facilityDetials?.skaiAccess === undefined) &&
        staffDetials &&
        (staffDetials?.permissions?.aiAccess === true ||
          !staffDetials?.permissions);

  // Quick Notes needs to know which client/facility the note is for before a
  // DSP can pick activities for it.
  const quickNotesDisabled =
    noteType?.name === "Incident Report"
      ? !incidentNoteData.clientsInvolved?.length || !form.values["Facility Name"]
      : !form.values["Client Name"] || !form.values["Facility Name"];

  const handleRedo = (type) => {
    form.setFieldValue("Description", "");
    if (recordingTypeRef.current === "description") {
      const updatedDescriptions = [...descriptions];
      updatedDescriptions[openedAccordion] = {
        ...updatedDescriptions[openedAccordion],
        description: "",
      };
      setDescriptions(updatedDescriptions);
    }
    if (
      recordingTypeRef.current === "essential" &&
      essentialSupport?.length > 0
    ) {
      const updatedEssentialDes = [...essentialSupport];
      updatedEssentialDes[essentialAccordion] = {
        ...updatedEssentialDes[essentialAccordion],
        description: "",
      };
      setEssentialSupport(updatedEssentialDes);
    }
    if (type === "behavior") {
      const updatedGoalDes = [...goals];
      updatedGoalDes[goalsAccordion] = {
        ...updatedGoalDes[goalsAccordion],
        behaviorDescription: "",
      };
      setGoals(updatedGoalDes);
    } else if (type === "response") {
      const updatedGoalDes = [...goals];
      updatedGoalDes[goalsAccordion] = {
        ...updatedGoalDes[goalsAccordion],
        responseDescription: "",
      };
      setGoals(updatedGoalDes);
    } else if (type === "plan") {
      const updatedGoalDes = [...goals];
      updatedGoalDes[goalsAccordion] = {
        ...updatedGoalDes[goalsAccordion],
        planDescription: "",
      };
      setGoals(updatedGoalDes);
    }

    stopListening();
    stopTimer();
    setRecordingTime(0);
    startTimer();
    startListening();
  };
  const isAdmin = profile?.user?.role === "admin" || !profile?.user?.type;

  // Staff Signature block — rendered above each Cancel/Save button
  const renderSignatureBlock = (sig, key) => (
    <Box key={key} style={{ padding: "12px 16px", backgroundColor: "#FFFFFF", borderRadius: 8, border: "1px solid var(--app-border)", marginTop: 8 }}>
      {sig?.signature || sig?.signatureImage ? (
        <img src={decrypt(sig?.signature) || decrypt(sig?.signatureImage)} alt="Staff Signature" style={{ maxHeight: 60, objectFit: "contain" }} />
      ) : sig?.signatureText ? (
        <Text fz={28} fw={400} ff="Island Moments" color="#171717">{sig?.signatureText}</Text>
      ) : null}
    </Box>
  );

  const renderSignature = () => {
    if (!noteType) return null;

    if (noteType?.name === "Incident Report") {
      const staffSignatures = (incidentNoteData?.staffInvolved || [])
        .map((id) => ({
          id,
          sig: allSignature?.find(
            ({ staff, admin }) => staff?.id === id || admin?.id === id
          ),
        }))
        .filter(({ sig }) => sig?.signature || sig?.signatureImage || sig?.signatureText);

      if (staffSignatures.length === 0) return null;

      // Same Grid > Grid.Col > detailsForm shell as every other Incident
      // Report section, so this card lines up with the ones above it.
      return (
        <Grid mt={5}>
          <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
            <Box className={classes.detailsForm}>
              <Text className={classes.incidentClassificationTitle}>
                <LuPenLine />
                STAFF SIGNATURES
              </Text>
              {staffSignatures.map(({ id, sig }) => renderSignatureBlock(sig, id))}
            </Box>
          </Grid.Col>
        </Grid>
      );
    }

    // Multi-select note types can carry several staff (held in multiStaff on
    // create/edit, or an array under "Staff Name" once saved). Show a signature
    // block for EACH selected staff, not just one. Falls back to the single
    // selected id for classic single-Select note types.
    const selectedStaffIds = useMultiStaff
      ? multiStaff
      : Array.isArray(form.values?.["Staff Name"])
      ? form.values["Staff Name"]
      : form.values?.["Staff Name"]
      ? [form.values["Staff Name"]]
      : [];

    const staffSignatures = selectedStaffIds
      .map((id) => ({
        id,
        sig: allSignature?.find(
          ({ staff, admin }) => staff?.id === id || admin?.id === id
        ),
      }))
      .filter(({ sig }) => sig?.signature || sig?.signatureImage || sig?.signatureText);

    if (staffSignatures.length === 0) return null;
    return (
      <Box style={{ padding: "16px", borderTop: "1px solid var(--app-border)", marginTop: 16 }}>
        <Text ff="Inter" fz={12} fw={600} c="var(--app-text-muted)" mb={8} style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {staffSignatures.length > 1 ? "Staff Signatures" : "Staff Signature"}
        </Text>
        {/* Signatures are dark ink strokes — keep a white chip on every theme */}
        {staffSignatures.map(({ id, sig }) => renderSignatureBlock(sig, id))}
      </Box>
    );
  };

  // Mantine's DateInput requires value to be a real Date instance or null —
  // form fields loaded from raw API data (or the "" initial value) are
  // sometimes plain strings/empty strings, which crashes DateInput internals
  // with "value.toISOString is not a function". Coerce defensively here.
  const toSafeDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const ReturnFormItem = (data, index, textArea = "") => {
    let { field: name, fieldType, placeholder, viewOnly } = data;
    if (
      textArea &&
      supportPlan !== "yes" &&
      essentialSupportToggle === "no" &&
      interventionPlanToggle === "no" &&
      data?.field === "Description" &&
      (noteType?.name === "Progress Note" ||
        noteType?.id === Essential_Support_Note)
    ) {
      return (
        <Box>
          <Box>
            <Text fz={13} fw={500} c="#344054" mb={7} ff="Inter">Description</Text>
            <Box className={classes.descTextareaWrapper}>
              <Textarea
                key={index}
                maxRows={10}
                minRows={10}
                placeholder={placeholder || "Enter a description..."}
                autosize={view || (viewOnly ? true : false) ? true : false}
                styles={{
                  wrapper: { border: "none", boxShadow: "none", padding: 0, borderRadius: 0 },
                  input: { border: "none", borderRadius: 0, background: "transparent", padding: "14px 16px", color: "var(--app-text)", fontSize: 14, fontWeight: 400, fontFamily: "Inter", lineHeight: 1.55 },
                }}
                {...form.getInputProps(name)}
                readOnly={view || (viewOnly ? true : false)}
              />
              {!view && isAiAccess && (
                <>
                  <AiActionButtons
                    onMicClick={() => handleMicStart("essential")}
                    onActionClick={(key) => handleDualCompare(key, "main")}
                    onSparklesClick={() => {
                      const errors = validatePromptFields();
                      if (Object.keys(errors).length > 0) {
                        setFormErrors(errors);
                        return;
                      }
                      setFormErrors({});
                      setAiActionsOpen((prev) => !prev);
                    }}
                    onQuickNotesClick={() => setQuickNotesTarget({ key: "main", getValue: () => form.values[name], setValue: (v) => form.setFieldValue(name, v) })}
                    quickNotesDisabled={quickNotesDisabled}
                    micDisabled={view || (existingRecord?.active === false && existingRecord?.paid === false)}
                    actionsDisabled={view || form.values.Description === "" || !form.values.Description}
                    aiActionsOpen={aiActionsOpen}
                  />
                  {comparePanel.open && comparePanel.context === "main" && (
                    <ComparePanel
                      action={comparePanel.action}
                      option1={comparePanel.option1}
                      option2={comparePanel.option2}
                      loading1={comparePanel.loading1}
                      loading2={comparePanel.loading2}
                      option1Model={comparePanel.option1Model}
                      option2Model={comparePanel.option2Model}
                      ollamaAvailable={comparePanel.ollamaAvailable}
                      onArrange={handleArrangeCompare}
                      onUse={(text, meta) => handleUseCompareOption(text, "main", meta)}
                      onCancel={handleCancelCompare}
                      onRegenerate={() => handleDualCompare(comparePanel.action, "main")}
                    />
                  )}
                  <QuickNotesPicker
                    opened={quickNotesTarget?.key === "main"}
                    close={() => setQuickNotesTarget(null)}
                    onInsert={handleQuickNotesInsert}
                    hideCarePlan={!hasCarePlan}
                  />
                </>
              )}
              {(formErrors.name || formErrors.facility) && (
                <Box px={12} py={6} className={classes.formErrorBox}>
                  {formErrors.name && (
                    <Text fz={12} c="red" ff="Inter">{formErrors.name}</Text>
                  )}
                  {formErrors.facility && (
                    <Text fz={12} c="red" ff="Inter">{formErrors.facility}</Text>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <RecordingControls
            isListening={isListening}
            recordingTime={recordingTime}
            onRedo={handleRedo}
            onCancel={handleRecordingCancel}
            onConfirm={handleRecordingConfirm}
            classes={classes}
          />
        </Box>
      );
    }

    // Show a red required-mark for any field whose custom[] entry has
    // priority: true (the Note Type Studio's "Required" toggle), plus
    // Incident Report's two hand-required fields (see onSave) which aren't
    // driven by that flag.
    const isIncidentRequiredField =
      noteType?.name === "Incident Report" &&
      (data?.field === "Description" ||
        data?.field === "Immediate Actions Taken");
    const isRequiredField = isIncidentRequiredField || !!data?.priority;
    const requiredMark = isRequiredField ? (
      <span className={classes.incidentRequiredMark}> *</span>
    ) : null;
    const labelNode = isRequiredField ? (
      <>
        {name}
        {requiredMark}
      </>
    ) : (
      name
    );

    let obj = {
      string: (
        <TextInput
          size="md"
          radius="md"
          id="reason"
          key={index}
          type="text"
          label={labelNode}
          className={classes.datePicker}
          // className={classes.essentialDiscriptionArea}
          {...form.getInputProps(name)}
          disabled={view || (viewOnly ? true : false)}
          placeholder={placeholder || `Enter response`}
        />
      ),
      "text area": (
        <>
          {data?.field === "Description" ? (
            <>
              <Text fz={14} fw={500} c="var(--app-text)" mb={8} ff="Inter">
                {name}
                {requiredMark}
              </Text>
              <Box
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  background: "var(--app-surface)",
                  overflow: "hidden",
                  boxShadow: "0 1px 2px rgba(16,24,40,.05)",
                  "&:focus-within": {
                    borderColor: "#7856ff",
                    boxShadow: "0 0 0 3px rgba(120,86,255,.16)",
                  },
                }}
              >
                <Textarea
                  id="enter-description"
                  key={index}
                  minRows={view || (viewOnly ? true : false) ? 7 : 7}
                  maxRows={view || (viewOnly ? true : false) ? 15 : 15}
                  autosize={view || (viewOnly ? true : false) ? true : false}
                  placeholder={placeholder || `Enter response`}
                  styles={{
                    wrapper: {
                      border: "none",
                      boxShadow: "none",
                      padding: 0,
                      borderRadius: !view && isAiAccess ? 0 : "0 0 12px 12px",
                    },
                    input: {
                      border: "none",
                      borderRadius: !view && isAiAccess ? 0 : "0 0 12px 12px",
                      background: "transparent",
                      padding: "14px 16px",
                      color: "var(--app-text)",
                      fontSize: 14,
                      fontWeight: 400,
                      fontFamily: "Inter",
                      lineHeight: 1.55,
                    },
                  }}
                  {...form.getInputProps(name)}
                  readOnly={view || viewOnly ? true : false}
                />
                {!view && isAiAccess && (
                  <>
                    <AiActionButtons
                      onMicClick={() => handleMicStart("essential")}
                      onActionClick={(key) => handleDualCompare(key, "main")}
                      onSparklesClick={() => {
                        const errors = validatePromptFields();
                        if (Object.keys(errors).length > 0) {
                          setFormErrors(errors);
                          return;
                        }
                        setFormErrors({});
                        setAiActionsOpen((prev) => !prev);
                      }}
                      onQuickNotesClick={() => setQuickNotesTarget({ key: "main", getValue: () => form.values[name], setValue: (v) => form.setFieldValue(name, v) })}
                      quickNotesDisabled={quickNotesDisabled}
                      micDisabled={view || (existingRecord?.active === false && existingRecord?.paid === false)}
                      actionsDisabled={view || !form.values.Description?.trim()}
                      aiActionsOpen={aiActionsOpen}
                    />
                    {comparePanel.open && comparePanel.context === "main" && (
                      <ComparePanel
                        action={comparePanel.action}
                        option1={comparePanel.option1}
                        option2={comparePanel.option2}
                        loading1={comparePanel.loading1}
                        loading2={comparePanel.loading2}
                        option1Model={comparePanel.option1Model}
                        option2Model={comparePanel.option2Model}
                        ollamaAvailable={comparePanel.ollamaAvailable}
                        onArrange={handleArrangeCompare}
                        onUse={(text, meta) => handleUseCompareOption(text, "main", meta)}
                        onCancel={handleCancelCompare}
                        onRegenerate={() => handleDualCompare(comparePanel.action, "main")}
                      />
                    )}
                    <QuickNotesPicker
                      opened={quickNotesTarget?.key === "main"}
                      close={() => setQuickNotesTarget(null)}
                      onInsert={handleQuickNotesInsert}
                      hideCarePlan={!hasCarePlan}
                    />
                  </>
                )}
                {(formErrors.name || formErrors.facility) && (
                  <Box px={12} py={6} className={classes.formErrorBox}>
                    {formErrors.name && (
                      <Text fz={12} c="red" ff="Inter">{formErrors.name}</Text>
                    )}
                    {formErrors.facility && (
                      <Text fz={12} c="red" ff="Inter">{formErrors.facility}</Text>
                    )}
                  </Box>
                )}
              </Box>
            </>
          ) : (
            <Textarea
              id={
                data?.field === "Medication"
                  ? "enter-medication"
                  : "enter-note"
              }
              key={index}
              label={labelNode}
              minRows={view || (viewOnly ? true : false) ? 7 : 7}
              maxRows={view || (viewOnly ? true : false) ? 15 : 15}
              autosize={view || (viewOnly ? true : false) ? true : false}
              placeholder={placeholder || `Enter response`}
              className={`${classes.essentialDiscriptionArea} ${
                view || (viewOnly ? true : false) ? classes.disabledTextarea : ""
              }`}
              radius="md"
              {...form.getInputProps(name)}
              readOnly={view || viewOnly ? true : false}
            />
          )}

          {data?.field === "Description" ? (
            <>
              <RecordingControls
                isListening={isListening}
                recordingTime={recordingTime}
                onRedo={handleRedo}
                onCancel={handleRecordingCancel}
                onConfirm={handleRecordingConfirm}
                classes={classes}
              />
              {/* {isListening && (
            <Box className={classes.waveSection}>
              <Box className={classes.wave}>
                <WaveformVisualizer isActive={isListening} />
              </Box>

              <Box className={classes.waveButtons}>
                <Button
                  variant="light"
                  color="gray"
                  radius={"xl"}
                  size="xs"
                  onClick={handleRedo}
                >
                  <Flex align={"center"} gap={5}>
                    <RecordingRedoSvg />
                    <Text c={"var(--app-text)"}>Redo</Text>
                  </Flex>
                </Button>
                <Flex align={"center"} gap={5}>
                  <UnstyledButton
                    variant="outline"
                    color="gray"
                    onClick={handleRecordingCancel}
                  >
                    <RecordingCancelSvg />
                  </UnstyledButton>
                  <Text size="sm" mt="sm" color="dimmed" pb={15}>
                    {formatTime(recordingTime)}
                  </Text>
                  <UnstyledButton onClick={handleRecordingConfirm}>
                    <RecordingTickMarkSvg />
                  </UnstyledButton>
                </Flex>
              </Box>
            </Box>
          )} */}
            </>
          ) : null}
        </>
      ),

      number: (
        <Flex direction={"column"}>
          {isBilling_Services ? (
            <>
              <NumberInput
                size="md"
                radius="md"
                key={index}
                label={labelNode}
                hideControls
                className={classes.input}
                {...form.getInputProps(name)}
                disabled={view || (viewOnly ? true : false) || !serviceNumber}
                placeholder={placeholder || `Enter response`}
              />
              {isBilling_Services && (
                <Text fz={12} fs="italic" c="var(--app-text-faint, #8A93A2)">
                  Approved Units: {serviceNumber?.servicenumber}
                </Text>
              )}
            </>
          ) : (
            <NumberInput
              size="md"
              radius="md"
              key={index}
              label={labelNode}
              hideControls
              className={classes.input}
              {...form.getInputProps(name)}
              disabled={view || (viewOnly ? true : false)}
              placeholder={placeholder || `Enter response`}
            />
          )}
        </Flex>
      ),

      time: (
        <TimeInput
          size="md"
          radius="md"
          label={labelNode}
          key={index}
          ref={ref}
          placeholder={`Select ${name}`}
          className={classes.datePicker}
          icon={
            <img
              className={classes.clockIcon}
              src={"/assets/images/clock.png"}
              alt="Icon"
            />
          }
          {...form.getInputProps(name)}
          onChange={(e) => {
            const isSafari = /^((?!chrome|android).)*safari/i.test(
              navigator.userAgent
            );
            const datetime = new Date(e.target.value).toLocaleTimeString(
              "en-US",
              { hour12: false }
            );
            let time = e.target.value.split(":")?.slice(0, 2)?.join(":");
            isSafari
              ? form.setFieldValue(name, time)
              : form.setFieldValue(name, e.target.value);
          }}
          disabled={view || (viewOnly ? true : false)}
        />
      ),
      date: (
        <DateInput
          size="md"
          radius="md"
          key={index}
          label={labelNode}
          id="date-click"
          icon={<DateSvg />}
          className={classes.datePicker}
          {...form.getInputProps(name)}
          value={toSafeDate(form.getInputProps(name)?.value)}
          placeholder={`Select ${name}`}
          disabled={view || (viewOnly ? true : false)}
          maxDate={isBilling_Services ? today : null}
        />
      ),
      Clients: noteType?.name === "Incident Report" ? (
        <Box>
          <MultiSelect
            size="md"
            radius="md"
            searchable
            label="Client(s) Involved"
            className={classes.gcMultiSelect}
            classNames={{
              value: classes.gcMsPill,
              defaultValueRemove: classes.gcMsPillRemove,
              item: classes.gcMsItem,
            }}
            placeholder={selectedFacility ? "Select client(s)..." : "Select a facility first"}
            rightSection={icon}
            rightSectionPointerEvents="none"
            value={incidentNoteData.clientsInvolved || []}
            onChange={(val) => {
              const clientOptions = allPatients
                ?.filter(
                  (_p) =>
                    _p?.active === true &&
                    _p?.deleted === false &&
                    _p?.facilityId?.some((f) => f?.id === selectedFacility)
                )
                ?.map((p) => p?.id) || [];
              if (val.includes("ALL")) {
                setIncidentNoteData((prev) => ({ ...prev, clientsInvolved: clientOptions }));
              } else {
                setIncidentNoteData((prev) => ({ ...prev, clientsInvolved: val }));
              }
            }}
            withAsterisk
            data={[
              { value: "ALL", label: "Select All" },
              ...(allPatients
                ?.filter(
                  (_p) =>
                    _p?.active === true &&
                    _p?.deleted === false &&
                    _p?.facilityId?.some((f) => f?.id === selectedFacility)
                )
                ?.map((opt) => ({ value: opt?.id, label: opt?.name })) || []),
            ]}
            disabled={view || (edit && !saveAsNew) || (viewOnly || (userId && !autoRestoreDraft) ? true : false) || !selectedFacility}
            maxDropdownHeight={240}
          />
        </Box>
      ) : showGroupClientPicker ? (
        <Box>
          <MultiSelect
            size="md"
            radius="md"
            searchable
            label={name}
            className={classes.gcMultiSelect}
            classNames={{
              value: classes.gcMsPill,
              defaultValueRemove: classes.gcMsPillRemove,
              item: classes.gcMsItem,
            }}
            placeholder={selectedFacility ? "Select clients..." : "Select a facility first"}
            rightSection={icon}
            rightSectionPointerEvents="none"
            value={selectedGroupClients}
            onChange={setSelectedGroupClients}
            withAsterisk
            data={
              // Opened from a specific client's profile to ADD or DUPLICATE a
              // note — lock the options to just that client (same treatment
              // as Incident Report's Client(s) Involved field above). When
              // EDITING an existing note, don't restrict: the saved note may
              // already have other clients attached (e.g. other group-session
              // participants), so the full facility roster must stay
              // available or those already-selected chips can't render.
              userId && !autoRestoreDraft && (!edit || saveAsNew)
                ? allPatients
                    ?.filter((_p) => _p?.id === userId)
                    ?.map((opt) => ({ value: opt?.id, label: opt?.name })) || []
                : allPatients
                    ?.filter(
                      (_p) =>
                        _p?.active === true &&
                        _p?.deleted === false &&
                        _p?.facilityId?.some((f) => f?.id === selectedFacility)
                    )
                    ?.map((opt) => ({ value: opt?.id, label: opt?.name })) || []
            }
            disabled={view || (viewOnly || (userId && !autoRestoreDraft) ? true : false) || !selectedFacility}
            maxDropdownHeight={240}
          />
          {!(userId && !autoRestoreDraft) && (
            <Flex className={classes.gcHint}>
              <IconInfoCircle size={14} />
              All clients who participated in this group session. Unselect anyone who did not attend.
            </Flex>
          )}
        </Box>
      ) : useMultiClient ? (
        <Box>
          <MultiSelect
            size="md"
            radius="md"
            searchable
            label={name}
            className={classes.gcMultiSelect}
            classNames={{
              value: classes.gcMsPill,
              defaultValueRemove: classes.gcMsPillRemove,
              item: classes.gcMsItem,
            }}
            placeholder={`Select ${name}`}
            rightSection={icon}
            rightSectionPointerEvents="none"
            value={multiClients}
            onChange={(val) => onClientsChange(val, name)}
            withAsterisk
            data={
              // Opened from a specific client's profile to ADD or DUPLICATE a
              // note — lock the options to just that client (same treatment
              // as Group Note above). When EDITING an existing note, don't
              // restrict: the saved note may already have other clients
              // attached, so the full roster must stay available or those
              // already-selected chips can't render.
              userId && !autoRestoreDraft && (!edit || saveAsNew)
                ? allPatients
                    ?.filter((_p) => _p?.id === userId)
                    ?.map((opt) => ({ value: opt?.id, label: opt?.name })) || []
                : [
                    { value: "ALL", label: "Select All" },
                    ...(allPatients
                      ?.filter((_p) => _p?.active === true && _p?.deleted === false)
                      ?.map((opt) => ({ value: opt?.id, label: opt?.name })) || []),
                  ]
            }
            disabled={view || (edit && !saveAsNew) || (viewOnly || (userId && !autoRestoreDraft) ? true : false)}
            maxDropdownHeight={240}
          />
        </Box>
      ) : (
        <Select
          size="md"
          radius="md"
          searchable
          label={name}
          className={classes.select}
          rightSection={icon}
          defaultValue={form?.getInputProps(name)}
          id={"client-name-note"}
          {...form.getInputProps(name)}
          placeholder={`Select ${name}`}
          rightSectionPointerEvents="none"
          value={selectedPatient?.id}
          required={viewOnly || (userId && !autoRestoreDraft) ? false : true}
          onChange={(v) => handlePatientChange(v, name)}
          data={allPatients
            ?.filter(
              (_p) =>
                _p?.active === true &&
                _p?.deleted === false &&
                // Once a facility is chosen, constrain the resident list to
                // that facility — selecting a resident from a different
                // facility crashed the care-plan handlers (no matching plan →
                // undefined destructure). Before a facility is picked, show
                // all residents so the resident-first selection flow still
                // works. Mirrors the Group Note / Incident Report pickers.
                (!selectedFacility ||
                  _p?.facilityId?.some((f) => f?.id === selectedFacility))
            )
            ?.map((opt) => ({
              value: opt?.id,
              label: opt?.name,
            }))}
          disabled={view || (edit && !saveAsNew) || (viewOnly || (userId && !autoRestoreDraft) ? true : false)}
        />
      ),
      Provider: (
        <Select
          size="md"
          radius={"md"}
          className={classes.select}
          searchable
          label={labelNode}
          rightSection={icon}
          defaultValue={form?.getInputProps(name)}
          id={"client-name-note"}
          {...form.getInputProps(name)}
          placeholder={`Select ${name}`}
          rightSectionPointerEvents="none"
          onChange={(v) => handlePatientChange(v, name)}
          value={selectedPatient?.providerId?.[0]?.id || ""}
          data={
            selectedPatient?.providerId?.length > 0
              ? selectedPatient?.providerId?.map((_p) => ({
                  value: _p?.id,
                  label: _p?.name,
                }))
              : []
          }
          disabled={true}
        />
      ),
      Facilities: (
        <Select
          size="md"
          radius="md"
          // selectTall lifts this field to the Client picker's height — the two
          // share a Grid row and a plain `select` is 8px shorter (see styles.js).
          className={`${classes.select} ${classes.selectTall}`}
          searchable
          label={name}
          rightSection={icon}
          {...form.getInputProps(name)}
          placeholder={`Select ${name}`}
          onChange={(e) => handleFacilityChange(e, name)}
          rightSectionPointerEvents="none"
          disabled={
            noteType?.name === "Incident Report"
              ? view || (edit && !saveAsNew)
              : view || (viewOnly ? true : false)
          }
          required={viewOnly || userId ? false : true}
          data={getOptions(
            "Facilities",
            noteType?.custom,
            undefined,
            view ? true : false
          )?.map((opt) => {
            if ((isGroupNoteCreating && !userId) || (noteType?.name === "Incident Report" && !userId)) return opt;
            // Multiple clients on the note → enable the UNION of every
            // selected client's assigned facilities, not just one client's.
            if (isMultiClientSelected) {
              const allowed = multiClients?.some((cid) => {
                const p = allPatients?.find((_p) => _p?.id === cid);
                const facs = Array.isArray(p?.facilityId)
                  ? p.facilityId
                  : p?.facilityId
                  ? [p.facilityId]
                  : [];
                return facs?.some((fac) => fac?.id == opt?.value);
              });
              return allowed ? opt : { ...opt, disabled: true };
            }
            // Group Note doesn't drive `selectedPatient` (it uses the
            // clients MultiSelect, not the single-client Select that calls
            // handlePatientChange), so look the scanned/opened client up
            // directly by userId instead of relying on selectedPatient.
            const facilityScopePatient = isGroupNoteCreating
              ? allPatients?.find((p) => p?.id === userId)
              : selectedPatient;
            if (Array.isArray(facilityScopePatient?.facilityId)) {
              if (
                facilityScopePatient?.facilityId?.find(
                  (fac) => fac?.id == opt?.value
                )
              ) {
                return opt;
              } else {
                return { ...opt, disabled: true };
              }
            } else {
              if (facilityScopePatient?.facilityId?.id == opt?.value) {
                return opt;
              } else {
                return { ...opt, disabled: true };
              }
            }
          })}
        />
      ),
      Staff: (
        <Box className={classes.staffContainer}>
          {/* <Text fw={500}>{name}:</Text> */}
          {noteType?.name === "Incident Report" ? (
            <MultiSelect
              size="md"
              radius="md"
              searchable
              label="Staff & Witnesses Present"
              className={classes.gcMultiSelect}
              classNames={{
                value: classes.incidentStaffPill,
                defaultValueRemove: classes.incidentStaffPillRemove,
                item: classes.incidentStaffItem,
              }}
              placeholder="Select staff & witnesses"
              rightSection={icon}
              rightSectionPointerEvents="none"
              value={incidentNoteData.staffInvolved || []}
              onChange={(val) => {
                const staffOptions = getOptions("Staff", noteType?.custom)?.filter(
                  (opt) => staff?.staffs?.find((s) => s?.id === opt?.value)?.type !== "contractor"
                );
                if (val.includes("ALL")) {
                  setIncidentNoteData((prev) => ({
                    ...prev,
                    staffInvolved: staffOptions?.map((opt) => opt?.value) || [],
                  }));
                } else {
                  setIncidentNoteData((prev) => ({ ...prev, staffInvolved: val }));
                }
              }}
              withAsterisk
              data={[
                { value: "ALL", label: "Select All" },
                ...(getOptions("Staff", noteType?.custom)?.filter((opt) => staff?.staffs?.find((s) => s?.id === opt?.value)?.type !== "contractor") || []),
              ]}
              // An incident usually involves more than the person filing it, so
              // staff can add the other staff and witnesses who were present —
              // they are not limited to themselves.
              disabled={view || (viewOnly ? true : false)}
              maxDropdownHeight={240}
            />
          ) : useMultiStaff ? (
            <MultiSelect
              size="md"
              radius="md"
              searchable
              label={name}
              className={classes.gcMultiSelect}
              classNames={{
                value: classes.incidentStaffPill,
                defaultValueRemove: classes.incidentStaffPillRemove,
                item: classes.incidentStaffItem,
              }}
              placeholder={`Select ${name}`}
              rightSection={icon}
              rightSectionPointerEvents="none"
              value={multiStaff}
              onChange={(val) => {
                const staffOptions = getOptions("Staff All", noteType?.custom)?.filter(
                  (opt) => {
                    const s = allStaff?.find((st) => st?.id === opt?.value);
                    return s?.active === true && s?.deleted !== true && s?.type !== "contractor";
                  }
                );
                if (val.includes("ALL")) {
                  setMultiStaff(staffOptions?.map((opt) => opt?.value) || []);
                } else {
                  setMultiStaff(val);
                }
              }}
              withAsterisk
              data={[
                { value: "ALL", label: "Select All" },
                ...(getOptions("Staff All", noteType?.custom)?.filter(
                  (opt) => {
                    const s = allStaff?.find((st) => st?.id === opt?.value);
                    return s?.active === true && s?.deleted !== true && s?.type !== "contractor";
                  }
                ) || []),
              ]}
              disabled={view}
              maxDropdownHeight={240}
            />
          ) : view ? (
            <Select
              width={"100%"}
              size="md"
              className={classes.select}
              radius="md"
              rightSection={icon}
              label={labelNode}
              rightSectionPointerEvents="none"
              data={getOptions("Staff All", noteType?.custom)?.filter((opt) => allStaff?.find((s) => s?.id === opt?.value)?.type !== "contractor")}
              placeholder={`Select ${name}`}
              searchable
              defaultValue={form?.getInputProps(name)}
              {...form.getInputProps(name)}
              disabled={true}
            />
          ) : edit ? (
            <Box className={classes.staffOnEdit}>
              <Grid
                gutter={14}
                // cols={isMobileScreen ? 1 : 3}
                className={classes.mwd}
                pb={30}
              >
                {!isAdmin && (
                  <Grid.Col span={12}>
                    {/* for staff only */}
                    <Select
                      width={"100%"}
                      size="md"
                      className={classes.select}
                      radius="md"
                      label={labelNode}
                      rightSection={icon}
                      rightSectionPointerEvents="none"
                      data={getOptions("Staff All", noteType?.custom)?.filter((opt) => allStaff?.find((s) => s?.id === opt?.value)?.type !== "contractor")}
                      placeholder={`Select ${name}`}
                      searchable
                      defaultValue={form?.getInputProps(name)}
                      {...form.getInputProps(name)}
                      disabled={true}
                    />
                  </Grid.Col>
                )}

                {isAdmin && (
                  <>
                    {/* for both main Admin & role with Admin */}
                    {!isGroupNote && (
                      <Grid.Col span={isMobileScreen ? 12 : 6}>
                        <Select
                          width={"100%"}
                          size="md"
                          className={classes.select}
                          radius="md"
                          label={labelNode}
                          rightSection={icon}
                          rightSectionPointerEvents="none"
                          data={getOptions("Staff All", noteType?.custom)?.filter((opt) => allStaff?.find((s) => s?.id === opt?.value)?.type !== "contractor")}
                          placeholder={`Select ${name}`}
                          searchable
                          defaultValue={form?.getInputProps(name)}
                          {...form.getInputProps(name)}
                          disabled={true}
                        />
                      </Grid.Col>
                    )}

                    <Grid.Col span={isGroupNote || isMobileScreen ? 12 : 6}>
                      <Select
                        size="md"
                        radius="md"
                        label={labelNode}
                        rightSection={icon}
                        className={classes.select}
                        rightSectionPointerEvents="none"
                        data={getOptions("Staff", noteType?.custom)?.filter((opt) => staff?.staffs?.find((s) => s?.id === opt?.value)?.type !== "contractor")}
                        placeholder={`Select New ${name}`}
                        searchable
                        defaultValue={form?.getInputProps(name)}
                        {...form.getInputProps(name)}
                        disabled={
                          view ||
                          (viewOnly
                            ? true
                            : !edit && profile?.user?.type === "staff"
                            ? true
                            : false)
                        }
                      />
                    </Grid.Col>
                  </>
                )}

                {/* <Grid.Col span={isMobileScreen ? 12 : 6}>
                    <Select
                      width={"100%"}
                      size="md"
                      className={classes.select}
                      radius="md"
                      label={labelNode}
                      rightSection={icon}
                      rightSectionPointerEvents="none"
                      data={getOptions("Staff All", noteType?.custom)?.filter((opt) => allStaff?.find((s) => s?.id === opt?.value)?.type !== "contractor")}
                      placeholder={`Select ${name}`}
                      searchable
                      defaultValue={form?.getInputProps(name)}
                      {...form.getInputProps(name)}
                      disabled={true}
                    />
                  </Grid.Col>
                  <Grid.Col span={isMobileScreen ? 12 : 6}>
                    <Select
                      size="md"
                      radius="md"
                      label={labelNode}
                      rightSection={icon}
                      className={classes.select}
                      rightSectionPointerEvents="none"
                      data={getOptions("Staff", noteType?.custom)?.filter((opt) => staff?.staffs?.find((s) => s?.id === opt?.value)?.type !== "contractor")}
                      placeholder={`Select New ${name}`}
                      searchable
                      defaultValue={form?.getInputProps(name)}
                      {...form.getInputProps(name)}
                      disabled={
                        view ||
                        (viewOnly
                          ? true
                          : !edit &&
                            profile?.user?.type === "staff" &&
                            profile?.user?.role !== "admin"
                          ? true
                          : false)
                      }
                      // disabled={
                      //   view ||
                      //   (viewOnly
                      //     ? true
                      //     : !edit && profile?.user?.type === "staff"
                      //     ? true
                      //     : false)
                      // }
                    />
                  </Grid.Col> */}
              </Grid>
            </Box>
          ) : (
            <Select
              size="md"
              radius="md"
              label={labelNode}
              className={classes.select}
              rightSection={icon}
              rightSectionPointerEvents="none"
              data={getOptions("Staff", noteType?.custom)?.filter((opt) => staff?.staffs?.find((s) => s?.id === opt?.value)?.type !== "contractor")}
              placeholder={`Select ${name}`}
              searchable
              defaultValue={form?.getInputProps(name)}
              {...form.getInputProps(name)}
              disabled={
                view ||
                (viewOnly
                  ? true
                  : !edit &&
                    profile?.user?.type === "staff" &&
                    profile?.user?.role !== "admin"
                  ? true
                  : false)
              }
              // disabled={
              //   view ||
              //   (viewOnly
              //     ? true
              //     : !edit && profile?.user?.type === "staff"
              //     ? true
              //     : false)
              // }
            />
          )}
        </Box>
      ),
      "Facility Types": (
        <Select
          size="xs"
          searchable
          label={labelNode}
          variant="filled"
          rightSection={icon}
          className={classes.select}
          {...form.getInputProps(name)}
          placeholder={`Select ${name}`}
          rightSectionPointerEvents="none"
          disabled={view || (viewOnly ? true : false)}
          data={getOptions("Facility Types", noteType?.custom)}
        />
      ),
      Other: (
        <Select
          size="md"
          radius="md"
          searchable
          className={classes.select}
          label={labelNode}
          id="enter-shift"
          rightSection={icon}
          {...form.getInputProps(name)}
          placeholder={`Select ${name}`}
          rightSectionPointerEvents="none"
          disabled={view || (viewOnly ? true : false)}
          data={getOptions("Other", noteType?.custom, name)}
        />
      ),
      "Multiple Choice": (
        <Checkbox.Group
          label={labelNode}
          id="enter-shift"
          {...form.getInputProps(name)}
        >
          {getOptions("multiple choices", noteType?.custom, name)?.map(
            (option) => (
              <Checkbox
                color="gray"
                key={option?.value}
                value={option?.value}
                label={option?.label}
                disabled={view || (viewOnly ? true : false)}
                mt={index === 0 ? "10px" : "10px"}
              />
            )
          )}
        </Checkbox.Group>
      ),
      "Single Choice": (
        <Radio.Group
          label={labelNode}
          id="enter-shift"
          {...form.getInputProps(name)}
        >
          {getOptions("single choice", noteType?.custom, name)?.map(
            (option) => (
              <Radio
                disabled={view || (viewOnly ? true : false)}
                color="gray"
                key={option?.value}
                value={option?.value}
                label={option?.label}
                mt={index === 0 ? "10px" : "10px"}
              />
            )
          )}
        </Radio.Group>
      ),
      Dropdown: (
        <Select
          size="md"
          radius="md"
          searchable
          className={classes.select}
          label={labelNode}
          id="enter-shift"
          rightSection={icon}
          {...form.getInputProps(name)}
          placeholder={`Select ${name}`}
          rightSectionPointerEvents="none"
          disabled={view || (viewOnly ? true : false)}
          data={getOptions("dropdown", noteType?.custom, name)}
        />
      ),
      "Medication Records": (
        <Group align="flex-end">
          <Select
            rightSection={icon}
            size="xs"
            searchable
            label={labelNode}
            data={medicationOptions}
            className={classes.select}
            {...form.getInputProps(name)}
            placeholder={`Select ${name}`}
            disabled={view || (viewOnly ? true : false)}
          />
          <Popover
            shadow="md"
            width={300}
            position="bottom-end"
            className={classes.popoverInfoTarget}
          >
            <Popover.Target>
              <IconInfoCircle
                color="gray"
                styles={{ marginTop: "30px !important" }}
              />
            </Popover.Target>
            <Popover.Dropdown sx={{ pointerEvents: "none" }}>
              <Textarea
                key={`${index}-test`}
                radius="md"
                autosize={true}
                icon={
                  <img
                    className={classes.descriptionIcon}
                    src="/assets/images/home2.png"
                    alt="home"
                  />
                }
                placeholder={
                  placeholder || `Select ${name} to see details here.`
                }
                className={`${classes.myfont} ${classes.descriptionText} ${classes.medicationInfoTextarea}`}
                value={form.values[name]}
                disabled={true}
              />
            </Popover.Dropdown>
          </Popover>
        </Group>
      ),
      "Support Plan": (
        <Group align="flex-end">
          <Select
            size="xs"
            searchable
            label={labelNode}
            data={supportOptions}
            className={classes.select}
            {...form.getInputProps(name)}
            placeholder={`Select ${name}`}
            disabled={view || (viewOnly ? true : false)}
          />
          <Popover
            width={300}
            shadow="md"
            position="bottom-end"
            className={classes.popoverInfoTarget}
          >
            <Popover.Target>
              <IconInfoCircle
                color="gray"
                styles={{ marginTop: "30px !important" }}
              />
            </Popover.Target>
            <Popover.Dropdown sx={{ pointerEvents: "none" }}>
              <Textarea
                key={`${index}-test`}
                radius="md"
                autosize={true}
                icon={
                  <img
                    className={classes.descriptionIcon}
                    src="/assets/images/home2.png"
                    alt="home"
                  />
                }
                placeholder={
                  placeholder || `Select ${name} to see details here.`
                }
                className={`${classes.myfont} ${classes.descriptionText} ${classes.medicationInfoTextarea}`}
                value={form.values[name]}
                disabled={true}
              />
            </Popover.Dropdown>
          </Popover>
        </Group>
      ),
      "level of need": (
        <Select
          size="md"
          radius="md"
          searchable
          className={classes.select}
          label={labelNode}
          id="enter-shift"
          rightSection={icon}
          {...form.getInputProps(name)}
          placeholder={`Select ${name}`}
          rightSectionPointerEvents="none"
          disabled={view || (viewOnly ? true : false)}
          data={getOptions("level of need", noteType?.custom)}
        />
      ),
    };

    if (
      (essentialSupportToggle === "yes" || interventionPlanToggle === "yes") &&
      name === "Description" &&
      textArea
    )
      return null;
    else if (!textArea && fieldType?.name != "text area")
      return obj[fieldType?.name];
    else if (textArea && fieldType?.name === "text area")
      return obj[fieldType?.name];
    else return null;
    // return obj[fieldType?.name]
  };

  const showRadioButtons =
    (noteType?.name === "Incident Report" &&
      noteType?.name === "Medication Note") ||
    noteType?.name === "Progress Note" ||
    noteType?.custom?.find((_c) => _c?.field === "Support Plan");

  const handleToggleChange = (e, type) => {
    setAiActionsOpen(false);
    setComparePanel(COMPARE_PANEL_RESET);
    setEssentialSupportErrors([]);

    if (type === "interventionPlan") {
      setInterventionPlanToggle(e);
    } else {
      setEssentialSupportToggle(e);
      setAiAssistantCardOpened(false);
      setMessages([]);
      form.setFieldValue("Description", "");
    }
  };

  const validatePromptFields = () => {
    const errors = {};

    const missingClient =
      noteType?.name === "Incident Report"
        ? !incidentNoteData.clientsInvolved?.length
        : isGroupNoteCreating
        ? selectedGroupClients.length === 0
        : !selectedPatient?.name;
    if (missingClient) {
      errors.name =
        noteType?.name === "Incident Report" || isGroupNoteCreating
          ? "Please select at least one client."
          : "Client name is required.";
    }

    // const hasNoteDescription = !!noteDetails?.Description;
    // const hasDescriptionsList = descriptions?.some(
    //   (item) => !!item?.description
    // );
    // const hasSupportDescription = !!essentialSupport?.description;

    // if (!hasNoteDescription && !hasDescriptionsList && !hasSupportDescription) {
    //   errors.description = "Description is required.";
    // }


    

    if (!edit && !facilityDetials?.name) {
      errors.facility = "Facility Name is required.";
    }

      if (edit && !facilityDetials) {
      errors.facility = "Facility Name is required.";
    }

    return errors;
  };
  const handleAiAssistantSubmit = (values) => {
    if (values !== "custom-description") {
      const errors = validatePromptFields();
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
    }
    setFormErrors({});
    // if (!messageValue) return;
    // setVisible(false);
    // sendMessageToOpenAI(messageValue);
    setMessageValue("");
    // if(tokensUsage)
    checkTokenUsageWarning(existingRecord, values);
  };

  useEffect(() => {
    form.setFieldValue("noteid", noteType);
  }, [noteType]);

  const noteTypeApproval = noteType?.adminApprovals?.find(
    (item) => item?.admin === profile?.user?.admin
  )
    ? noteType?.adminApprovals?.find(
        (item) => item?.admin === profile?.user?.admin
      )?.status
    : noteType?.approvalRequired;

  // Incident Reports always go to a Supervisor — the API forces them to
  // 'pending' whatever the note type's approval settings say — so they get
  // their own confirmation copy. Every other note type is unchanged.
  const isIncidentReportType = noteType?.name === "Incident Report";

  // const isStaffWithoutApproval =
  // profile?.user?.type === "staff" && !newAdminApproval;

  return (
    <>
      {/* -----> Pending Modal <----- */}

      {noteType && (
        <CustomModal
          modalState={pendingModal}
          modalTopImg={pendingModalIcon}
          modalTitle={
            isIncidentReportType
              ? "Pending Supervisor Approval"
              : noteTypeApproval
              ? "Pending Approval"
              : "Note Successfully Saved"
          }
          modalTextOne={
            isIncidentReportType
              ? `The Incident Report has been submitted for Supervisor approval.`
              : noteTypeApproval
              ? `The note has been submitted for admin approval.`
              : null
          }
          modalTextTwo={
            isIncidentReportType
              ? `A Supervisor will review and sign off on it — you'll be`
              : noteTypeApproval
              ? `Please check back to see if it was approved or`
              : null
          }
          modalTextThree={
            isIncidentReportType
              ? "notified of the outcome."
              : noteTypeApproval
              ? "denied."
              : null
          }
          closeModal={setPendingModal}
          colorOne={"yellow"}
          variantOne={"filled"}
          btnTextOne="OK"
          noteType={noteType}
          setNoteType={setNoteType}
        />
      )}

      {/* -----> Note Editid <----- */}

      <CustomModal
        modalState={editModal}
        modalTopImg={pendingModalIcon}
        modalTitle={"Note Edited"}
        modalText={"Note has been edited successfully."}
        closeModal={setEditModal}
        colorOne={"yellow"}
        variantOne={"filled"}
        btnTextOne="OK"
      />

      <Drawer
            shadow="md"
            size={isMobileScreen ? "100%" : 700}
            zIndex={1000}
            opened={opened}
            title={
              view
                ? "View"
                : saveAsNew
                ? "Duplicate Note"
                : edit
                ? "Edit"
                : "Add Note"
            }
            overlayProps={{ blur: 3 }}
            className={classes.mymodal}
            closeOnClickOutside={false}
            position="right"
            onClose={async () => {
              // Save draft before resetting — mirrors the periodic auto-save's
              // own per-note-type clientId/facilityId derivation and extra
              // snapshot keys (groupClientIds / multiClientIds / multiStaffIds)
              // so a Group Note or multi-select note's full selection isn't
              // lost when the drawer is closed via the X icon or an outside
              // click instead of the 3s auto-save interval catching it first.
              // Awaited (not fire-and-forget) so resetForm/cancelForm below —
              // which end up calling the parent's close(), which refetches
              // the "All" tab's notes — don't race ahead of this PUT and
              // read stale data missing the just-saved draft.
              if (!edit && !view && autoSaveDraftsEnabled) {
                const ntId = NOTETYPE || noteType?.id;
                const isIncidentReportClose = noteType?.name === "Incident Report";
                const cId = isGroupNote
                  ? selectedGroupClients
                  : isIncidentReportClose
                  ? incidentNoteData.clientsInvolved
                  : isMultiSelectNoteType
                  ? multiClients
                  : selectedPatient?.id || userId;
                const fId = isGroupNote || isIncidentReportClose
                  ? selectedFacility
                  : selectedPatient?.facilityId?.id || form?.values?.["Facility Name"];
                const hasClientClose = Array.isArray(cId) ? cId.length > 0 : !!cId;
                if (ntId && hasClientClose && fId) {
                  await saveNoteDraft({
                    noteTypeId: ntId,
                    clientId: cId,
                    facilityId: fId,
                    formState: { fields: { ...form.values }, incidentDetails: isIncidentReportClose ? incidentNoteData : undefined, groupClientIds: isGroupNote ? selectedGroupClients : undefined, multiClientIds: isMultiSelectNoteType ? multiClients : undefined, multiStaffIds: isMultiSelectNoteType ? multiStaff : undefined, descriptions, essentialSupport, essentialSupportCount, essentialSupportType, essentialSupportNumber, essentialNumber, activities, frequencyOptions, outComeTitle, outcomeNumbers, goals, goalItems, goalOptions, goalNumber, objectivesByGoal, interventionsByGoal, supportPlan, essentialSupportToggle, interventionPlanToggle },
                  }).catch(() => {});
                }
              }
              resetForm();
              // Closing via the drawer's own X icon or an outside click must
              // reset exactly like the Cancel button (resetForm + cancelForm)
              // — previously only resetForm() ran here, so state like
              // form.reset(), essential-support/goals arrays, AI/behavior
              // cards, and the recording state were left stale.
              cancelForm();
              setSelectedPatient(null);
              setSelectedFacility(null);
              setNoteType(null);
              setOpenedAccordion("0");
              setEssentialAccordion("0");
              setFacilityDetials(null);
              setStaffDetials(null);
              // setView(false);
            }}
          >
            <form
              onSubmit={form?.onSubmit((values) => onSave(values))}
              className={classes.formStyling}
            >
              {/* Auto-save indicator — sticky centered */}
              {!edit && !view && draftStatus !== "idle" && !pendingDraft && (
                <Box className={classes.draftSavingIndicator}>
                  <Box className={classes.draftSavingPill}>
                    <Box className={classes.draftSavingDot} />
                    <Text ff="Inter" fz={11} fw={500} c="var(--app-text)">
                      {draftStatus === "saving" && "Saving..."}{draftStatus === "saved" && "Saved"}{draftStatus === "restored" && "Restored"}
                    </Text>
                  </Box>
                </Box>
              )}

              {/* Draft loading — shown while fetching/restoring when opened from Drafts tab */}
              {autoRestoreDraft && draftFetching && !edit && !view && (
                <Box
                  className={classes.draftBanner}
                  style={{ marginLeft: isMobileScreen ? -12 : -24, marginRight: isMobileScreen ? -12 : -24 }}
                >
                  <Flex direction="column" align="center" gap={8}>
                    <Loader color="yellow" size="sm" />
                    <Text ff="Inter" fz={13} fw={500} c="var(--app-text-muted)">Loading your draft...</Text>
                  </Flex>
                </Box>
              )}

              {/* Draft recovery banner — only shown when NOT in auto-restore mode */}
              {pendingDraft && !autoRestoreDraft && !edit && !view && (
                <Box
                  className={classes.draftBanner}
                  style={{ marginLeft: isMobileScreen ? -12 : -24, marginRight: isMobileScreen ? -12 : -24 }}
                >
                  <Flex direction="column" align="center" gap={4}>
                    <Box className={classes.draftRecoveryIcon}>
                      <IconPencil size={20} color="var(--app-primary)" stroke={2} />
                    </Box>
                    <Text ff="Inter" fz={15} fw={700} c="var(--app-text)">Unsaved Draft Found</Text>
                    <Text ff="Inter" fz={12} fw={400} c="var(--app-text-muted)" mb={8}>Pick up where you left off or start fresh</Text>
                    <Flex gap={10}>
                      <Button radius={8} ff="Inter" fz={14} fw={600}
                        leftIcon={<IconPencil size={15} />}
                        className={classes.draftContinueBtn}
                        onClick={restoreDraft}>
                        Continue editing
                      </Button>
                      <Button radius={8} ff="Inter" fz={14} fw={600}
                        leftIcon={<IconTrash size={15} />}
                        className={classes.draftDiscardBtn}
                        onClick={() => setPendingDraft(null)}>
                        Discard
                      </Button>
                    </Flex>
                  </Flex>
                </Box>
              )}

              {saveAsNew && (
                <Box className={classes.duplicateInfoBanner}>
                  <IconCopy size={17} stroke={2} />
                  <Text ff="Inter" fz={12.5} lh={1.45}>
                    Copied from note{" "}
                    <b>#{`${data?.data?._id || data?.data?.id || ""}`.slice(-6).toUpperCase()}</b>.
                    Date and time reset to today. Review and save.
                  </Text>
                </Box>
              )}

              <NoteTypeComp
                edit={edit}
                view={view}
                noteType={noteType}
                setNoteType={handleNoteTypeChange}
                // setSelectedNote={setSelectedNote}
                className={classes.noteType}
                setEssentialSupportToggle={setEssentialSupportToggle}
                setMessages={setMessages}
                setAiAssistantCardOpened={setAiAssistantCardOpened}
                handleRecordingCancel={handleRecordingCancel}
                setShowAiButtons={setAiActionsOpen}
              />
              {noteType && (
                <Box className={classes.noteFieldsBox}>
                  <Text
                    sx={{
                      fontFamily: "Inter",
                      fontSize: "16px",
                      fontWeight: 600,
                      margin: "16px 0px",
                      color: "var(--app-text)",
                    }}
                  >
                    <span>{`${view ? "View" : edit ? "Edit" : "Add"} ${noteType?.name}`}</span>
                    {isGroupNoteCreating && (
                      <Box component="span" className={classes.gcBadge}>
                        <IconUsers size={13} strokeWidth={2} />
                        Multi-client
                      </Box>
                    )}
                    {noteType?.name === "Incident Report" && incidentNoteData.severity && (
                      <Box
                        component="span"
                        className={classes.incidentSeverityBadge}
                        style={{
                          borderColor: severityColorMap[incidentNoteData.severity].border,
                          backgroundColor: severityColorMap[incidentNoteData.severity].background,
                          color: severityColorMap[incidentNoteData.severity].text,
                        }}
                      >
                        <Box
                          component="span"
                          className={classes.incidentSeverityDot}
                          style={{ backgroundColor: severityColorMap[incidentNoteData.severity].dot }}
                        />
                        {incidentNoteData.severity}
                      </Box>
                    )}
                  </Text>

                  {noteType?.name === "Incident Report" &&
                    (incidentNoteData.severity === "Serious" ||
                      incidentNoteData.severity === "Critical") && (
                      <Box
                        className={classes.incidentReportableBanner}
                        style={
                          incidentNoteData.severity === "Critical"
                            ? {
                                borderColor: severityColorMap.Critical.border,
                                backgroundColor: severityColorMap.Critical.background,
                              }
                            : {
                                borderColor: severityColorMap.Serious.border,
                                backgroundColor: severityColorMap.Serious.background,
                              }
                        }
                      >
                        <FiAlertTriangle
                          size={18}
                          color={
                            incidentNoteData.severity === "Critical"
                              ? severityColorMap.Critical.text
                              : severityColorMap.Serious.text
                          }
                        />
                        <Box>
                          <Text
                            fw={700}
                            fz={14}
                            style={{
                              color:
                                incidentNoteData.severity === "Critical"
                                  ? severityColorMap.Critical.text
                                  : severityColorMap.Serious.text,
                            }}
                          >
                            {incidentNoteData.severity === "Critical"
                              ? "Critical incident — immediate reporting required"
                              : "Reportable incident"}
                          </Text>
                          <Text
                            fz={13}
                            style={{
                              color:
                                incidentNoteData.severity === "Critical"
                                  ? severityColorMap.Critical.text
                                  : severityColorMap.Serious.text,
                            }}
                          >
                            {incidentNoteData.severity === "Critical"
                              ? "Notify the agency on-call supervisor immediately and file the state reportable-incident report within 24 hours. Agency notification and medical review are required below."
                              : "This severity level is reportable. Agency notification is required and the incident must be reviewed by a supervisor within 24 hours."}
                          </Text>
                        </Box>
                      </Box>
                    )}
                  {noteType?.name === "Incident Report" && (
                    <Grid mt={5}>
                        <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Box className={classes.detailsForm}>
                            <Text className={classes.incidentClassificationTitle}>
                              <LuBookOpen />
                              INCIDENT CLASSIFICATION
                            </Text>

                            <Select
                              className={classes.select}
                              label="Incident Type"
                              withAsterisk
                              searchable
                              placeholder="Select Incident Type"
                              value={incidentNoteData.type}
                              onChange={(val) =>
                                setIncidentNoteData((prev) => ({ ...prev, type: val }))
                              }
                              disabled={view}
                              data={[
                                "Fall",
                                "Medication Error",
                                "Behavioral / Aggression",
                                "Elopement / Wandering",
                                "Self-Injurious Behavior",
                                "Injury (Accidental)",
                                "Medical Emergency",
                                "Property Damage",
                                "Allegation of Abuse / Neglect",
                                "Other",
                              ]}
                            />

                            {incidentNoteData.type === "Other" && (
                              <TextInput
                                className={classes.input}
                                mt={16}
                                label="Specify Incident Type"
                                withAsterisk
                                placeholder="Describe the type of incident"
                                value={incidentNoteData.otherType || ""}
                                onChange={(e) => {
                                  const nextValue = e.target.value;
                                  setIncidentNoteData((prev) => ({
                                    ...prev,
                                    otherType: nextValue,
                                  }));
                                }}
                                disabled={view}
                              />
                            )}

                            <Text className={classes.incidentSeverityLabel} mt={16}>
                              Severity <span className={classes.incidentRequiredMark}>*</span>
                            </Text>
                            <Box className={classes.incidentSeverityGroup}>
                              {["Minor", "Moderate", "Serious", "Critical"].map(
                                (option) => (
                                  <Box
                                    key={option}
                                    onClick={() =>
                                      !view &&
                                      setIncidentNoteData((prev) => ({
                                        ...prev,
                                        severity: option,
                                      }))
                                    }
                                    className={`${classes.incidentSeverityOption} ${
                                      incidentNoteData.severity === option
                                        ? classes.incidentSeverityOptionActive
                                        : ""
                                    }`}
                                    style={
                                      incidentNoteData.severity === option
                                        ? {
                                            borderColor: severityColorMap[option].border,
                                            color: severityColorMap[option].text,
                                            backgroundColor: severityColorMap[option].background,
                                            boxShadow: `${severityColorMap[option].border} 0px 0px 0px 1px inset`,
                                          }
                                        : undefined
                                    }
                                  >
                                    <Box
                                      className={classes.incidentSeverityDot}
                                      style={{ backgroundColor: severityColorMap[option].dot }}
                                    />
                                    {option}
                                  </Box>
                                )
                              )}
                            </Box>
                          </Box>
                        </Grid.Col>
                        </Grid>
                      )}
                  {noteType?.name === "Incident Report" && (
                    <Grid mt={5}>
                      <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Box className={classes.detailsForm}>
                          <Text className={classes.incidentClassificationTitle}>
                            <LuMapPin />
                            WHEN &amp; WHERE
                          </Text>

                          <Grid>
                            {noteType?.custom
                              ?.filter(
                                (value) => value.fieldType?.name?.toLowerCase() === "date"
                              )
                              .map((value, index) => (
                                <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6} key={`incident-date-${index}`}>
                                  {ReturnFormItem(value, index)}
                                </Grid.Col>
                              ))}
                            {noteType?.custom
                              ?.filter((value) => value.fieldType?.name === "time")
                              .map((value, index) => (
                                <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6} key={`incident-time-${index}`}>
                                  {ReturnFormItem(value, index)}
                                </Grid.Col>
                              ))}
                            {noteType?.custom
                              ?.filter((value) => value.field === "Location")
                              .map((value, index) => (
                                <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12} key={`incident-location-${index}`}>
                                  {ReturnFormItem(value, index)}
                                </Grid.Col>
                              ))}
                          </Grid>
                        </Box>
                      </Grid.Col>
                    </Grid>
                  )}
                  {noteType?.name === "Incident Report" && (
                    <Grid mt={5}>
                      <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Box className={classes.detailsForm}>
                          <Text className={classes.incidentClassificationTitle}>
                            <LuUsers />
                            PEOPLE INVOLVED
                          </Text>

                          <Grid>
                            {noteType?.custom
                              ?.filter((value) => value.field === "Facility Name")
                              .map((value, index) => (
                                <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12} key={`incident-facility-${index}`}>
                                  {ReturnFormItem(value, index)}
                                </Grid.Col>
                              ))}
                            {noteType?.custom
                              ?.filter((value) => value.field === "Client Name")
                              .map((value, index) => (
                                <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12} key={`incident-client-${index}`}>
                                  {ReturnFormItem(value, index)}
                                </Grid.Col>
                              ))}
                            {noteType?.custom
                              ?.filter((value) => value.field === "Staff Name")
                              .map((value, index) => (
                                <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12} key={`incident-staff-${index}`}>
                                  {ReturnFormItem(value, index)}
                                </Grid.Col>
                              ))}
                          </Grid>
                        </Box>
                      </Grid.Col>
                    </Grid>
                  )}
                  {noteType?.name === "Incident Report" && (
                    <Grid mt={5}>
                      <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Box className={classes.detailsForm}>
                          <Text className={classes.incidentClassificationTitle}>
                            <LuFileText />
                            WHAT HAPPENED
                          </Text>

                          <Grid>
                            {noteType?.custom
                              ?.filter((value) => value.field === "Description")
                              .map((value, index) => (
                                <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12} key={`incident-description-${index}`}>
                                  {ReturnFormItem(value, index, "text area")}
                                </Grid.Col>
                              ))}
                            {noteType?.custom
                              ?.filter((value) => value.field === "Immediate Actions Taken")
                              .map((value, index) => (
                                <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12} key={`incident-actions-${index}`}>
                                  {ReturnFormItem(value, index, "text area")}
                                </Grid.Col>
                              ))}
                          </Grid>
                        </Box>
                      </Grid.Col>
                    </Grid>
                  )}
                  {noteType?.name === "Incident Report" && (
                    <Grid mt={5}>
                      <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Box className={classes.detailsForm}>
                          <Text className={classes.incidentClassificationTitle}>
                            <TiPlusOutline />
                            INJURIES &amp; MEDICAL ATTENTION
                          </Text>

                          <Text className={classes.incidentSeverityLabel}>
                            Was an injury sustained? <span className={classes.incidentRequiredMark}>*</span>
                          </Text>
                          <Box className={classes.incidentYesNoGroup}>
                            {["No", "Yes"].map((option) => (
                              <Box
                                key={option}
                                onClick={() =>
                                  !view &&
                                  setIncidentNoteData((prev) => ({
                                    ...prev,
                                    injurySustained:
                                      option === "Yes"
                                        ? prev.injurySustained && typeof prev.injurySustained === "object"
                                          ? prev.injurySustained
                                          : { description: "", bodyLocation: "", medicalAttention: "" }
                                        : false,
                                  }))
                                }
                                className={`${classes.incidentYesNoOption} ${
                                  (option === "Yes") === !!incidentNoteData.injurySustained
                                    ? classes.incidentYesNoOptionActive
                                    : ""
                                }`}
                              >
                                {option}
                              </Box>
                            ))}
                          </Box>

                          {incidentNoteData.injurySustained && (
                            <>
                              <TextInput
                                mt={16}
                                className={classes.input}
                                label="Description of Injury"
                                placeholder="e.g. superficial abrasion, minor swelling, laceration"
                                value={incidentNoteData.injurySustained.description || ""}
                                onChange={(e) => {
                                  const nextValue = e.target.value;
                                  setIncidentNoteData((prev) => ({
                                    ...prev,
                                    injurySustained: {
                                      ...(prev.injurySustained && typeof prev.injurySustained === "object"
                                        ? prev.injurySustained
                                        : {}),
                                      description: nextValue,
                                    },
                                  }));
                                }}
                                disabled={view}
                              />
                              <Grid mt={4}>
                                <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                  <TextInput
                                    className={classes.input}
                                    label="Body Location"
                                    placeholder="e.g. left forearm"
                                    value={incidentNoteData.injurySustained.bodyLocation || ""}
                                    onChange={(e) => {
                                      const nextValue = e.target.value;
                                      setIncidentNoteData((prev) => ({
                                        ...prev,
                                        injurySustained: {
                                          ...(prev.injurySustained && typeof prev.injurySustained === "object"
                                            ? prev.injurySustained
                                            : {}),
                                          bodyLocation: nextValue,
                                        },
                                      }));
                                    }}
                                    disabled={view}
                                  />
                                </Grid.Col>
                                <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                  <Select
                                    className={classes.select}
                                    label={
                                      <>
                                        Medical Attention <span className={classes.incidentRequiredMark}>*</span>
                                      </>
                                    }
                                    placeholder="Select"
                                    searchable
                                    value={incidentNoteData.injurySustained.medicalAttention}
                                    onChange={(val) =>
                                      setIncidentNoteData((prev) => ({
                                        ...prev,
                                        injurySustained: {
                                          ...prev.injurySustained,
                                          medicalAttention: val,
                                        },
                                      }))
                                    }
                                    data={[
                                      "None required",
                                      "First aid on site",
                                      "Assessed by nurse",
                                      "Seen by physician",
                                      "911 / EMS called",
                                      "Transported to ER",
                                      "Hospitalized",
                                    ]}
                                    disabled={view}
                                  />
                                </Grid.Col>
                              </Grid>
                            </>
                          )}

                          <Box component="hr" className={classes.incidentDivider} />

                          <Text className={classes.incidentSeverityLabel}>
                            Were emergency services (911 / EMS) called? <span className={classes.incidentRequiredMark}>*</span>
                          </Text>
                          <Box className={classes.incidentYesNoGroup}>
                            {["No", "Yes"].map((option) => (
                              <Box
                                key={option}
                                onClick={() =>
                                  !view &&
                                  setIncidentNoteData((prev) => ({
                                    ...prev,
                                    emergencyServicesCalled:
                                      option === "Yes"
                                        ? prev.emergencyServicesCalled && typeof prev.emergencyServicesCalled === "object"
                                          ? prev.emergencyServicesCalled
                                          : { clientTransported: false, timeEmsArrived: "", receivingHospital: "" }
                                        : false,
                                  }))
                                }
                                className={`${classes.incidentYesNoOption} ${
                                  (option === "Yes") === !!incidentNoteData.emergencyServicesCalled
                                    ? classes.incidentYesNoOptionActive
                                    : ""
                                }`}
                              >
                                {option}
                              </Box>
                            ))}
                          </Box>

                          {incidentNoteData.emergencyServicesCalled && (
                            <Box className={classes.incidentSubSection}>
                              <Grid>
                                <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                  <Text className={classes.incidentSeverityLabel}>
                                    Was the client transported to the hospital? <span className={classes.incidentRequiredMark}>*</span>
                                  </Text>
                                  <Box className={classes.incidentYesNoGroup}>
                                    {["No", "Yes"].map((option) => (
                                      <Box
                                        key={option}
                                        onClick={() =>
                                          !view &&
                                          setIncidentNoteData((prev) => ({
                                            ...prev,
                                            emergencyServicesCalled: {
                                              ...prev.emergencyServicesCalled,
                                              clientTransported: option === "Yes",
                                            },
                                          }))
                                        }
                                        className={`${classes.incidentYesNoOption} ${
                                          (option === "Yes") === !!incidentNoteData.emergencyServicesCalled.clientTransported
                                            ? classes.incidentYesNoOptionActive
                                            : ""
                                        }`}
                                      >
                                        {option}
                                      </Box>
                                    ))}
                                  </Box>
                                </Grid.Col>
                                <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                  <TimeInput
                                    className={classes.datePicker}
                                    label="Time EMS Arrived"
                                    format="12"
                                    icon={
                                      <img
                                        style={{ width: "20px" }}
                                        src={"/assets/images/clock.png"}
                                        alt="Icon"
                                      />
                                    }
                                    value={incidentNoteData.emergencyServicesCalled.timeEmsArrived || ""}
                                    onChange={(e) => {
                                      const nextValue = e.target.value;
                                      setIncidentNoteData((prev) => ({
                                        ...prev,
                                        emergencyServicesCalled: {
                                          ...(prev.emergencyServicesCalled && typeof prev.emergencyServicesCalled === "object"
                                            ? prev.emergencyServicesCalled
                                            : {}),
                                          timeEmsArrived: nextValue,
                                        },
                                      }));
                                    }}
                                    disabled={view}
                                  />
                                </Grid.Col>
                                {incidentNoteData.emergencyServicesCalled.clientTransported && (
                                  <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                                    <TextInput
                                      className={classes.input}
                                      label="Receiving Hospital / Facility"
                                      withAsterisk
                                      placeholder="e.g. Mercy General Hospital — Emergency Dept."
                                      value={incidentNoteData.emergencyServicesCalled.receivingHospital || ""}
                                      onChange={(e) => {
                                        const nextValue = e.target.value;
                                        setIncidentNoteData((prev) => ({
                                          ...prev,
                                          emergencyServicesCalled: {
                                            ...(prev.emergencyServicesCalled && typeof prev.emergencyServicesCalled === "object"
                                              ? prev.emergencyServicesCalled
                                              : {}),
                                            receivingHospital: nextValue,
                                          },
                                        }));
                                      }}
                                      disabled={view}
                                    />
                                  </Grid.Col>
                                )}
                              </Grid>
                            </Box>
                          )}
                        </Box>
                      </Grid.Col>
                    </Grid>
                  )}
                  {noteType?.name === "Incident Report" && (
                    <Grid mt={5}>
                      <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Box className={classes.detailsForm}>
                          <Text className={classes.incidentClassificationTitle}>
                            <LuPower />
                            RESTRAINT / PHYSICAL INTERVENTION
                          </Text>

                          <Text className={classes.incidentSeverityLabel}>
                            Was a restraint or physical intervention used? <span className={classes.incidentRequiredMark}>*</span>
                          </Text>
                          <Box className={classes.incidentYesNoGroup}>
                            {["No", "Yes"].map((option) => (
                              <Box
                                key={option}
                                onClick={() =>
                                  !view &&
                                  setIncidentNoteData((prev) => ({
                                    ...prev,
                                    restraintUsed:
                                      option === "Yes"
                                        ? prev.restraintUsed && typeof prev.restraintUsed === "object"
                                          ? prev.restraintUsed
                                          : { type: null, duration: "" }
                                        : false,
                                  }))
                                }
                                className={`${classes.incidentYesNoOption} ${
                                  (option === "Yes") === !!incidentNoteData.restraintUsed
                                    ? classes.incidentYesNoOptionActive
                                    : ""
                                }`}
                              >
                                {option}
                              </Box>
                            ))}
                          </Box>

                          {incidentNoteData.restraintUsed && (
                            <Grid mt={4}>
                              <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                <Select
                                  className={classes.select}
                                  label="Type of Intervention"
                                  placeholder="Select"
                                  searchable
                                  value={incidentNoteData.restraintUsed.type}
                                  onChange={(val) =>
                                    setIncidentNoteData((prev) => ({
                                      ...prev,
                                      restraintUsed: { ...prev.restraintUsed, type: val },
                                    }))
                                  }
                                  data={[
                                    "Verbal de-escalation only",
                                    "Personal hold (2-person)",
                                    "Physical escort",
                                    "Seclusion",
                                    "PRN medication",
                                  ]}
                                  disabled={view}
                                />
                              </Grid.Col>
                              <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                <NumberInput
                                  className={classes.input}
                                  hideControls
                                  label="Duration (minutes)"
                                  placeholder="0"
                                  value={incidentNoteData.restraintUsed.duration}
                                  onChange={(nextValue) => {
                                    setIncidentNoteData((prev) => ({
                                      ...prev,
                                      restraintUsed: {
                                        ...(prev.restraintUsed && typeof prev.restraintUsed === "object"
                                          ? prev.restraintUsed
                                          : {}),
                                        duration: nextValue,
                                      },
                                    }));
                                  }}
                                  disabled={view}
                                />
                              </Grid.Col>
                            </Grid>
                          )}
                        </Box>
                      </Grid.Col>
                    </Grid>
                  )}
                  {noteType?.name === "Incident Report" && (() => {
                    const getInitials = (fullName) =>
                      (fullName || "")
                        .split(" ")
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((p) => p[0]?.toUpperCase())
                        .join("") || "?";

                    const selectedClients = (incidentNoteData.clientsInvolved || [])
                      .map((id) => allPatients?.find((p) => p?.id === id))
                      .filter(Boolean);

                    const buildGroups = (patient) => {
                      const clientName = patient?.name || "";
                      // The client form only stores ONE guardian/emergency
                      // contact (guardianFirstName + guardianLastName ->
                      // emergencyContactName) — there is no separate
                      // "guardianFullName" field.
                      const guardianName = `${patient?.form2?.["guardianFirstName"] || ""} ${
                        patient?.form2?.["guardianLastName"] || ""
                      }`.trim();
                      const guardians = [];
                      if (guardianName) {
                        guardians.push({
                          key: `${patient?.id}-guardian`,
                          name: guardianName,
                          sub: [clientName, "Guardian"].filter(Boolean).join(" · "),
                          phone: patient?.form2?.["emergencyContactPhoneNumber"] || "",
                        });
                      }

                      const medicalProviders = [];
                      if (patient?.form2?.["pcpName"]) {
                        medicalProviders.push({
                          key: `${patient?.id}-pcp`,
                          name: patient.form2["pcpName"],
                          sub: [clientName, "Primary Physician"].filter(Boolean).join(" · "),
                          phone: patient?.form2?.["pcpPhoneNumber"] || "",
                        });
                      }
                      (patient?.form2?.["additionalProvider"] || []).forEach((prov, i) => {
                        if (prov?.providerName) {
                          medicalProviders.push({
                            key: `${patient?.id}-additional-${i}`,
                            name: prov.providerName,
                            sub: [clientName, prov?.providerType || "Provider"].filter(Boolean).join(" · "),
                            phone: prov?.providerPhone || "",
                          });
                        }
                      });

                      return { guardians, medicalProviders };
                    };

                    const groupDefs = [
                      {
                        key: "guardians",
                        label: "GUARDIANS & LEGAL REPRESENTATIVES",
                        icon: <LuShieldCheck />,
                        iconBg: "rgb(238, 240, 253)",
                        iconColor: "rgb(83, 92, 202)",
                      },
                      {
                        key: "medicalProviders",
                        label: "MEDICAL PROVIDERS",
                        icon: <LuStethoscope />,
                        iconBg: "rgb(236, 253, 243)",
                        iconColor: "rgb(2, 122, 72)",
                      },
                      {
                        key: "agencyInternal",
                        label: "AGENCY & INTERNAL",
                        icon: <LuTarget />,
                        iconBg: "rgb(240, 240, 245)",
                        iconColor: "rgb(71, 84, 103)",
                      },
                    ];

                    const allEntries = {
                      guardians: [],
                      medicalProviders: [],
                      agencyInternal: [],
                    };
                    selectedClients.forEach((patient) => {
                      const groups = buildGroups(patient);
                      allEntries.guardians.push(...groups.guardians);
                      allEntries.medicalProviders.push(...groups.medicalProviders);
                    });

                    // Agency & internal contacts are org-level, not tied to a
                    // specific client. The Supervisor / Program Manager is the
                    // main admin on the account, so their name is shown here.
                    const supervisorName = profile?.admin?.name || "";
                    allEntries.agencyInternal = [
                      {
                        key: "agency-supervisor",
                        name: "Supervisor / Program Manager",
                        sub: supervisorName || "Main Admin",
                        phone: "On-call",
                        initials: supervisorName ? getInitials(supervisorName) : "TB",
                      },
                      {
                        key: "agency-state-reporting",
                        name: "Agency / State Reporting Line",
                        sub: "Reportable-incident hotline",
                        initials: "SR",
                        // Serious and Critical incidents are reportable, so the
                        // state reporting line has to be notified.
                        required: isReportableSeverity,
                      },
                    ];

                    const toggleContact = (key) =>
                      !view &&
                      setIncidentNoteData((prev) => ({
                        ...prev,
                        notifiedContacts: {
                          ...prev.notifiedContacts,
                          [key]: !prev.notifiedContacts?.[key],
                        },
                      }));

                    return (
                      <Grid mt={5}>
                        <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Box className={classes.detailsForm}>
                            <Text className={classes.incidentClassificationTitle}>
                              <LuBell />
                              NOTIFICATIONS MADE
                            </Text>
                            <Box className={classes.incidentNotifyHint}>
                              <LuInfo size={14} />
                              Contacts on file for the selected client(s). Check everyone who was notified of this incident.
                            </Box>

                            {selectedClients.length === 0 ? (
                              <Text className={classes.incidentNotifySub}>
                                Select client(s) involved above to see their contacts here.
                              </Text>
                            ) : (
                              groupDefs.map((group) => {
                                const entries = allEntries[group.key];
                                const notifiedCount = entries.filter(
                                  (e) => incidentNoteData.notifiedContacts?.[e.key]
                                ).length;
                                return (
                                  <Box key={group.key}>
                                    <Box className={classes.incidentNotifyGroupHeader}>
                                      <Box
                                        className={classes.incidentNotifyGroupIcon}
                                        style={{ background: group.iconBg, color: group.iconColor }}
                                      >
                                        {group.icon}
                                      </Box>
                                      <Text className={classes.incidentNotifyGroupLabel}>
                                        {group.label}
                                      </Text>
                                      <Box className={classes.incidentNotifyGroupDivider} />
                                      <Text className={classes.incidentNotifyGroupCount}>
                                        {notifiedCount} of {entries.length} notified
                                      </Text>
                                    </Box>
                                    {entries.length === 0 ? (
                                      <Text className={classes.incidentNotifySub} py={8}>
                                        None on file.
                                      </Text>
                                    ) : (
                                      entries.map((entry) => (
                                        <Box
                                          key={entry.key}
                                          className={classes.incidentNotifyRow}
                                          onClick={() => toggleContact(entry.key)}
                                        >
                                          <Checkbox
                                            className={classes.incidentNotifyCheckbox}
                                            checked={!!incidentNoteData.notifiedContacts?.[entry.key]}
                                            onChange={() => toggleContact(entry.key)}
                                            disabled={view}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <Box className={classes.incidentNotifyAvatar}>
                                            {entry.initials || getInitials(entry.name)}
                                          </Box>
                                          <Box>
                                            <Text className={classes.incidentNotifyName}>
                                              {entry.name}
                                              {entry.required && (
                                                <span className={classes.incidentRequiredPill}>
                                                  Required
                                                </span>
                                              )}
                                            </Text>
                                            <Text className={classes.incidentNotifySub}>{entry.sub}</Text>
                                          </Box>
                                          {entry.phone && (
                                            <Text className={classes.incidentNotifyPhone}>{entry.phone}</Text>
                                          )}
                                        </Box>
                                      ))
                                    )}
                                  </Box>
                                );
                              })
                            )}

                            <Text className={classes.incidentSeverityLabel} mt={16}>
                              Notification Notes <span>(optional)</span>
                            </Text>
                            <Textarea
                              className={classes.incidentPlainTextArea}
                              minRows={4}
                              autosize
                              placeholder="Add any details — who was contacted, when, by what method, and their response. e.g. Guardian notified by phone at 2:40 PM; requested a call back from the nurse."
                              value={incidentNoteData.notificationNotes}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                setIncidentNoteData((prev) => ({
                                  ...prev,
                                  notificationNotes: nextValue,
                                }));
                              }}
                              disabled={view}
                            />
                          </Box>
                        </Grid.Col>
                      </Grid>
                    );
                  })()}
                  {noteType?.name === "Incident Report" && (
                    <Grid mt={5}>
                      <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                        <Box className={classes.detailsForm}>
                          <Text className={classes.incidentClassificationTitle}>
                            <LuFileText />
                            FOLLOW-UP &amp; PREVENTIVE ACTION
                          </Text>
                          <Textarea
                            className={classes.incidentPlainTextArea}
                            minRows={4}
                            autosize
                            placeholder="Describe planned follow-up: care-plan review, environmental changes, additional supervision, staff training, or physician follow-up."
                            value={incidentNoteData.followUpAction}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setIncidentNoteData((prev) => ({
                                ...prev,
                                followUpAction: nextValue,
                              }));
                            }}
                            disabled={view}
                          />
                        </Box>
                      </Grid.Col>
                    </Grid>
                  )}
                  {noteType?.name === "Incident Report" && (() => {
                    // Only worth a section once a Supervisor has actually signed
                    // off. While the report is being written or is still waiting
                    // on review there is nothing to show here — the post-save
                    // dialog already explains that it goes to a Supervisor.
                    if (!incidentNoteData.reporterSignedAt) return null;

                    return (
                      <Grid mt={5}>
                        <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                          <Box className={classes.detailsForm}>
                            <Text className={classes.incidentClassificationTitle}>
                              <LuPenLine />
                              SIGNATURES &amp; REVIEW
                            </Text>
                            <Text className={classes.incidentSeverityLabel}>
                              Supervisor Signature
                            </Text>
                            <Box
                              style={{
                                padding: "12px 16px",
                                backgroundColor: "#FFFFFF",
                                borderRadius: 8,
                                border: "1px solid var(--app-border)",
                              }}
                            >
                              {incidentNoteData.reporterSignatureImage ? (
                                <img
                                  src={decrypt(
                                    incidentNoteData.reporterSignatureImage
                                  )}
                                  alt="Signature"
                                  style={{ maxHeight: 60, objectFit: "contain" }}
                                />
                              ) : (
                                <Text
                                  ff="Island Moments"
                                  fz={28}
                                  fw={400}
                                  color="#171717"
                                  style={{ lineHeight: 1.2 }}
                                >
                                  {incidentNoteData.reporterSignature ||
                                    incidentNoteData.reporterName ||
                                    "—"}
                                </Text>
                              )}
                              <Text className={classes.incidentNotifySub}>
                                Signed by{" "}
                                {incidentNoteData.reporterName || "supervisor"} ·{" "}
                                {moment(
                                  incidentNoteData.reporterSignedAt
                                ).format("MMM D, YYYY · h:mm A")}
                              </Text>
                            </Box>
                          </Box>
                        </Grid.Col>
                      </Grid>
                    );
                  })()}
                  {isStudioTemplate && (
                    <StudioSectionsForm
                      sections={noteType?.sections}
                      form={form}
                      classes={classes}
                      view={view}
                      edit={edit}
                      hideCarePlan={isMultiClientSelected}
                      onClientChange={(ids, name) => onClientsChange(ids, name)}
                      onFacilityChange={(id, name) => handleFacilityChange(id, name)}
                    />
                  )}
                  {!isStudioTemplate && noteType?.name !== "Incident Report" && (
                  <>
                  <Box className={classes.detailsForm}>
                    <Box className={classes.ch2}>
                      <IconUsers size={15} />
                      Client &amp; Staff
                    </Box>
                    <Grid mt={5}>
                      {isGroupNoteCreating ? (
                        <>
                          {/* Staff Name – col 6 */}
                          {noteType?.custom?.filter((v) => v.field === "Staff Name").map((v, i) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6} key={`gn-staff-${i}`}>
                              {ReturnFormItem(v, i)}
                            </Grid.Col>
                          ))}
                          {/* Facility Name – col 6 */}
                          {noteType?.custom?.filter((v) => v.field === "Facility Name").map((v, i) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6} key={`gn-facility-${i}`}>
                              {ReturnFormItem(v, i)}
                            </Grid.Col>
                          ))}
                          {/* Client Name – full width (renders MultiSelect because isGroupNoteCreating) */}
                          {noteType?.custom?.filter((v) => v.field === "Client Name").map((v, i) => (
                            <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12} key={`gn-client-${i}`}>
                              {ReturnFormItem(v, i)}
                            </Grid.Col>
                          ))}
                          {/* Date – col 6 */}
                          {noteType?.custom?.filter((v) => v.fieldType?.name?.toLowerCase() === "date").map((v, i) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6} key={`gn-date-${i}`}>
                              {ReturnFormItem(v, i)}
                            </Grid.Col>
                          ))}
                          {/* Time – col 6 */}
                          {noteType?.custom?.filter((v) => v.fieldType?.name === "time").map((v, i) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6} key={`gn-time-${i}`}>
                              {ReturnFormItem(v, i)}
                            </Grid.Col>
                          ))}
                          {/* Shift – col 6 */}
                          {noteType?.custom?.filter((v) => v.field === "Shift").map((v, i) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6} key={`gn-shift-${i}`}>
                              {ReturnFormItem(v, i)}
                            </Grid.Col>
                          ))}
                          {/* Any remaining half-width fields (e.g. Duration) */}
                          {noteType?.custom
                            ?.filter((v) =>
                              !["Staff Name", "Facility Name", "Client Name", "Shift"].includes(v.field) &&
                              !["date", "time", "text area"].includes(v.fieldType?.name?.toLowerCase())
                            )
                            .map((v, i) => (
                              <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6} key={`gn-extra-${i}`}>
                                {ReturnFormItem(v, i)}
                              </Grid.Col>
                            ))}
                        </>
                      ) : (
                        <>
                          {/* -----> Level of need <----- */}
                      {noteType?.custom?.filter(
                        (value) => value?.field === "Level of Need"
                      ) &&
                        noteType?.custom
                          ?.filter((value) => value.field === "Level of Need")
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}
                      {/* -----> Staff Name <----- */}
                      {noteType?.custom?.filter(
                        (value) => value?.field === "Staff Name"
                      ) &&
                        noteType?.custom
                          ?.filter((value) => value.field === "Staff Name")
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {/* -----> Client Name <----- */}

                      {noteType?.custom?.filter(
                        (value) => value?.field === "Client Name"
                      ) &&
                        noteType?.custom
                          ?.filter((value) => value.field === "Client Name")
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {/* -----> Billing <----- */}

                      {isBilling_Services && (
                        <>
                          {/* -----> Provider <----- */}

                          <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            {noteType?.custom
                              ?.filter((value) => value.field === "Provider")
                              .map((value, index) =>
                                ReturnFormItem(value, index)
                              )}
                          </Grid.Col>

                          {/* -----> Service (Unit Type: Days) <----- */}

                          <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                            <Select
                              size="md"
                              searchable
                              label={"Service (Unit Type: Days)"}
                              radius={"md"}
                              rightSection={icon}
                              // defaultValue={userId}
                              id={"client-name-note"}
                              className={`${classes.select} ${classes.selectBG}`}
                              {...form.getInputProps("Service Type")}
                              placeholder={`Select Service (Unit Type: Days)`}
                              rightSectionPointerEvents="none"
                              required={view || userId ? false : true}
                              onChange={(v) => {
                                handlePatientChange(v, "Service Type");
                              }}
                              data={
                                selectedPatient?.form1?.services?.length > 0
                                  ? selectedPatient?.form1?.services?.map(
                                      (val) => ({
                                        value: val?.servicenumber,
                                        label: val?.servicetitle,
                                      })
                                    )
                                  : []
                              }
                              disabled={view}
                            />
                          </Grid.Col>

                          {/* -----> Unit/Day/Hours <----- */}

                          <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            {noteType?.custom
                              ?.filter(
                                (value) => value.field === "Unit/Day/Hours"
                              )
                              .map((value, index) =>
                                ReturnFormItem(value, index)
                              )}
                          </Grid.Col>

                          {/* -----> Service Date <----- */}

                          {/* <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            {noteType?.custom
                              ?.filter(
                                (value) => value.field === "Service Date"
                              )
                              .map((value, index) =>
                                ReturnFormItem(value, index)
                              )}
                          </Grid.Col> */}
                        </>
                      )}

                      {currentNote !== Billing_Services_Note &&
                        noteType?.custom
                          ?.filter((value) => value.field === "Facility Name")
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {/* -----> Date <----- */}
                      {noteType?.custom?.filter(
                        (value) => value.field === "Date"
                      ) &&
                        noteType?.custom
                          ?.filter(
                            (value) =>
                              value.fieldType?.name?.toLowerCase() === "date"
                          )
                          // ?.filter((value) => value.field === "Date")
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}
                      {/* -----> Time <----- */}
                      {noteType?.custom?.filter(
                        // (value) => value.field === "Time"
                        (value) => value.fieldType?.name === "time"
                      ) &&
                        noteType?.custom
                          // ?.filter((value) => value.field === "Time")
                          ?.filter((value) => value.fieldType?.name === "time")
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}
                      {/* -----> Shift <----- */}
                      {noteType?.general === true &&
                        noteType?.custom?.filter(
                          (value) => value.field === "Shift"
                        ) &&
                        noteType?.custom
                          ?.filter((value) => value.field === "Shift")
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {isEssentialSupport && !isMultiClientSelected && (
                        <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                          <Box className={classes.radioGroupParent}>
                            <Text>Use Essential Support</Text>

                            <Tooltip
                              label="Select Facility Name First"
                              placement="bottom"
                              withArrow
                              trigger="hover"
                              disabled={selectedFacility}
                            >
                              <Radio.Group
                                size="xs"
                                value={essentialSupportToggle}
                                name="useSupportPlan"
                                onChange={(value) => {
                                  handleToggleChange(value, "essentialSupport");
                                }}
                              >
                                <Group className={classes.segToggleRadioGroup}>
                                  <Radio
                                    value="yes"
                                    label="Yes"
                                    id="check-yes"
                                    color="gray"
                                    disabled={view}
                                    variant="outline"
                                    className={classes.segToggleRadioRoot}
                                  />
                                  <Radio
                                    value="no"
                                    label="No"
                                    color="gray"
                                    disabled={view}
                                    variant="outline"
                                    className={classes.segToggleRadioRoot}
                                  />
                                </Group>
                              </Radio.Group>
                            </Tooltip>
                          </Box>
                        </Grid.Col>
                      )}

                      {isIntervention_Progress_Note && !isMultiClientSelected && (
                        <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                          <Box className={classes.radioGroupParent}>
                            <Text>Use Intervention plan</Text>

                            <Tooltip
                              label="Select Facility Name First"
                              placement="bottom"
                              withArrow
                              trigger="hover"
                              disabled={selectedFacility}
                            >
                              <Radio.Group
                                size="xs"
                                value={interventionPlanToggle}
                                name="useInterventionPlan"
                                onChange={(value) => {
                                  handleToggleChange(value, "interventionPlan");
                                }}
                              >
                                <Group className={classes.segToggleRadioGroup}>
                                  <Radio
                                    value="yes"
                                    label="Yes"
                                    id="check-yes"
                                    color="gray"
                                    disabled={view}
                                    variant="outline"
                                    className={classes.segToggleRadioRoot}
                                  />
                                  <Radio
                                    value="no"
                                    label="No"
                                    color="gray"
                                    disabled={view}
                                    variant="outline"
                                    className={classes.segToggleRadioRoot}
                                  />
                                </Group>
                              </Radio.Group>
                            </Tooltip>
                          </Box>
                        </Grid.Col>
                      )}

                      {showRadioButtons && !isMultiClientSelected && (
                        <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                          <Box className={classes.radioGroupParent}>
                            <Text>Use Support Plan</Text>
                            <Radio.Group
                              size="xs"
                              value={supportPlan}
                              name="useSupportPlan"
                              onChange={handleSupportPlanChange}
                              variant="outline"
                            >
                              <Group className={classes.segToggleRadioGroup}>
                                <Radio
                                  value="yes"
                                  label="Yes"
                                  id="check-yes"
                                  color="gray"
                                  disabled={view}
                                  variant="outline"
                                  className={classes.segToggleRadioRoot}
                                />
                                <Radio
                                  value="no"
                                  label="No"
                                  color="gray"
                                  disabled={view}
                                  variant="outline"
                                  className={classes.segToggleRadioRoot}
                                />
                              </Group>
                            </Radio.Group>
                          </Box>
                        </Grid.Col>
                      )}

                      {/* Custom Field Time */}
                      {/* {noteType?.custom?.filter(
                        (value) =>
                          value?.field_type === "Custom Field" &&
                          value.fieldType?.name === "time"
                      ) &&
                        noteType?.custom
                          ?.filter(
                            (value) =>
                              value?.field_type === "Custom Field" &&
                              value.fieldType?.name === "time"
                          )
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))} */}

                      {/* Custom Field Dropdown */}
                      {noteType?.general === false &&
                        noteType?.custom?.filter(
                          (value) => value.fieldType?.name === "Dropdown"
                        ) &&
                        noteType?.custom
                          ?.filter(
                            (value) => value.fieldType?.name === "Dropdown"
                          )
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {/* Custom Field Number */}
                      {(noteType?.general === false || isGroupNote) &&
                        noteType?.custom?.filter(
                          (value) =>
                            value?.field_type === "Custom Field" &&
                            value.fieldType?.name === "number"
                        ) &&
                        noteType?.custom
                          ?.filter(
                            (value) =>
                              value?.field_type === "Custom Field" &&
                              value.fieldType?.name === "number"
                          )
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {/* Custom Field Other */}
                      {noteType?.general === false &&
                        noteType?.custom?.filter(
                          (value) =>
                            value?.field_type === "Custom Field" &&
                            value.fieldType?.name?.toLowerCase() === "other"
                        ) &&
                        noteType?.custom
                          ?.filter(
                            (value) =>
                              value?.field_type === "Custom Field" &&
                              value.fieldType?.name?.toLowerCase() === "other"
                          )
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {/* Custom Field Multiple Choice */}
                      {noteType?.custom?.filter(
                        (value) => value.fieldType?.name === "Multiple Choice"
                      ) &&
                        noteType?.custom
                          ?.filter(
                            (value) =>
                              value.fieldType?.name === "Multiple Choice"
                          )
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {/* Custom Field Single Choice */}
                      {noteType?.custom?.filter(
                        (value) => value.fieldType?.name === "Single Choice"
                      ) &&
                        noteType?.custom
                          ?.filter(
                            (value) => value.fieldType?.name === "Single Choice"
                          )
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {/* Custom Field String */}
                      {noteType?.general === false &&
                        noteType?.custom?.filter(
                          (value) =>
                            value?.field_type === "Custom Field" &&
                            value.fieldType?.name === "string"
                        ) &&
                        noteType?.custom
                          ?.filter(
                            (value) =>
                              value?.field_type === "Custom Field" &&
                              value.fieldType?.name === "string"
                          )
                          .map((value, index) => (
                            <Grid.Col xs={12} sm={12} md={6} lg={6} xl={6}>
                              {ReturnFormItem(value, index)}
                            </Grid.Col>
                          ))}

                      {/* -----> Use Essential Support */}
                        </>
                      )}
                    </Grid>
                  </Box>

                  {/* -----> Description — own card, matches mockup's descCard() <-----
                      Rendered only if at least one of the three branches below would
                      actually produce a Description field — ReturnFormItem(..., "text
                      area") returns null whenever it's captured elsewhere instead (per-
                      outcome/per-support/per-goal) OR this note type/custom config has
                      no "text area" field at all. Checked dynamically (not by hardcoding
                      note-type names) so custom note types never show an empty card. */}
                  {(() => {
                    const customFields = noteType?.custom || [];
                    const hasDescription = isGroupNoteCreating
                      ? customFields.some((v, i) => ReturnFormItem(v, i, "text area"))
                      : supportPlan !== "yes" && noteType?.name === "Progress Note"
                      ? customFields.some((v, i) => ReturnFormItem(v, i, "text area"))
                      : noteType?.name !== "Progress Note"
                      ? customFields.some((v, i) => ReturnFormItem(v, i, "text area"))
                      : false;
                    if (!hasDescription) return null;
                    return (
                      <Box className={classes.accordionCard}>
                        <Box className={classes.ch2}>
                          <IconFileText size={15} />
                          Note
                        </Box>
                        <Grid>
                          {isGroupNoteCreating &&
                            customFields.map((v, i) =>
                              ReturnFormItem(v, i, "text area") && (
                                <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12} key={`gn-desc-${i}`}>
                                  {ReturnFormItem(v, i, "text area")}
                                </Grid.Col>
                              )
                            )}
                          {!isGroupNoteCreating &&
                            supportPlan !== "yes" &&
                            noteType?.name === "Progress Note" &&
                            customFields.map(
                              (value, index) =>
                                ReturnFormItem(value, index, "text area") && (
                                  <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                                    {ReturnFormItem(value, index, "text area")}
                                  </Grid.Col>
                                )
                            )}
                          {!isGroupNoteCreating &&
                            noteType?.name !== "Progress Note" &&
                            customFields.map(
                              (value, index) =>
                                ReturnFormItem(value, index, "text area") && (
                                  <Grid.Col xs={12} sm={12} md={12} lg={12} xl={12}>
                                    {ReturnFormItem(value, index, "text area")}
                                  </Grid.Col>
                                )
                            )}
                        </Grid>
                      </Box>
                    );
                  })()}
                  </>
                  )}

                  {/* Studio "Use care plan?" = Yes but the selected client has no
                      active plan of a type this template documents — tell the
                      user why nothing loaded instead of showing an empty
                      authoring block. With both parts selected, only the
                      missing one(s) are named; the part that DID load renders
                      its normal block below. */}
                  {(() => {
                    if (
                      !isCareplanTemplate ||
                      form.values[careplanLabel] !== "Yes" ||
                      isMultiClientSelected
                    )
                      return null;
                    const missing = [
                      careplanHasOutcomes && !hasActiveSupportPlan ? "support plan" : null,
                      careplanHasEssential && !hasActiveEssentialSupportPlan
                        ? "essential supports"
                        : null,
                    ].filter(Boolean);
                    if (!missing.length) return null;
                    return (
                      <Box
                        className={`${classes.supportPlanAccordion} ${classes.my}`}
                      >
                        <Box className={classes.ch2}>
                          <IconClipboardCheck size={15} />
                          Care Plan
                        </Box>
                        <Text ff="Inter" fz={13} c="var(--app-text-muted, #737373)" p="8px 4px">
                          No active {missing.join(" or ")} on file for this client
                          {selectedFacility ? "" : " (select a client and facility above)"}.
                        </Text>
                      </Box>
                    );
                  })()}

                  {/* support plan map start */}
                  {supportPlan === "yes" &&
                    (noteType?.name === "Progress Note" ||
                      noteType?.custom?.find(
                        (_c) => _c?.field === "Support Plan"
                      ) ||
                      isCareplanTemplate) && (
                      <Box
                        className={`${classes.supportPlanAccordion} ${classes.my}`}
                      >
                        <Box className={classes.ch2}>
                          <IconClipboardCheck size={15} />
                          Support Plan Outcomes
                        </Box>
                        {supportPlan === "yes" && (
                          <Box>
                            <Accordion
                              value={openedAccordion}
                              onChange={setOpenedAccordion}
                              chevron={
                                <IconChevronDown
                                            color="gray"
                                           
                                          />
                              }
                            >
                              {descriptions?.map((medication, index) => {
                                // planId is only stamped onto the row once an
                                // outcome is picked, so a fresh (or duplicated)
                                // row had no labels at all and every field
                                // rendered with a blank label. Default to the
                                // Support plan wording, same as CarePlanUpdate.
                                const labels =
                                  planTypeLabels[medication?.planId] ||
                                  planTypeLabels["1"];
                                return (
                                  <Accordion.Item
                                    className={`${classes.accordionItem} ${classes.addNoteAccordionItem}`}
                                    value={`${index}`}
                                  >
                                    <Accordion.Control>
                                      <Flex
                                        align="center"
                                        justify="space-between"
                                        w="100%"
                                        onClick={handleRecordingConfirm}
                                      >
                                        <Text
                                          color="var(--app-text)"
                                          fz={13.5}
                                          fw={650}
                                          ff="Inter"
                                        >{`Outcome ${
                                          medication?.details?.outcomeNumber
                                            ? medication?.details?.outcomeNumber
                                            : ""
                                        }`}</Text>
                                        <Flex align="center" gap="8px">
                                      
                                          {!view && (
                                            <Tooltip
                                              label="Remove"
                                              withArrow
                                              position="bottom"
                                            >
                                              <ActionIcon
                                                className={classes.accDeleteBtn}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  removeMedication(
                                                    medication?.id
                                                  );
                                                }}
                                              >
                                                <RiDeleteBin7Line size={16} />
                                              </ActionIcon>
                                            </Tooltip>
                                          )}
                                        </Flex>
                                      </Flex>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                      <div key={medication?.id}>
                                        {/* view fields */}
                                        {view == true ? (
                                          <Grid
                                            gutter={14}
                                            cols={isMobileScreen ? 1 : 2}
                                            className={classes.mwd}
                                            pb={30}
                                          >
                                            {/* <Grid.Col
                                              span={isMobileScreen ? 12 : 3}
                                            >
                                              <Box
                                                className={
                                                  classes.outcomeContainer
                                                }
                                              >
                                                <Text
                                                  className={
                                                    classes.outcomeText
                                                  }
                                                >
                                                  {`${
                                                    labels?.addNewButtonLabel ||
                                                    ""
                                                  } Number`}
                                                  <Text c={"red"}>*</Text>
                                                </Text>
                                                <Text
                                                  c="var(--app-text-faint, #8A93A2)"
                                                  ml={10}
                                                  ff={"inter"}
                                                >
                                                  {
                                                    medication?.details
                                                      ?.outcomeNumber
                                                  }
                                                </Text>
                                              </Box>
                                            </Grid.Col> */}
                                            <Grid.Col
                                              span={isMobileScreen ? 12 : 6}
                                            >
                                              <Text className={classes.cpinfoLbl}>
                                                {`${
                                                  labels?.addNewButtonLabel ||
                                                  ""
                                                } Title`}
                                              </Text>
                                              <Text className={classes.cpinfoVal}>
                                                {medication?.details
                                                  ?.outcomeTitle || "-"}
                                              </Text>
                                            </Grid.Col>
                                            <Grid.Col
                                              span={isMobileScreen ? 12 : 6}
                                            >
                                              <Text className={classes.cpinfoLbl}>
                                                {`${
                                                  labels?.addNewButtonLabel ||
                                                  ""
                                                } Category`}
                                              </Text>
                                              <Text className={classes.cpinfoVal}>
                                                {medication?.outComeCategory ||
                                                  "-"}
                                              </Text>
                                              {/* <TextInput
                                                className={classes.input}
                                                label={`${
                                                  labels?.addNewButtonLabel ||
                                                  ""
                                                } Category`}
                                                size="sm"
                                                placeholder="Enter outcome category"
                                                value={
                                                  medication?.outComeCategory
                                                }
                                                disabled
                                              /> */}
                                            </Grid.Col>
                                          </Grid>
                                        ) : (
                                          // Edit fields
                                          <Grid
                                            gutter={14}
                                            cols={isMobileScreen ? 1 : 3}
                                            className={classes.mwd}
                                          >
                                            <Grid.Col xs={12} sm={12} md={6}>
                                              <Select
                                                required
                                                size="sm"
                                                radius={"md"}
                                                className={classes.select}
                                                data={outComeTitle.filter(
                                                  (option) =>
                                                    !descriptions.some(
                                                      (desc) =>
                                                        desc?.id !==
                                                          medication?.id &&
                                                        desc?.outcomeTitle ===
                                                          option?.value
                                                    )
                                                )}
                                                label={
                                                  labels?.outcomeTitleLabel ||
                                                  ""
                                                }
                                                placeholder={`Select ${
                                                  labels?.outcomeTitleLabel ||
                                                  ""
                                                }`}
                                                value={medication?.outcomeTitle}
                                                onChange={(value) => {
                                                  handleMedicationChange(
                                                    medication?.id,
                                                    "outcomeTitle",
                                                    value,
                                                    index
                                                  );
                                                }}
                                                disabled={view}
                                                rightSectionPointerEvents="none"
                                                rightSection={icon}
                                              />
                                              {errors[index] && (
                                                <Text color="red" fz={14}>
                                                  {errors[index]}
                                                </Text>
                                              )}
                                            </Grid.Col>
                                            {/* -----> Outcome Number <----- */}
                                            <Grid.Col xs={12} sm={12} md={2}>
                                              <Select
                                                id="outcome-number"
                                                size="sm"
                                                radius={"md"}
                                                required
                                                data={outcomeNumbers?.filter(
                                                  (option) =>
                                                    !descriptions?.some(
                                                      (desc) =>
                                                        desc?.id !==
                                                          medication?.id &&
                                                        desc?.outcomeNumber ===
                                                          option.value
                                                    )
                                                )}
                                                label={
                                                  labels?.outcomeNumberLabel ||
                                                  ""
                                                }
                                                className={classes.select}
                                                placeholder={`Select ${
                                                  labels?.outcomeNumberLabel ||
                                                  ""
                                                }`}
                                                value={
                                                  medication?.outcomeNumber
                                                }
                                                onChange={(value) => {
                                                  handleMedicationChange(
                                                    medication?.id,
                                                    "outcomeNumber",
                                                    value,
                                                    index
                                                  );
                                                }}
                                                disabled={view}
                                                // variant="filled"
                                                rightSectionPointerEvents="none"
                                                rightSection={icon}
                                              />
                                              {errors[index] && (
                                                <Text color="red" fz={14}>
                                                  {errors[index]}
                                                </Text>
                                              )}
                                            </Grid.Col>
                                            <Grid.Col xs={12} sm={12} md={4}>
                                              <TextInput
                                                className={classes.input}
                                                radius={"md"}
                                                label={
                                                  labels?.outcomeCategoryLabel ||
                                                  ""
                                                }
                                                size="sm"
                                                placeholder={
                                                  labels?.outcomeCategoryLabel ||
                                                  ""
                                                }
                                                value={
                                                  medication?.outComeCategory
                                                }
                                                disabled
                                              />
                                            </Grid.Col>
                                            {/* <Grid.Col
                                              xs={12}
                                              sm={12}
                                              md={12}
                                              lg={12}
                                              xl={12}
                                            >
                                              <OutcomeNumber
                                                disabled={true}
                                                num={
                                                  medication?.details
                                                    ?.outcomeNumber
                                                }
                                              />
                                            </Grid.Col> */}
                                          </Grid>
                                        )}
                                        <ViewInfo
                                          data={medication}
                                          labels={labels}
                                        />

                                        {/* -----> Was the goal met? <---- */}
                                        <Select
                                          mt={16}
                                          id="about-goal"
                                          size="sm"
                                          radius={"md"}
                                          className={classes.select}
                                          data={[
                                            "Yes",
                                            "No",
                                            "Goal in progress",
                                            "Not Applicable",
                                          ]}
                                          label="Was this goal met?"
                                          placeholder="Select a response"
                                          value={medication?.metGoal}
                                          onChange={(value) => {
                                            handleMedicationChange(
                                              medication?.id,
                                              "metGoal",
                                              value,
                                              index
                                            );
                                          }}
                                          disabled={view}
                                          rightSectionPointerEvents="none"
                                          rightSection={icon}
                                        />
                                        <Box mt={16}>
                                          <Text fz={13} fw={500} c="#344054" mb={7} ff="Inter">Description</Text>
                                          <Box className={classes.descTextareaWrapper}>
                                            <Textarea
                                              id="add-note"
                                              size="sm"
                                              maxRows={5}
                                              minRows={10}
                                              placeholder="Enter Note"
                                              styles={{
                                                wrapper: { border: "none", boxShadow: "none", padding: 0, borderRadius: 0 },
                                                input: { border: "none", borderRadius: 0, background: "transparent", padding: "14px 16px", color: "var(--app-text)", fontSize: 14, fontWeight: 400, fontFamily: "Inter", lineHeight: 1.55 },
                                              }}
                                              value={
                                                medication?.description?.length >
                                                0
                                                  ? medication?.description
                                                  : ""
                                              }
                                              onChange={(e) => {
                                                handleMedicationChange(
                                                  medication?.id,
                                                  "description",
                                                  e?.target?.value,
                                                  index
                                                );

                                                let tempNotesArr = outcomeNotes;
                                                tempNotesArr.splice(
                                                  index,
                                                  1,
                                                  e?.target?.value
                                                );
                                                setOutcomeNotes([
                                                  ...tempNotesArr,
                                                ]);
                                              }}
                                              readOnly={view}
                                              autosize={view}
                                            />
                                            {!view && isAiAccess && (
                                              <>
                                                <AiActionButtons
                                                  onMicClick={() => handleMicStart("description")}
                                                  onActionClick={(key) => handleDualCompare(key, `description_${index}`)}
                                                  onSparklesClick={() => {
                                                    const errors = validatePromptFields();
                                                    if (Object.keys(errors).length > 0) {
                                                      setFormErrors(errors);
                                                      return;
                                                    }
                                                    setFormErrors({});
                                                    setAiActionsOpen((prev) => !prev);
                                                  }}
                                                  onQuickNotesClick={() => setQuickNotesTarget({ key: `description_${index}`, getValue: () => medication?.description, setValue: (v) => handleMedicationChange(medication?.id, "description", v, index) })}
                                                  quickNotesDisabled={quickNotesDisabled}
                                                  micDisabled={view || (existingRecord?.active === false && existingRecord?.paid === false)}
                                                  actionsDisabled={view || !medication?.description?.trim()}
                                                  aiActionsOpen={aiActionsOpen}
                                                />
                                                {comparePanel.open && comparePanel.context === `description_${index}` && (
                                                  <ComparePanel
                                                    action={comparePanel.action}
                                                    option1={comparePanel.option1}
                                                    option2={comparePanel.option2}
                                                    loading1={comparePanel.loading1}
                                                    loading2={comparePanel.loading2}
                                                    option1Model={comparePanel.option1Model}
                                                    option2Model={comparePanel.option2Model}
                                                    ollamaAvailable={comparePanel.ollamaAvailable}
                                                    onArrange={handleArrangeCompare}
                                                    onUse={(text, meta) => handleUseCompareOption(text, `description_${index}`, meta)}
                                                    onCancel={handleCancelCompare}
                                                    onRegenerate={() => handleDualCompare(comparePanel.action, `description_${index}`)}
                                                  />
                                                )}
                                                <QuickNotesPicker
                                                  opened={quickNotesTarget?.key === `description_${index}`}
                                                  close={() => setQuickNotesTarget(null)}
                                                  onInsert={handleQuickNotesInsert}
                                                  hideCarePlan={!hasCarePlan}
                                                />
                                              </>
                                            )}
                                            {(formErrors.name || formErrors.facility) && (
                                              <Box px={12} py={6} className={classes.formErrorBox}>
                                                {formErrors.name && <Text fz={12} c="red" ff="Inter">{formErrors.name}</Text>}
                                                {formErrors.facility && <Text fz={12} c="red" ff="Inter">{formErrors.facility}</Text>}
                                              </Box>
                                            )}
                                          </Box>
                                        </Box>
                                        <RecordingControls
                                          isListening={isListening}
                                          recordingTime={recordingTime}
                                          onRedo={handleRedo}
                                          onCancel={handleRecordingCancel}
                                          onConfirm={handleRecordingConfirm}
                                          classes={classes}
                                        />
                                        {!view && (
                                          <p
                                            style={{
                                              color: "red",
                                              margin: 0,
                                              padding: 0,
                                            }}
                                          >
                                            {medication?.error}
                                          </p>
                                        )}
                                      </div>
                                    </Accordion.Panel>
                                  </Accordion.Item>
                                );
                              })}
                              {!view && (
                                <ButtonComp
                                  type="button"
                                  text={"+ Add New Outcome"}
                                  onClick={addDescription}
                                  btnClass={classes.addNewOutcomeBtn}
                                  variant="default"
                                  disabled={
                                    outcomeNumbers?.length ===
                                    descriptions?.length
                                  }
                                />
                              )}
                            </Accordion>
                          </Box>
                        )}
                      </Box>
                    )}

                  {/* essential plan map start */}
                  {essentialSupportToggle === "yes" && (
                    <Box className={`${classes.supportPlanAccordion} ${classes.my}`}

                    >
                      
                      
                      <Box className={classes.ch2}>
                        <IconClipboardCheck size={15} />
                        Essential Support
                      </Box>
                      <Accordion
                        value={essentialAccordion}
                        onChange={(value) => {
                          if (view === true || edit === true) {
                            let data = essentialSupport?.[value];
                            handleEssentialTypeChangeForNoteView(
                              data?.essentialSupportType,
                              data?.howOftenEssentialSupport
                            );
                          }
                          setEssentialAccordion(value);
                        }}
                        mt={16}
                        // variant="separated"
                         chevron={
                                <IconChevronDown
                                            color="gray"
                                           
                                          />
                              }
                        // className={classes.essentialAccordion}
                      >
                        {Array.isArray(essentialSupport) &&
                          essentialSupport?.map((support, index) => (
                            <Accordion.Item
                              key={support?.id}
                              value={String(index)}
                                className={`${classes.accordionItem} ${classes.addNoteAccordionItem}`}
                            >
                              <Accordion.Control>
                                <Box
                                  className={classes.accordionControl}
                                  onClick={handleRecordingConfirm}
                                >
                                  <Text
                                    color="var(--app-text)"
                                    fz={13.5}
                                    fw={650}
                                    ff="Inter"
                                  >
                                    Essential Support {""}
                                    {support?.essentialNumber
                                      ? support.essentialNumber
                                      : ""}
                                    {/* {index + 1} */}
                                  </Text>
                                  {!view && (
                                    <Tooltip
                                      label="Remove"
                                      withArrow
                                      position="bottom"
                                    >
                                      <ActionIcon
                                        className={classes.accDeleteBtn}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeEssentialSupport(support?.id);
                                        }}
                                      >
                                        <AiOutlineDelete size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Accordion.Control>

                              <Accordion.Panel>
                                <Grid
                                  gutter={14}
                                  pb={26}
                                  className={classes.mwd}
                                >
                                  <Grid.Col
                                    xs={12}
                                    sm={12}
                                    md={6}
                                    lg={6}
                                    xl={6}
                                  >
                                    <Select
                                      id="essential-support-type"
                                      size="sm"
                                      required
                                      data={essentialSupportTypeState}
                                      // data={
                                      //   selectedSupportType
                                      //     ? essentialSupportTypeState.filter(
                                      //         (item) =>
                                      //           item.value ===
                                      //             support?.essentialSupportType ||
                                      //           item.value !==
                                      //             selectedSupportType
                                      //       )
                                      //     : essentialSupportTypeState
                                      // }
                                      label={"Essential Support Type"}
                                      className={classes.select}
                                      placeholder={"Select Type"}
                                      readOnly={view}
                                      value={
                                        support?.essentialSupportType || ""
                                      }
                                      // disabled={true}
                                      onChange={(value) => {
                                        handleEssentialTypeChange(
                                          support?.id,
                                          "essentialSupportType",
                                          value,
                                          index
                                        );
                                      }}
                                      // error={
                                      //   essentialSupportErrors[index] &&
                                      //   !support?.essentialSupportType &&
                                      //   "Support Type is required"
                                      // }
                                      variant="filled"
                                      // mx={{ base: 0, sm: 17 }}
                                      my={{ base: 26, sm: 0 }}
                                      withAsterisk={false}
                                      rightSectionPointerEvents="none"
                                      rightSection={icon}
                                    />
                                  </Grid.Col>
                                  <Grid.Col
                                    xs={12}
                                    sm={12}
                                    md={6}
                                    lg={6}
                                    xl={6}
                                  >
                                    <Select
                                      id="essential-support-frequency"
                                      size="sm"
                                      required
                                      data={frequencyOptions || []}
                                      label={"Frequency"}
                                      className={classes.select}
                                      placeholder={"Select Frequency"}
                                      value={
                                        support?.howOftenEssentialSupport || ""
                                      } // ✅ set value
                                      onChange={(value) => {
                                        // setSelectedFrequency(value); // ✅ store user's selection
                                        handleEssentialFrequencyChange(
                                          support?.id,
                                          "howOftenEssentialSupport",
                                          value,
                                          index
                                        );
                                      }}
                                      readOnly={view}
                                      variant="filled"
                                      my={{ base: 26, sm: 0 }}
                                      withAsterisk={false}
                                      rightSectionPointerEvents="none"
                                      rightSection={icon}
                                    />
                                  </Grid.Col>
                                  <Grid.Col
                                    xs={12}
                                    sm={12}
                                    md={6}
                                    lg={6}
                                    xl={6}
                                  >
                                    <Select
                                      id="essential-support-outcome-number"
                                      size="sm"
                                      required
                                      data={essentialNumber || []}
                                      label={"Outcome #"}
                                      className={classes.select}
                                      placeholder={"#"}
                                      readOnly={view}
                                      value={support?.essentialNumber || ""} // ✅ set value
                                      onChange={(value) => {
                                        handleEssentialNumberChange(
                                          support?.id,
                                          "essentialNumber",
                                          value,
                                          index
                                        );
                                      }}
                                      variant="filled"
                                      // mx={{ base: 0, sm: 10 }}
                                      // my={{ base: 26, sm: 0 }}
                                      withAsterisk={false}
                                      rightSectionPointerEvents="none"
                                      rightSection={icon}
                                    />
                                    {/* <TextInput
                                      id="essential-support-type"
                                      size="sm"
                                      disabled
                                      label={"Outcome #"}
                                      value={
                                        essentialNumber
                                      }
                                      className={classes.input}
                                      placeholder={"#"}
                                      variant="filled"
                                      withAsterisk={false}
                                    /> */}
                                  </Grid.Col>
                                  <Grid.Col
                                    xs={12}
                                    sm={12}
                                    md={6}
                                    lg={6}
                                    xl={6}
                                  >
                                    <Select
                                      id="essential-support-type"
                                      size="sm"
                                      required
                                      // data={essentialSupportNumber}
                                      // data={activities?.map((activity) => ({
                                      //   label: activity.supportStatement, // Use the support statement as the label
                                      //   value: activity.id, // Use the activity number as the value
                                      // }))}
                                      data={activities
                                        ?.filter(
                                          (activity) =>
                                            // Only show activities not selected in other supports (except this one)
                                            !essentialSupport.some(
                                              (s, i) =>
                                                i !== index &&
                                                s?.essentialActivityNumber ===
                                                  activity.id
                                            )
                                        )
                                        .map((activity) => ({
                                          label: activity?.supportStatement,
                                          value: activity?.id,
                                        }))}
                                      label={"Essential Support Activities"}
                                      className={classes.select}
                                      placeholder={"Select Activities"}
                                      value={
                                        support?.essentialActivityNumber || ""
                                      }
                                      onChange={(value) => {
                                        handleEssentialChange(
                                          support?.id,
                                          "essentialActivityNumber",
                                          value,
                                          index
                                        );
                                      }}
                                      disabled={view}
                                      error={
                                        essentialSupportErrors[index] &&
                                        !support?.essentialActivityNumber &&
                                        "Support Activity is required"
                                      }
                                      withAsterisk={false}
                                      variant="filled"
                                      rightSectionPointerEvents="none"
                                      rightSection={icon}
                                      nothingFound={"Activities already added"}
                                    />
                                  </Grid.Col>
                                </Grid>
                                <EssentialViewInfo
                                  data={support}
                                  className={classes.essentialViewInfoAccordion}
                                />
                                <Text fz={13} fw={500} c="#344054" mb={7} ff="Inter">Description</Text>
                                <Box className={classes.descTextareaWrapper}>
                                  <Textarea
                                    id="add-note"
                                    size="sm"
                                    maxRows={5}
                                    minRows={10}
                                    placeholder="Enter Note"
                                    styles={{
                                      wrapper: { border: "none", boxShadow: "none", padding: 0, borderRadius: 0 },
                                      input: { border: "none", borderRadius: 0, background: "transparent", padding: "14px 16px", color: "var(--app-text)", fontSize: 14, fontWeight: 400, fontFamily: "Inter", lineHeight: 1.55 },
                                    }}
                                    value={
                                      support?.description?.length > 0
                                        ? support?.description
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleEssentialSupportDesc(
                                        support?.id,
                                        "description",
                                        e?.target?.value,
                                        index
                                      );
                                      let tempNotesArr = essentialNotes;
                                      tempNotesArr.splice(
                                        index,
                                        1,
                                        e?.target?.value
                                      );
                                      setEssentialNotes([...tempNotesArr]);
                                    }}
                                    readOnly={view}
                                    autosize={view}
                                  />
                                  {!view && isAiAccess && (
                                    <>
                                      <AiActionButtons
                                        onMicClick={() => handleMicStart("essential")}
                                        onActionClick={(key) => handleDualCompare(key, `essential_${index}`)}
                                        onSparklesClick={() => {
                                          const errors = validatePromptFields();
                                          if (Object.keys(errors).length > 0) {
                                            setFormErrors(errors);
                                            return;
                                          }
                                          setFormErrors({});
                                          setAiActionsOpen((prev) => !prev);
                                        }}
                                        onQuickNotesClick={() => setQuickNotesTarget({ key: `essential_${index}`, getValue: () => support?.description, setValue: (v) => handleEssentialSupportDesc(support?.id, "description", v, index) })}
                                        quickNotesDisabled={quickNotesDisabled}
                                        micDisabled={view || (existingRecord?.active === false && existingRecord?.paid === false)}
                                        actionsDisabled={view || !support?.description?.trim()}
                                        aiActionsOpen={aiActionsOpen}
                                      />
                                      {comparePanel.open && comparePanel.context === `essential_${index}` && (
                                        <ComparePanel
                                          action={comparePanel.action}
                                          option1={comparePanel.option1}
                                          option2={comparePanel.option2}
                                          loading1={comparePanel.loading1}
                                          loading2={comparePanel.loading2}
                                          option1Model={comparePanel.option1Model}
                                          option2Model={comparePanel.option2Model}
                                          ollamaAvailable={comparePanel.ollamaAvailable}
                                          onArrange={handleArrangeCompare}
                                          onUse={(text, meta) => handleUseCompareOption(text, `essential_${index}`, meta)}
                                          onCancel={handleCancelCompare}
                                          onRegenerate={() => handleDualCompare(comparePanel.action, `essential_${index}`)}
                                        />
                                      )}
                                      <QuickNotesPicker
                                        opened={quickNotesTarget?.key === `essential_${index}`}
                                        close={() => setQuickNotesTarget(null)}
                                        onInsert={handleQuickNotesInsert}
                                        hideCarePlan={!hasCarePlan}
                                      />
                                    </>
                                  )}
                                  {(formErrors.name || formErrors.facility) && (
                                    <Box px={12} py={6} className={classes.formErrorBox}>
                                      {formErrors.name && <Text fz={12} c="red" ff="Inter">{formErrors.name}</Text>}
                                      {formErrors.facility && <Text fz={12} c="red" ff="Inter">{formErrors.facility}</Text>}
                                    </Box>
                                  )}
                                </Box>
                                <RecordingControls
                                  isListening={isListening}
                                  recordingTime={recordingTime}
                                  onRedo={handleRedo}
                                  onCancel={handleRecordingCancel}
                                  onConfirm={handleRecordingConfirm}
                                  classes={classes}
                                />
                              </Accordion.Panel>
                            </Accordion.Item>
                          ))}
                      </Accordion>
                      {!view && (
                        <ButtonComp
                          type="button"
                          text={"+ Add Essential Support"}
                          onClick={addEssentialSupport}
                          btnClass={classes.addNewOutcomeBtn}
                          variant="default"
                        />
                      )}
                    </Box>
                  )}

                  {/* intervention plan map start */}
                  {interventionPlanToggle === "yes" && (
                    <Box className={classes.accordionCard}>
                      <Box className={classes.ch2}>
                        <IconClipboardCheck size={15} />
                        Intervention Plan Outcomes
                      </Box>
                      <Accordion
                        value={goalsAccordion}
                        onChange={(value) => {
                          // if (view === true || edit === true) {
                          //   let data = essentialSupport?.[value];
                          //   handleEssentialTypeChangeForNoteView(
                          //     data?.essentialSupportType,
                          //     data?.howOftenEssentialSupport
                          //   );
                          // }
                          setGoalsAccordion(value);
                        }}
                        mt={16}
                          chevron={
                                <IconChevronDown
                                            color="gray"
                                           
                                          />
                              }
                
                      >
                        {Array.isArray(goals) &&
                          goals?.map((goalItem, index) => (
                            <Accordion.Item
                              key={goalItem?.id}
                              value={String(index)}
                              className={classes.essentialAccordionItem}
                            >
                              <Accordion.Control>
                                <Box
                                  className={classes.accordionControl}
                                  onClick={handleRecordingConfirm}
                                >
                                  <Text
                                    color="var(--app-text)"
                                    fz={13.5}
                                    fw={650}
                                    ff="Inter"
                                  >
                                    Goal {""}
                                    {goalItem?.goalNumber
                                      ? goalItem.goalNumber
                                      : index + 1}
                                    {/* {index + 1} */}
                                  </Text>
                                  {!view && (
                                    <Tooltip
                                      label="Remove"
                                      withArrow
                                      position="bottom"
                                    >
                                      <ActionIcon
                                        className={classes.accDeleteBtn}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeGoal(goalItem?.id);
                                        }}
                                      >
                                        <AiOutlineDelete size={16} />
                                      </ActionIcon>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Accordion.Control>

                              <Accordion.Panel>
                                <Grid
                                  gutter={14}
                                  pb={26}
                                  className={classes.mwd}
                                >
                                  <Grid.Col
                                    xs={12}
                                    sm={12}
                                    md={5}
                                    lg={5}
                                    xl={5}
                                  >
                                    <TimeInput
                                      label="Start Time"
                                      className={classes.datePicker}
                                      icon={
                                        <img
                                          className={classes.clockIcon}
                                          src={"/assets/images/clock.png"}
                                          alt="Icon"
                                        />
                                      }
                                      format="12"
                                      value={goalItem?.startTimeGoal || ""}
                                      onChange={(e) => {
                                        const isSafari =
                                          /^((?!chrome|android).)*safari/i.test(
                                            navigator.userAgent
                                          );
                                        let time = e.target.value
                                          .split(":")
                                          ?.slice(0, 2)
                                          ?.join(":");
                                        isSafari
                                          ? handleEssentialSupportDesc(
                                              goalItem.id,
                                              "startTimeGoal",
                                              time,
                                              index
                                            )
                                          : handleEssentialSupportDesc(
                                              goalItem.id,
                                              "startTimeGoal",
                                              time,
                                              index
                                            );
                                      }}
                                      disabled={view}
                                    />
                                  </Grid.Col>
                                  <Grid.Col span={2}>
                                    <Flex
                                      align="center"
                                      justify="center"
                                      h="100%"
                                      pt={28}
                                    >
                                      <Text fz={25} color="var(--app-text)" fw={400}>
                                        -
                                      </Text>
                                    </Flex>
                                  </Grid.Col>
                                  <Grid.Col
                                    xs={12}
                                    sm={12}
                                    md={5}
                                    lg={5}
                                    xl={5}
                                  >
                                    <TimeInput
                                      label="End Time"
                                      format="12"
                                      className={classes.datePicker}
                                      icon={
                                        <img
                                          className={classes.clockIcon}
                                          src={"/assets/images/clock.png"}
                                          alt="Icon"
                                        />
                                      }
                                      value={goalItem?.endTimeGoal || ""}
                                      onChange={(e) => {
                                        const isSafari =
                                          /^((?!chrome|android).)*safari/i.test(
                                            navigator.userAgent
                                          );
                                        let time = e.target.value
                                          .split(":")
                                          ?.slice(0, 2)
                                          ?.join(":");
                                        isSafari
                                          ? handleEssentialSupportDesc(
                                              goalItem.id,
                                              "endTimeGoal",
                                              time,
                                              index
                                            )
                                          : handleEssentialSupportDesc(
                                              goalItem.id,
                                              "endTimeGoal",
                                              time,
                                              index
                                            );
                                      }}
                                      disabled={view}
                                    />
                                  </Grid.Col>
                                  <Grid.Col
                                    xs={12}
                                    sm={12}
                                    md={10}
                                    lg={10}
                                    xl={10}
                                  >
                                    <Select
                                      id="essential-support-type"
                                      size="sm"
                                      required
                                      data={goalOptions.filter(
                                        (g) =>
                                          !goals.some(
                                            (gi) =>
                                              gi.identifiedGoal === g.value &&
                                              gi.id !== goalItem.id
                                          )
                                      )}
                                      label={"Identified Goals"}
                                      className={classes.select}
                                      placeholder={"Select Identified Goals"}
                                      readOnly={view}
                                      value={goalItem?.identifiedGoal || ""}
                                      // disabled={true}
                                      onChange={(value) => {
                                        handleEssentialTypeChange(
                                          goalItem?.id,
                                          "identifiedGoal",
                                          value,
                                          index
                                        );
                                      }}
                                      // error={
                                      //   essentialSupportErrors[index] &&
                                      //   !goalItem?.identifiedGoal &&
                                      //   "Support Type is required"
                                      // }
                                      variant="filled"
                                      // mx={{ base: 0, sm: 17 }}
                                      my={{ base: 26, sm: 0 }}
                                      withAsterisk={false}
                                      rightSectionPointerEvents="none"
                                      rightSection={icon}
                                    />
                                  </Grid.Col>
                                  <Grid.Col
                                    xs={12}
                                    sm={12}
                                    md={2}
                                    lg={2}
                                    xl={2}
                                  >
                                    <Select
                                      id="essential-goalItem-outcome-number"
                                      size="sm"
                                      required
                                      data={goalNumber || []}
                                      label={"Goal #"}
                                      className={classes.select}
                                      placeholder={"#"}
                                      readOnly={view}
                                      value={goalItem?.goalNumber || ""} // ✅ set value
                                      onChange={(value) => {
                                        handleEssentialNumberChange(
                                          goalItem?.id,
                                          "goalNumber",
                                          value,
                                          index
                                        );
                                      }}
                                      variant="filled"
                                      withAsterisk={false}
                                      rightSectionPointerEvents="none"
                                      rightSection={icon}
                                    />
                                  </Grid.Col>
                                </Grid>

                                {goalItems[index]?.map((_gi, gIndex) => (
                                  <Grid key={_gi.id} mb={10}>
                                    <Grid.Col
                                      xs={12}
                                      sm={12}
                                      md={!view ? 11 : 12}
                                      lg={!view ? 11 : 12}
                                      xl={!view ? 11 : 12}
                                    >
                                      <Box
                                        className={classes.interventionGoalItem}
                                      >
                                        {view ? (
                                          <Box className={classes.goalItemsBox}>
                                            <Text
                                              size="sm"
                                              fw={500}
                                              ff={"Inter"}
                                            >
                                              Objective
                                            </Text>
                                            <Text size="sm" ff={"Inter"}>
                                              {_gi?.objectives || ""}
                                            </Text>
                                          </Box>
                                        ) : (
                                          <Select
                                            id={`essential-goalItem-type-${_gi.id}`}
                                            size="sm"
                                            required
                                            data={objectivesByGoal?.filter(
                                              (o) =>
                                                !goalItems[index].some(
                                                  (g) =>
                                                    g?.objectives ===
                                                      o?.value &&
                                                    g.id !== _gi.id
                                                )
                                            )}
                                            // data={objectivesByGoal}
                                            label="Objective"
                                            className={classes.select}
                                            placeholder="Select Objective"
                                            readOnly={view}
                                            value={_gi?.objectives || ""}
                                            onChange={(value) =>
                                              handleGoalActivityChange(
                                                _gi.id,
                                                "objectives",
                                                value,
                                                gIndex,
                                                index
                                              )
                                            }
                                            mb={10}
                                            withAsterisk={false}
                                            rightSectionPointerEvents="none"
                                            rightSection={icon}
                                          />
                                        )}

                                        {view ? (
                                          <Box className={classes.goalItemsBox}>
                                            <Text
                                              size="sm"
                                              fw={500}
                                              ff={"Inter"}
                                            >
                                              Intervention Instruction
                                            </Text>
                                            <Text size="sm" ff={"Inter"}>
                                              {_gi?.Intervention_Instruction ||
                                                ""}
                                            </Text>
                                          </Box>
                                        ) : (
                                          <Select
                                            id={`essential-goalItem-frequency-${goalItem.id}`}
                                            size="sm"
                                            required
                                            data={interventionsByGoal.filter(
                                              (i) =>
                                                !goalItems[index].some(
                                                  (g) =>
                                                    g.Intervention_Instruction ===
                                                      i.value && g.id !== _gi.id
                                                )
                                            )}
                                            // data={interventionsByGoal}
                                            label="Intervention"
                                            className={classes.select}
                                            placeholder="Select Intervention"
                                            value={
                                              _gi?.Intervention_Instruction ||
                                              ""
                                            }
                                            onChange={(value) =>
                                              handleGoalActivityChange(
                                                _gi.id,
                                                "Intervention_Instruction",
                                                value,
                                                gIndex,
                                                index
                                              )
                                            }
                                            readOnly={view}
                                            withAsterisk={false}
                                            rightSectionPointerEvents="none"
                                            rightSection={icon}
                                          />
                                        )}
                                      </Box>
                                    </Grid.Col>

                                    {!view && (
                                      <Grid.Col xs={12} sm={12} md={1} gap={10}>
                                        <Flex
                                          direction="column"
                                          gap={10}
                                          justify="center"
                                          align="center"
                                          h="100%"
                                        >
                                          <Button
                                            onClick={() =>
                                              handleAddGoalItem(index)
                                            }
                                            className={`${classes.interventionAddGoalItem} ${
                                              view || isAddDisabled
                                                ? classes.goalItemBtnDisabled
                                                : ""
                                            }`}
                                            disabled={view || isAddDisabled}
                                          >
                                            <TbPlus
                                              size={15}
                                              color="var(--app-text)"
                                            />
                                          </Button>
                                          <Button
                                            onClick={() =>
                                              handleRemoveGoalItem(
                                                _gi.id,
                                                index
                                              )
                                            }
                                            className={`${classes.interventionAddGoalItem} ${
                                              goalItems[index]?.length === 1
                                                ? classes.goalItemBtnDisabled
                                                : ""
                                            }`}
                                            disabled={
                                              goalItems[index]?.length === 1
                                            } // keep at least 1 row
                                          >
                                            <TbTrash
                                              size={15}
                                              color="var(--app-text)"
                                            />
                                          </Button>
                                        </Flex>
                                      </Grid.Col>
                                    )}
                                  </Grid>
                                ))}

                                {/* Behavior Description */}
                                <Text fz={13} fw={500} c="#344054" mb={7} ff="Inter">Behavior</Text>
                                <Box className={classes.descTextareaWrapper}>
                                  <Textarea
                                    id="add-note"
                                    size="sm"
                                    maxRows={5}
                                    minRows={10}
                                    placeholder="Enter behavior"
                                    styles={{
                                      wrapper: { border: "none", boxShadow: "none", padding: 0, borderRadius: 0 },
                                      input: { border: "none", borderRadius: 0, background: "transparent", padding: "14px 16px", color: "var(--app-text)", fontSize: 14, fontWeight: 400, fontFamily: "Inter", lineHeight: 1.55 },
                                    }}
                                    value={
                                      goalItem?.behaviorDescription?.length > 0
                                        ? goalItem?.behaviorDescription
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleEssentialSupportDesc(
                                        goalItem?.id,
                                        "behaviorDescription",
                                        e?.target?.value,
                                        index
                                      );
                                      let tempNotesArr = essentialNotes;
                                      tempNotesArr.splice(
                                        index,
                                        1,
                                        e?.target?.value
                                      );
                                      setEssentialNotes([...tempNotesArr]);
                                    }}
                                    readOnly={view}
                                    autosize={view}
                                  />
                                  {!view && isAiAccess && (
                                    <>
                                      <AiActionButtons
                                        onMicClick={() => handleMicStart("behavior")}
                                        onActionClick={(key) => handleDualCompare(key, "behavior")}
                                        onSparklesClick={() => handleDualCompare("behavior", "behavior")}
                                        onQuickNotesClick={() => setQuickNotesTarget({ key: "behavior", getValue: () => goalItem?.behaviorDescription, setValue: (v) => handleEssentialSupportDesc(goalItem?.id, "behaviorDescription", v, index) })}
                                        quickNotesDisabled={quickNotesDisabled}
                                        micDisabled={view || micLabel === "plan" || micLabel === "response" || (existingRecord?.active === false && existingRecord?.paid === false)}
                                        actionsDisabled={view || !goalItem?.behaviorDescription?.trim()}
                                        aiActionsOpen={false}
                                        sparklesActive={comparePanel.open && comparePanel.context === "behavior"}
                                      />
                                      {comparePanel.open && comparePanel.context === "behavior" && (
                                        <ComparePanel
                                          action={comparePanel.action}
                                          option1={comparePanel.option1}
                                          option2={comparePanel.option2}
                                          loading1={comparePanel.loading1}
                                          loading2={comparePanel.loading2}
                                          option1Model={comparePanel.option1Model}
                                          option2Model={comparePanel.option2Model}
                                          ollamaAvailable={comparePanel.ollamaAvailable}
                                          onArrange={handleArrangeCompare}
                                          onUse={(text, meta) => handleUseCompareOption(text, "behavior", meta)}
                                          onCancel={handleCancelCompare}
                                          onRegenerate={() => handleDualCompare(comparePanel.action, "behavior")}
                                        />
                                      )}
                                      <QuickNotesPicker
                                        opened={quickNotesTarget?.key === "behavior"}
                                        close={() => setQuickNotesTarget(null)}
                                        onInsert={handleQuickNotesInsert}
                                        hideCarePlan={!hasCarePlan}
                                      />
                                    </>
                                  )}
                                </Box>
                                <RecordingControls
                                  isListening={
                                    micLabel === "behavior" ? true : false
                                  }
                                  recordingTime={recordingTime}
                                  onRedo={() => handleRedo("behavior")}
                                  onCancel={() =>
                                    handleRecordingCancel("behavior")
                                  }
                                  onConfirm={() =>
                                    handleRecordingConfirm("behavior")
                                  }
                                  classes={classes}
                                />
                                {/* Response Description */}
                                <Text fz={13} fw={500} c="#344054" mb={7} mt={10} ff="Inter">Response</Text>
                                <Box className={classes.descTextareaWrapper}>
                                  <Textarea
                                    id="add-note"
                                    size="sm"
                                    maxRows={5}
                                    minRows={10}
                                    placeholder="Enter Response"
                                    styles={{
                                      wrapper: { border: "none", boxShadow: "none", padding: 0, borderRadius: 0 },
                                      input: { border: "none", borderRadius: 0, background: "transparent", padding: "14px 16px", color: "var(--app-text)", fontSize: 14, fontWeight: 400, fontFamily: "Inter", lineHeight: 1.55 },
                                    }}
                                    value={
                                      goalItem?.responseDescription?.length > 0
                                        ? goalItem?.responseDescription
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleEssentialSupportDesc(
                                        goalItem?.id,
                                        "responseDescription",
                                        e?.target?.value,
                                        index
                                      );
                                    }}
                                    readOnly={view}
                                    autosize={view}
                                  />
                                  {!view && isAiAccess && (
                                    <>
                                      <AiActionButtons
                                        onMicClick={() => handleMicStart("response")}
                                        onActionClick={(key) => handleDualCompare(key, "response")}
                                        onSparklesClick={() => handleDualCompare("response", "response")}
                                        onQuickNotesClick={() => setQuickNotesTarget({ key: "response", getValue: () => goalItem?.responseDescription, setValue: (v) => handleEssentialSupportDesc(goalItem?.id, "responseDescription", v, index) })}
                                        quickNotesDisabled={quickNotesDisabled}
                                        micDisabled={view || micLabel === "behavior" || micLabel === "plan" || (existingRecord?.active === false && existingRecord?.paid === false)}
                                        actionsDisabled={view || !goalItem?.responseDescription?.trim()}
                                        aiActionsOpen={false}
                                        sparklesActive={comparePanel.open && comparePanel.context === "response"}
                                      />
                                      {comparePanel.open && comparePanel.context === "response" && (
                                        <ComparePanel
                                          action={comparePanel.action}
                                          option1={comparePanel.option1}
                                          option2={comparePanel.option2}
                                          loading1={comparePanel.loading1}
                                          loading2={comparePanel.loading2}
                                          option1Model={comparePanel.option1Model}
                                          option2Model={comparePanel.option2Model}
                                          ollamaAvailable={comparePanel.ollamaAvailable}
                                          onArrange={handleArrangeCompare}
                                          onUse={(text, meta) => handleUseCompareOption(text, "response", meta)}
                                          onCancel={handleCancelCompare}
                                          onRegenerate={() => handleDualCompare(comparePanel.action, "response")}
                                        />
                                      )}
                                      <QuickNotesPicker
                                        opened={quickNotesTarget?.key === "response"}
                                        close={() => setQuickNotesTarget(null)}
                                        onInsert={handleQuickNotesInsert}
                                        hideCarePlan={!hasCarePlan}
                                      />
                                    </>
                                  )}
                                </Box>
                                <RecordingControls
                                  isListening={
                                    micLabel === "response" ? true : false
                                  }
                                  recordingTime={recordingTime}
                                  onRedo={() => handleRedo("response")}
                                  onCancel={() =>
                                    handleRecordingCancel("response")
                                  }
                                  onConfirm={() =>
                                    handleRecordingConfirm("response")
                                  }
                                  classes={classes}
                                />

                                {/* Plan description */}
                                <Text fz={13} fw={500} c="#344054" mb={7} mt={10} ff="Inter">Plan</Text>
                                <Box className={classes.descTextareaWrapper}>
                                  <Textarea
                                    id="add-note"
                                    size="sm"
                                    mt={0}
                                    maxRows={5}
                                    minRows={10}
                                    placeholder="Enter Plan"
                                    styles={{
                                      wrapper: { border: "none", boxShadow: "none", padding: 0, borderRadius: 0 },
                                      input: { border: "none", borderRadius: 0, background: "transparent", padding: "14px 16px", color: "var(--app-text)", fontSize: 14, fontWeight: 400, fontFamily: "Inter", lineHeight: 1.55 },
                                    }}
                                    value={
                                      goalItem?.planDescription?.length > 0
                                        ? goalItem?.planDescription
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleEssentialSupportDesc(
                                        goalItem?.id,
                                        "planDescription",
                                        e?.target?.value,
                                        index
                                      );
                                    }}
                                    readOnly={view}
                                    autosize={view}
                                  />
                                  {!view && isAiAccess && (
                                    <>
                                      <AiActionButtons
                                        onMicClick={() => handleMicStart("plan")}
                                        onActionClick={(key) => handleDualCompare(key, "plan")}
                                        onSparklesClick={() => handleDualCompare("plan", "plan")}
                                        onQuickNotesClick={() => setQuickNotesTarget({ key: "plan", getValue: () => goalItem?.planDescription, setValue: (v) => handleEssentialSupportDesc(goalItem?.id, "planDescription", v, index) })}
                                        quickNotesDisabled={quickNotesDisabled}
                                        micDisabled={view || micLabel === "behavior" || micLabel === "response" || (existingRecord?.active === false && existingRecord?.paid === false)}
                                        actionsDisabled={view || !goalItem?.planDescription?.trim()}
                                        aiActionsOpen={false}
                                        sparklesActive={comparePanel.open && comparePanel.context === "plan"}
                                      />
                                      {comparePanel.open && comparePanel.context === "plan" && (
                                        <ComparePanel
                                          action={comparePanel.action}
                                          option1={comparePanel.option1}
                                          option2={comparePanel.option2}
                                          loading1={comparePanel.loading1}
                                          loading2={comparePanel.loading2}
                                          option1Model={comparePanel.option1Model}
                                          option2Model={comparePanel.option2Model}
                                          ollamaAvailable={comparePanel.ollamaAvailable}
                                          onArrange={handleArrangeCompare}
                                          onUse={(text, meta) => handleUseCompareOption(text, "plan", meta)}
                                          onCancel={handleCancelCompare}
                                          onRegenerate={() => handleDualCompare(comparePanel.action, "plan")}
                                        />
                                      )}
                                      <QuickNotesPicker
                                        opened={quickNotesTarget?.key === "plan"}
                                        close={() => setQuickNotesTarget(null)}
                                        onInsert={handleQuickNotesInsert}
                                        hideCarePlan={!hasCarePlan}
                                      />
                                    </>
                                  )}
                                </Box>
                                <RecordingControls
                                  isListening={
                                    micLabel === "plan" ? true : false
                                  }
                                  recordingTime={recordingTime}
                                  onRedo={() => handleRedo("plan")}
                                  onCancel={() => handleRecordingCancel("plan")}
                                  onConfirm={() =>
                                    handleRecordingConfirm("plan")
                                  }
                                  classes={classes}
                                />
                                <Select
                                  mt={16}
                                  id="about-goal"
                                  size="sm"
                                  radius={"md"}
                                  className={classes.select}
                                  data={[
                                    "Yes",
                                    "No",
                                    "Goal in progress",
                                    "Not Applicable",
                                  ]}
                                  label="Was this goal met?"
                                  placeholder="Select a response"
                                  value={goalItem?.metGoalIntervention}
                                  onChange={(value) => {
                                    handleEssentialSupportDesc(
                                      goalItem?.id,
                                      "metGoalIntervention",
                                      value,
                                      index
                                    );
                                  }}
                                  disabled={view}
                                  rightSectionPointerEvents="none"
                                  rightSection={icon}
                                />
                              </Accordion.Panel>
                            </Accordion.Item>
                          ))}
                      </Accordion>
                      {!view && (
                        <ButtonComp
                          type="button"
                          text={"+ Add New Goal"}
                          onClick={addNewGoal}
                          btnClass={classes.addNewOutcomeBtn}
                          variant="default"
                        />
                      )}
                    </Box>
                  )}

                  {profile?.user?.type === "staff" &&
                    data?.data?.approvalStatus === "pending" && (
                      <Box className={classes.rejectedNote}>
                        <Box className={classes.rejectionIconBox}>
                          <NotePendingSvg />
                          <Text ff={"Inter"} fz={14} fw={500} color="var(--app-text)">
                            Pending Approval
                          </Text>
                        </Box>
                      </Box>
                    )}

                  {/* -----> Cancel/Save — single footer attached to the bottom of the drawer, matches mockup's .dfoot <----- */}
                  {!view && (
                    <>
                      {edit && !saveAsNew && renderSignature()}
                      <Space h="md" />
                      <GroupedButtons
                        parentClass={classes.groupedButtonsParent}
                        funcOne={() => {
                          resetForm();
                          cancelForm();
                        }}
                        type="submit"
                        cancelBtnClass={classes.cancelNoteBtn}
                        saveBtnClass={classes.saveNoteBtn}
                        textOne={"Cancel"}
                        textTwo={"Save"}
                        disabledTwo={isSaveDisabled}
                      />
                    </>
                  )}
                </Box>
              )}

            </form>
      </Drawer>
    </>
  );
};

export default NoteDrawer;
